import { strFromU8, strToU8, unzipSync } from 'fflate'

import type {
  SpreadsheetCellRecord,
  SpreadsheetCellValue,
  SpreadsheetExcelExportResponsePayload,
  SpreadsheetEngineErrorCode,
  SpreadsheetExcelIoWarning,
  SpreadsheetExcelImportPreviewResponsePayload,
  SpreadsheetExcelOoxmlArtifactManifestEntry,
  SpreadsheetExcelWorkbookIoFormat,
  SpreadsheetAutoFilter,
  SpreadsheetAutoFilterColumnCriteria,
  SpreadsheetAutoFilterOperator,
  SpreadsheetAutoFilterSortCondition,
  SpreadsheetAutoFilterSortState,
  SpreadsheetWorkbookCalculationSettings,
  SpreadsheetWorkbookCustomProperty,
  SpreadsheetWorkbookDocumentProperties,
  SpreadsheetWorkbookProperties,
  SpreadsheetWorkbookSecurity,
  SpreadsheetWorkbookTheme,
  SpreadsheetWorkbookView,
  SpreadsheetRangeAddress,
  SpreadsheetSheetCalculationProperties,
  SpreadsheetSheetCustomView,
  SpreadsheetSheetIgnoredError,
  SpreadsheetSheetProtectedRange,
  SpreadsheetSheetProtectionOptions,
  SpreadsheetTableColumnDefinition,
  SpreadsheetTableDefinition,
  SpreadsheetTableStyleDefinition,
  SpreadsheetChartArtifactType,
  SpreadsheetDerivedArtifact,
  SpreadsheetValidationRule,
  SpreadsheetWorkbookSnapshot,
} from './spreadsheetEngineProtocol'
import {
  SPREADSHEET_ENGINE_PROTOCOL_VERSION,
  SPREADSHEET_ENGINE_SHEET_CHUNK_COLS,
  SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS,
  canonicalizeSpreadsheetValidationRule,
  buildSheetChunkId,
  SPREADSHEET_ENGINE_ERROR_CODES,
} from './spreadsheetEngineProtocol'

export type SpreadsheetExcelOoxmlPackagePart = {
  part_name: string
  byte_length: number
  content_type?: string | null
  relationship_type?: string | null
  source_part_name?: string | null
  relationship_id?: string | null
  relationship_target?: string | null
  relationship_target_mode?: string | null
}

export type SpreadsheetExcelOoxmlWorksheetPart = {
  name: string
  workbook_sheet_id: string
  relationship_id: string
  part_name: string
  row_count: number
  col_count: number
  formula_count: number
  table_count: number
  hidden?: boolean
}

export type SpreadsheetExcelOoxmlImportPreviewInput = {
  format: SpreadsheetExcelWorkbookIoFormat
  file_name: string
  package_bytes: Uint8Array | ArrayBuffer
  requestId?: string
  workbookId?: string
  workbookRevision?: number | null
}

export type SpreadsheetExcelOoxmlSnapshotImportInput = {
  package_bytes: Uint8Array | ArrayBuffer
  workbook_id: string
  workbook_revision?: number
}

export type SpreadsheetExcelOoxmlSnapshotImportResult = {
  snapshot: SpreadsheetWorkbookSnapshot
  warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export type SpreadsheetExcelOoxmlSnapshotExportInput = {
  snapshot: SpreadsheetWorkbookSnapshot
  format?: SpreadsheetExcelWorkbookIoFormat
  file_name?: string | null
  include_calculated_values?: boolean
  requestId?: string
}

export type SpreadsheetExcelOoxmlSnapshotExportResult = {
  package_bytes: Uint8Array
  warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export type SpreadsheetExcelOoxmlPackageInspection = {
  format: SpreadsheetExcelWorkbookIoFormat
  workbook_part_name: string
  workbook_relationships_part_name: string
  worksheets: SpreadsheetExcelOoxmlWorksheetPart[]
  parts: SpreadsheetExcelOoxmlPackagePart[]
  warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

type ContentTypes = {
  defaults: Map<string, string>
  overrides: Map<string, string>
}

type Relationship = {
  id: string
  type: string
  target: string
  target_mode?: string
}

const OFFICE_DOCUMENT_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument'
const WORKSHEET_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet'
const CORE_PROPERTIES_REL_TYPE = 'http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties'
const EXTENDED_PROPERTIES_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties'
const CUSTOM_PROPERTIES_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/custom-properties'
const EXTERNAL_LINK_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/externalLink'
const DRAWING_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing'
const CHART_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart'
const PIVOT_TABLE_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/pivotTable'
const PIVOT_CACHE_DEFINITION_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/pivotCacheDefinition'
const CALC_CHAIN_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/calcChain'
const SHEET_METADATA_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/sheetMetadata'
const SLICER_REL_TYPE = 'http://schemas.microsoft.com/office/2007/relationships/slicer'
const TIMELINE_REL_TYPE = 'http://schemas.microsoft.com/office/2011/relationships/timeline'

const CORE_MODELED_RELATIONSHIP_TYPES = new Set([
  OFFICE_DOCUMENT_REL_TYPE,
  WORKSHEET_REL_TYPE,
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles',
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings',
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme',
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/table',
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments',
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/vmlDrawing',
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink',
  CORE_PROPERTIES_REL_TYPE,
  EXTENDED_PROPERTIES_REL_TYPE,
  CUSTOM_PROPERTIES_REL_TYPE,
])

const ADVANCED_RELATIONSHIP_HINTS = [
  '/drawing',
  '/image',
  '/chart',
  '/pivotTable',
  '/pivotCacheDefinition',
  '/pivotCacheRecords',
  '/calcChain',
  '/slicer',
  '/slicerCache',
  '/timeline',
  '/timelineCache',
  '/queryTable',
  '/connections',
  '/externalLink',
  '/customXml',
  '/sheetMetadata',
  '/vbaProject',
  '/ctrlProp',
  '/oleObject',
  '/package',
]

const ADVANCED_CONTENT_TYPE_HINTS = [
  'image/',
  'drawing',
  'chart',
  'pivot',
  'calcchain',
  'slicer',
  'timeline',
  'querytable',
  'connections',
  'externallink',
  'customxml',
  'sheetmetadata',
  'vbaproject',
  'vbaProject',
  'oleObject',
  'oleobject',
  'controlproperties',
]

const normalizePartName = (partName: string): string => {
  const pieces: string[] = []
  for (const piece of String(partName || '').replace(/\\/g, '/').replace(/^\/+/, '').split('/')) {
    if (!piece || piece === '.') continue
    if (piece === '..') {
      pieces.pop()
      continue
    }
    pieces.push(piece)
  }
  return pieces.join('/')
}

const dirname = (partName: string): string => {
  const normalized = normalizePartName(partName)
  const slashIndex = normalized.lastIndexOf('/')
  return slashIndex < 0 ? '' : normalized.slice(0, slashIndex)
}

const relationshipsPartNameFor = (partName: string): string => {
  const normalized = normalizePartName(partName)
  if (!normalized) return '_rels/.rels'
  const dir = dirname(normalized)
  const fileName = normalized.slice(dir.length > 0 ? dir.length + 1 : 0)
  return normalizePartName(`${dir}/_rels/${fileName}.rels`)
}

const sourcePartNameForRelationshipsPart = (relationshipsPartName: string): string | null => {
  const normalized = normalizePartName(relationshipsPartName)
  if (normalized === '_rels/.rels') return ''
  const marker = '/_rels/'
  const markerIndex = normalized.lastIndexOf(marker)
  if (markerIndex < 0 || !normalized.endsWith('.rels')) return null
  const dir = normalized.slice(0, markerIndex)
  const fileName = normalized.slice(markerIndex + marker.length, -'.rels'.length)
  if (!fileName) return null
  return normalizePartName(`${dir}/${fileName}`)
}

const resolveRelationshipTarget = (sourcePartName: string, target: string): string => {
  const rawTarget = String(target || '').trim()
  if (rawTarget.startsWith('/')) return normalizePartName(rawTarget)
  return normalizePartName(`${dirname(sourcePartName)}/${rawTarget}`)
}

const decodeXmlAttr = (value: string): string => value
  .replace(/&quot;/g, '"')
  .replace(/&apos;/g, "'")
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&amp;/g, '&')

const encodeXmlText = (value: unknown): string => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')

const encodeXmlAttr = (value: unknown): string => encodeXmlText(value)
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;')

const parseAttributes = (tag: string): Record<string, string> => {
  const attributes: Record<string, string> = {}
  const attrPattern = /([A-Za-z_][\w:.-]*)\s*=\s*(["'])(.*?)\2/g
  let match: RegExpExecArray | null
  while ((match = attrPattern.exec(tag)) != null) {
    attributes[match[1]!] = decodeXmlAttr(match[3]!)
  }
  return attributes
}

const readXml = (files: Record<string, Uint8Array>, partName: string): string | null => {
  const file = files[normalizePartName(partName)]
  return file == null ? null : strFromU8(file)
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n += 1) {
    let c = n
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1)
    table[n] = c >>> 0
  }
  return table
})()

const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff
  for (const byte of bytes) crc = CRC32_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

const formatCrc32 = (bytes: Uint8Array): string =>
  crc32(bytes).toString(16).padStart(8, '0')

const encodeBytesToBase64 = (bytes: Uint8Array): string => {
  if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64')
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

const decodeBase64ToBytes = (value: string): Uint8Array => {
  if (typeof Buffer !== 'undefined') return new Uint8Array(Buffer.from(value, 'base64'))
  const binary = atob(value)
  const out = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) out[index] = binary.charCodeAt(index)
  return out
}

const pushU16 = (out: number[], value: number) => {
  out.push(value & 0xff, (value >>> 8) & 0xff)
}

const pushU32 = (out: number[], value: number) => {
  out.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff)
}

const pushBytes = (out: number[], bytes: Uint8Array) => {
  for (const byte of bytes) out.push(byte)
}

const zipStoredEntries = (entries: Record<string, Uint8Array>): Uint8Array => {
  const local: number[] = []
  const central: number[] = []
  let entryCount = 0
  for (const [rawPath, data] of Object.entries(entries).sort(([left], [right]) => left.localeCompare(right))) {
    const name = strToU8(normalizePartName(rawPath))
    const checksum = crc32(data)
    const localOffset = local.length
    pushU32(local, 0x04034b50)
    pushU16(local, 20)
    pushU16(local, 0)
    pushU16(local, 0)
    pushU16(local, 0)
    pushU16(local, 0)
    pushU32(local, checksum)
    pushU32(local, data.length)
    pushU32(local, data.length)
    pushU16(local, name.length)
    pushU16(local, 0)
    pushBytes(local, name)
    pushBytes(local, data)

    pushU32(central, 0x02014b50)
    pushU16(central, 20)
    pushU16(central, 20)
    pushU16(central, 0)
    pushU16(central, 0)
    pushU16(central, 0)
    pushU16(central, 0)
    pushU32(central, checksum)
    pushU32(central, data.length)
    pushU32(central, data.length)
    pushU16(central, name.length)
    pushU16(central, 0)
    pushU16(central, 0)
    pushU16(central, 0)
    pushU16(central, 0)
    pushU32(central, 0)
    pushU32(central, localOffset)
    pushBytes(central, name)
    entryCount += 1
  }
  const centralOffset = local.length
  const centralSize = central.length
  const out = [...local, ...central]
  pushU32(out, 0x06054b50)
  pushU16(out, 0)
  pushU16(out, 0)
  pushU16(out, entryCount)
  pushU16(out, entryCount)
  pushU32(out, centralSize)
  pushU32(out, centralOffset)
  pushU16(out, 0)
  return Uint8Array.from(out)
}

const parseContentTypes = (xml: string | null): ContentTypes => {
  const defaults = new Map<string, string>()
  const overrides = new Map<string, string>()
  if (xml == null) return { defaults, overrides }

  for (const match of xml.matchAll(/<Default\b[^>]*>/g)) {
    const attrs = parseAttributes(match[0])
    const extension = attrs.Extension?.trim().toLowerCase()
    const contentType = attrs.ContentType?.trim()
    if (extension && contentType) defaults.set(extension, contentType)
  }
  for (const match of xml.matchAll(/<Override\b[^>]*>/g)) {
    const attrs = parseAttributes(match[0])
    const partName = attrs.PartName?.trim()
    const contentType = attrs.ContentType?.trim()
    if (partName && contentType) overrides.set(normalizePartName(partName), contentType)
  }
  return { defaults, overrides }
}

const getContentType = (partName: string, contentTypes: ContentTypes): string | null => {
  const normalized = normalizePartName(partName)
  const override = contentTypes.overrides.get(normalized)
  if (override) return override
  const dotIndex = normalized.lastIndexOf('.')
  const extension = dotIndex >= 0 ? normalized.slice(dotIndex + 1).toLowerCase() : ''
  return contentTypes.defaults.get(extension) ?? null
}

const parseRelationships = (xml: string | null): Relationship[] => {
  if (xml == null) return []
  return Array.from(xml.matchAll(/<Relationship\b[^>]*>/g))
    .map((match) => parseAttributes(match[0]))
    .filter((attrs) => attrs.Id && attrs.Type && attrs.Target)
    .map((attrs) => ({
      id: attrs.Id!,
      type: attrs.Type!,
      target: attrs.Target!,
      target_mode: attrs.TargetMode,
    }))
}

const parseWorkbookSheets = (xml: string | null): SpreadsheetExcelOoxmlWorksheetPart[] => {
  if (xml == null) return []
  return Array.from(xml.matchAll(/<sheet\b[^>]*>/g))
    .map((match) => parseAttributes(match[0]))
    .filter((attrs) => attrs.name && attrs.sheetId && (attrs['r:id'] || attrs.id))
    .map((attrs) => ({
      name: attrs.name!,
      workbook_sheet_id: attrs.sheetId!,
      relationship_id: (attrs['r:id'] ?? attrs.id)!,
      part_name: '',
      row_count: 0,
      col_count: 0,
      formula_count: 0,
      table_count: 0,
      hidden: attrs.state === 'hidden' || attrs.state === 'veryHidden' ? true : undefined,
    }))
}

const parseSharedStrings = (xml: string | null): string[] => {
  if (xml == null) return []
  const sharedStrings: string[] = []
  for (const itemMatch of xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) {
    const itemXml = itemMatch[1] ?? ''
    const runs = Array.from(itemXml.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)).map((match) => decodeXmlText(match[1] ?? ''))
    sharedStrings.push(runs.join(''))
  }
  return sharedStrings
}

const decodeXmlText = (value: string): string => decodeXmlAttr(value)
  .replace(/\r\n/g, '\n')
  .replace(/\r/g, '\n')

const parseStyleCount = (xml: string | null): number => {
  if (xml == null) return 0
  const cellXfsMatch = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/i.exec(xml)
  if (cellXfsMatch == null) return 0
  return Array.from(cellXfsMatch[1]!.matchAll(/<xf\b[^>]*\/?>/g)).length
}

const BUILT_IN_NUMBER_FORMATS = new Map<number, string>([
  [0, 'General'],
  [1, '0'],
  [2, '0.00'],
  [3, '#,##0'],
  [4, '#,##0.00'],
  [9, '0%'],
  [10, '0.00%'],
  [14, 'm/d/yy'],
  [15, 'd-mmm-yy'],
  [16, 'd-mmm'],
  [17, 'mmm-yy'],
  [22, 'm/d/yy h:mm'],
  [37, '#,##0 ;(#,##0)'],
  [38, '#,##0 ;[Red](#,##0)'],
  [39, '#,##0.00;(#,##0.00)'],
  [40, '#,##0.00;[Red](#,##0.00)'],
  [49, '@'],
])

const THEME_COLOR_ORDER = [
  'lt1',
  'dk1',
  'lt2',
  'dk2',
  'accent1',
  'accent2',
  'accent3',
  'accent4',
  'accent5',
  'accent6',
  'hlink',
  'folHlink',
] as const

type OoxmlThemeColor = {
  name: string
  rgb: string
}

type OoxmlThemeColors = Map<number, OoxmlThemeColor>

const DEFAULT_OOXML_THEME_COLORS: OoxmlThemeColors = new Map([
  [0, { name: 'lt1', rgb: '#FFFFFF' }],
  [1, { name: 'dk1', rgb: '#000000' }],
  [2, { name: 'lt2', rgb: '#E7E6E6' }],
  [3, { name: 'dk2', rgb: '#44546A' }],
  [4, { name: 'accent1', rgb: '#4472C4' }],
  [5, { name: 'accent2', rgb: '#ED7D31' }],
  [6, { name: 'accent3', rgb: '#A5A5A5' }],
  [7, { name: 'accent4', rgb: '#FFC000' }],
  [8, { name: 'accent5', rgb: '#5B9BD5' }],
  [9, { name: 'accent6', rgb: '#70AD47' }],
  [10, { name: 'hlink', rgb: '#0563C1' }],
  [11, { name: 'folHlink', rgb: '#954F72' }],
])

const extractSectionXml = (xml: string | null, tagName: string): string => {
  if (xml == null) return ''
  const match = new RegExp(`<${tagName}\\b[^>]*>([\\s\\S]*?)<\\/${tagName}>`, 'i').exec(xml)
  return match?.[1] ?? ''
}

const parseElementEntries = (
  xml: string,
  tagName: string,
): Array<{ attrs: Record<string, string>; body: string; raw: string }> => {
  const entries: Array<{ attrs: Record<string, string>; body: string; raw: string }> = []
  const pattern = new RegExp(`<${tagName}\\b([^>]*)\\/>|<${tagName}\\b([^>]*)>([\\s\\S]*?)<\\/${tagName}>`, 'gi')
  let match: RegExpExecArray | null
  while ((match = pattern.exec(xml)) != null) {
    const raw = match[0] ?? ''
    entries.push({
      attrs: parseAttributes(match[1] ?? match[2] ?? ''),
      body: match[3] ?? '',
      raw,
    })
  }
  return entries
}

const firstElementAttrs = (xml: string, tagName: string): Record<string, string> | null => {
  const selfClosingMatch = new RegExp(`<${tagName}\\b([^>]*)\\/>`, 'i').exec(xml)
  const openMatch = new RegExp(`<${tagName}\\b([^>]*)>`, 'i').exec(xml)
  const match = selfClosingMatch ?? openMatch
  return match == null ? null : parseAttributes(match[1] ?? '')
}

const firstRawElementXml = (xml: string | null, tagName: string): string | null => {
  if (xml == null) return null
  const match = new RegExp(`<${tagName}\\b[^>]*\\/>|<${tagName}\\b[^>]*>[\\s\\S]*?<\\/${tagName}>`, 'i').exec(xml)
  return match?.[0] ?? null
}

const parseHexColor = (value: string | undefined): [number, number, number] | null => {
  const normalized = String(value ?? '').trim().replace(/^#/, '')
  const hex = normalized.length === 8 ? normalized.slice(2) : normalized
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ]
}

const formatHexColor = ([red, green, blue]: [number, number, number]): string => {
  const toHex = (channel: number) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0').toUpperCase()
  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`
}

const applyTintToColor = (rgb: string, tint: number): string => {
  const channels = parseHexColor(rgb)
  if (channels == null || !Number.isFinite(tint) || tint === 0) return rgb
  return formatHexColor(channels.map((channel) => (
    tint < 0 ? channel * (1 + tint) : channel * (1 - tint) + 255 * tint
  )) as [number, number, number])
}

const normalizeRgbColor = (rgb: string): string | null => {
  const channels = parseHexColor(rgb)
  return channels == null ? null : formatHexColor(channels)
}

const parseThemeColors = (xml: string | null): OoxmlThemeColors => {
  const themeColors: OoxmlThemeColors = new Map()
  if (xml == null) return themeColors
  THEME_COLOR_ORDER.forEach((name, index) => {
    const pattern = new RegExp(`<(?:a:)?${name}\\b[^>]*>([\\s\\S]*?)<\\/(?:a:)?${name}>`, 'i')
    const body = pattern.exec(xml)?.[1] ?? ''
    const srgb = /<(?:a:)?srgbClr\b[^>]*\bval\s*=\s*(["'])(.*?)\1[^>]*>/i.exec(body)?.[2]
    const sys = /<(?:a:)?sysClr\b[^>]*\blastClr\s*=\s*(["'])(.*?)\1[^>]*>/i.exec(body)?.[2]
    const rgb = normalizeRgbColor(srgb ?? sys ?? '')
    if (rgb != null) themeColors.set(index, { name, rgb })
  })
  return themeColors
}

const parseWorkbookTheme = (xml: string | null): SpreadsheetWorkbookTheme | null => {
  if (xml == null || xml.trim().length === 0) return null
  const themeName = /<(?:a:)?theme\b([^>]*)>/i.exec(xml)?.[1]
  const colorSchemeMatch = /<(?:a:)?clrScheme\b([^>]*)>([\s\S]*?)<\/(?:a:)?clrScheme>/i.exec(xml)
  const colorSchemeAttrs = parseAttributes(colorSchemeMatch?.[1] ?? '')
  const colorSchemeBody = colorSchemeMatch?.[2] ?? ''
  const colors: NonNullable<NonNullable<SpreadsheetWorkbookTheme['color_scheme']>['colors']> = {}
  THEME_COLOR_ORDER.forEach((name) => {
    const pattern = new RegExp(`<(?:a:)?${name}\\b[^>]*>([\\s\\S]*?)<\\/(?:a:)?${name}>`, 'i')
    const body = pattern.exec(colorSchemeBody)?.[1] ?? ''
    if (!body) return
    const srgb = /<(?:a:)?srgbClr\b[^>]*\bval\s*=\s*(["'])(.*?)\1[^>]*>/i.exec(body)?.[2]
    const sys = /<(?:a:)?sysClr\b[^>]*\bval\s*=\s*(["'])(.*?)\1[^>]*>/i.exec(body)?.[2]
    const last = /<(?:a:)?sysClr\b[^>]*\blastClr\s*=\s*(["'])(.*?)\1[^>]*>/i.exec(body)?.[2]
    const rgb = normalizeRgbColor(srgb ?? last ?? '')
    colors[name] = {
      name,
      ...(rgb == null ? {} : { rgb }),
      ...(sys == null ? {} : { system_color: sys }),
      ...(last == null ? {} : { last_color: normalizeRgbColor(last) ?? last }),
    }
  })
  return {
    name: parseAttributes(themeName ?? '').name ?? null,
    color_scheme: {
      name: colorSchemeAttrs.name ?? null,
      colors,
    },
  }
}

const parseOoxmlColor = (xml: string, themeColors: OoxmlThemeColors): Record<string, unknown> | undefined => {
  const attrs = firstElementAttrs(xml, 'color') ?? firstElementAttrs(xml, 'fgColor') ?? firstElementAttrs(xml, 'bgColor')
  if (attrs == null) return undefined
  return parseOoxmlColorAttrs(attrs, themeColors)
}

const parseOoxmlColorAttrs = (
  attrs: Record<string, string>,
  themeColors: OoxmlThemeColors,
): Record<string, unknown> | undefined => {
  const color: Record<string, unknown> = {}
  if (attrs.rgb) color.rgb = normalizeRgbColor(attrs.rgb) ?? (attrs.rgb.startsWith('#') ? attrs.rgb : `#${attrs.rgb.slice(-6)}`)
  const themeIndex = Number(attrs.theme)
  if (attrs.theme) {
    color.theme = Number.isFinite(themeIndex) ? themeIndex : attrs.theme
    const themeColor = Number.isFinite(themeIndex) ? themeColors.get(themeIndex) : undefined
    if (themeColor != null) {
      color.themeName = themeColor.name
      color.rgb = applyTintToColor(themeColor.rgb, Number(attrs.tint ?? 0))
    }
  }
  if (attrs.indexed) color.indexed = Number.isFinite(Number(attrs.indexed)) ? Number(attrs.indexed) : attrs.indexed
  if (attrs.tint) color.tint = Number(attrs.tint)
  if (attrs.auto === '1' || attrs.auto === 'true') color.auto = true
  return Object.keys(color).length > 0 ? color : undefined
}

const parseOoxmlNamedColor = (
  xml: string,
  tagName: string,
  themeColors: OoxmlThemeColors,
): Record<string, unknown> | undefined => {
  const attrs = firstElementAttrs(xml, tagName)
  return attrs == null ? undefined : parseOoxmlColorAttrs(attrs, themeColors)
}

const compactUnknownRecord = (record: Record<string, unknown>): Record<string, unknown> => {
  const compacted: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(record)) {
    if (value == null || value === '') continue
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = compactUnknownRecord(value as Record<string, unknown>)
      if (Object.keys(nested).length > 0) compacted[key] = nested
      continue
    }
    compacted[key] = value
  }
  return compacted
}

const parseOoxmlFontStyle = (
  fontXml: string | null | undefined,
  themeColors: OoxmlThemeColors,
): Record<string, unknown> | undefined => {
  const body = fontXml ?? ''
  if (!body) return undefined
  const font: Record<string, unknown> = {}
  if (/<b\b/i.test(body)) font.bold = true
  if (/<i\b/i.test(body)) font.italic = true
  if (/<strike\b/i.test(body)) font.strikethrough = true
  const underlineAttrs = firstElementAttrs(body, 'u')
  if (underlineAttrs != null) font.underline = underlineAttrs.val ?? true
  const size = Number(firstElementAttrs(body, 'sz')?.val)
  if (Number.isFinite(size)) font.size = size
  const name = firstElementAttrs(body, 'name')?.val
  if (name) font.family = name
  const color = parseOoxmlColor(body, themeColors)
  if (color != null) font.color = color
  const compacted = compactUnknownRecord(font)
  return Object.keys(compacted).length > 0 ? compacted : undefined
}

const parseOoxmlFillStyle = (
  fillXml: string | null | undefined,
  themeColors: OoxmlThemeColors,
): Record<string, unknown> | undefined => {
  const rawBody = fillXml ?? ''
  const body = parseElementEntries(rawBody, 'fill')[0]?.body ?? rawBody
  const patternAttrs = firstElementAttrs(body, 'patternFill')
  if (patternAttrs == null) return undefined
  const fill: Record<string, unknown> = { patternType: patternAttrs.patternType ?? 'none' }
  const foreground = parseOoxmlNamedColor(body, 'fgColor', themeColors)
  const background = parseOoxmlNamedColor(body, 'bgColor', themeColors)
  if (foreground != null) fill.foregroundColor = foreground
  if (background != null) fill.backgroundColor = background
  const compacted = compactUnknownRecord(fill)
  return Object.keys(compacted).length > 0 ? compacted : undefined
}

const parseOoxmlBorderStyle = (
  borderXml: string | null | undefined,
  themeColors: OoxmlThemeColors,
): Record<string, unknown> | undefined => {
  const body = borderXml ?? ''
  const border: Record<string, unknown> = {}
  for (const edge of ['left', 'right', 'top', 'bottom', 'diagonal'] as const) {
    const entry = parseElementEntries(body, edge)[0]
    if (entry == null) continue
    const edgeStyle: Record<string, unknown> = {}
    if (entry.attrs.style) edgeStyle.style = entry.attrs.style
    const color = parseOoxmlColor(entry.body, themeColors)
    if (color != null) edgeStyle.color = color
    if (Object.keys(edgeStyle).length > 0) border[edge] = edgeStyle
  }
  const compacted = compactUnknownRecord(border)
  return Object.keys(compacted).length > 0 ? compacted : undefined
}

const parseOoxmlProtectionStyle = (xml: string): Record<string, unknown> | undefined => {
  const attrs = firstElementAttrs(xml, 'protection')
  if (attrs == null) return undefined
  const protection: Record<string, unknown> = {}
  if (attrs.locked != null) protection.locked = attrs.locked === '1' || attrs.locked === 'true'
  if (attrs.hidden != null) protection.hidden = attrs.hidden === '1' || attrs.hidden === 'true'
  return Object.keys(protection).length > 0 ? protection : undefined
}

const buildStyleFromOoxmlParts = ({
  attrs,
  body,
  index,
  fonts,
  fills,
  borders,
  numberFormats,
}: {
  attrs: Record<string, string>
  body: string
  index: number
  fonts: Array<Record<string, unknown> | undefined>
  fills: Array<Record<string, unknown> | undefined>
  borders: Array<Record<string, unknown> | undefined>
  numberFormats: Map<number, string>
}): Record<string, unknown> => {
  const style: Record<string, unknown> = { ooxml_style_index: index }
  const numberFormatId = Number(attrs.numFmtId)
  if (Number.isFinite(numberFormatId)) {
    const formatCode = numberFormats.get(numberFormatId) ?? BUILT_IN_NUMBER_FORMATS.get(numberFormatId)
    style.numberFormat = {
      ooxml_num_fmt_id: numberFormatId,
      ...(formatCode ? { formatCode } : null),
    }
  }
  const font = fonts[Number(attrs.fontId)]
  if (font != null) style.font = font
  const fill = fills[Number(attrs.fillId)]
  if (fill != null) style.fill = fill
  const border = borders[Number(attrs.borderId)]
  if (border != null) style.border = border
  const alignmentAttrs = firstElementAttrs(body, 'alignment')
  if (alignmentAttrs != null) {
    style.alignment = compactUnknownRecord({
      horizontal: alignmentAttrs.horizontal,
      vertical: alignmentAttrs.vertical,
      wrapText: alignmentAttrs.wrapText == null ? undefined : alignmentAttrs.wrapText === '1' || alignmentAttrs.wrapText === 'true',
      shrinkToFit: alignmentAttrs.shrinkToFit == null ? undefined : alignmentAttrs.shrinkToFit === '1' || alignmentAttrs.shrinkToFit === 'true',
      indent: alignmentAttrs.indent == null ? undefined : Number(alignmentAttrs.indent),
      textRotation: alignmentAttrs.textRotation == null ? undefined : Number(alignmentAttrs.textRotation),
    })
  }
  const protection = parseOoxmlProtectionStyle(body)
  if (protection != null) style.protection = protection
  return compactUnknownRecord(style)
}

const parseOoxmlStyleRecords = (
  xml: string | null,
  themeColors: OoxmlThemeColors,
): Record<string, Record<string, unknown>> => {
  const styleXml = xml ?? ''
  const numberFormats = new Map<number, string>()
  for (const entry of parseElementEntries(extractSectionXml(styleXml, 'numFmts'), 'numFmt')) {
    const id = Number(entry.attrs.numFmtId)
    if (Number.isFinite(id) && entry.attrs.formatCode) numberFormats.set(id, entry.attrs.formatCode)
  }
  const fonts = parseElementEntries(extractSectionXml(styleXml, 'fonts'), 'font').map((entry) => parseOoxmlFontStyle(entry.body, themeColors))
  const fills = parseElementEntries(extractSectionXml(styleXml, 'fills'), 'fill').map((entry) => parseOoxmlFillStyle(entry.body, themeColors))
  const borders = parseElementEntries(extractSectionXml(styleXml, 'borders'), 'border').map((entry) => parseOoxmlBorderStyle(entry.body, themeColors))
  const styles: Record<string, Record<string, unknown>> = {}
  parseElementEntries(extractSectionXml(styleXml, 'cellXfs'), 'xf').forEach((entry, index) => {
    styles[`ooxml_style_${index}`] = buildStyleFromOoxmlParts({
      attrs: entry.attrs,
      body: entry.body,
      index,
      fonts,
      fills,
      borders,
      numberFormats,
    })
  })
  parseElementEntries(extractSectionXml(styleXml, 'dxfs'), 'dxf').forEach((entry, index) => {
    const style = compactUnknownRecord({
      ooxml_dxf_index: index,
      font: parseOoxmlFontStyle(entry.body, themeColors),
      fill: parseOoxmlFillStyle(entry.body, themeColors),
      border: parseOoxmlBorderStyle(entry.body, themeColors),
      numberFormat: (() => {
        const attrs = firstElementAttrs(entry.body, 'numFmt')
        if (attrs == null) return undefined
        const id = Number(attrs.numFmtId)
        return compactUnknownRecord({
          ooxml_num_fmt_id: Number.isFinite(id) ? id : attrs.numFmtId,
          formatCode: attrs.formatCode,
        })
      })(),
      protection: parseOoxmlProtectionStyle(entry.body),
    })
    styles[`ooxml_dxf_${index}`] = style
  })
  if (Object.keys(styles).length > 0) return styles
  return parseStyleCount(xml) === 0 ? {} : Object.fromEntries(Array.from({ length: parseStyleCount(xml) }, (_, index) => [
    `ooxml_style_${index}`,
    { ooxml_style_index: index },
  ]))
}

const parseOoxmlTableStyles = (
  xml: string | null,
  styles: Record<string, Record<string, unknown>>,
): Record<string, SpreadsheetTableStyleDefinition> | undefined => {
  const tableStylesXml = extractSectionXml(xml ?? '', 'tableStyles')
  if (!tableStylesXml) return undefined
  const tableStyles: Record<string, SpreadsheetTableStyleDefinition> = {}
  for (const entry of parseElementEntries(tableStylesXml, 'tableStyle')) {
    const name = String(entry.attrs.name ?? '').trim()
    if (!name) continue
    const elements = parseElementEntries(entry.body, 'tableStyleElement')
      .map((element) => {
        const type = String(element.attrs.type ?? '').trim()
        if (!type) return null
        const dxfIndex = element.attrs.dxfId == null ? null : Number(element.attrs.dxfId)
        const dxfId = dxfIndex != null && Number.isInteger(dxfIndex) && dxfIndex >= 0 ? `ooxml_dxf_${dxfIndex}` : null
        const size = Number(element.attrs.size)
        return compactUnknownRecord({
          type,
          size: Number.isInteger(size) && size > 0 ? size : undefined,
          dxf_id: dxfId ?? undefined,
          style: dxfId == null ? undefined : styles[dxfId],
        }) as NonNullable<SpreadsheetTableStyleDefinition['elements']>[number]
      })
      .filter((element): element is NonNullable<SpreadsheetTableStyleDefinition['elements']>[number] => element != null)
    tableStyles[name] = {
      name,
      is_table: parseOoxmlBoolean(entry.attrs.table),
      is_pivot: parseOoxmlBoolean(entry.attrs.pivot),
      ...(elements.length > 0 ? { elements } : null),
    }
  }
  return Object.keys(tableStyles).length > 0 ? tableStyles : undefined
}

const columnIndexFromLetters = (letters: string): number => {
  let value = 0
  for (const char of letters.toUpperCase()) value = value * 26 + (char.charCodeAt(0) - 64)
  return Math.max(0, value - 1)
}

const parseCellRef = (ref: string): { row: number; col: number } | null => {
  const match = /^\$?([A-Za-z]+)\$?([1-9]\d*)$/.exec(String(ref || '').trim())
  if (!match) return null
  return {
    row: Number(match[2]) - 1,
    col: columnIndexFromLetters(match[1]!),
  }
}

const parseA1RangeRef = (ref: string, sheetId: string): SpreadsheetRangeAddress | null => {
  const trimmed = String(ref || '').trim().replace(/\$/g, '')
  const [startRef, endRef = startRef] = trimmed.split(':')
  const start = parseCellRef(startRef ?? '')
  const end = parseCellRef(endRef ?? '')
  if (start == null || end == null) return null
  return {
    sheet_id: sheetId,
    start_row: Math.min(start.row, end.row),
    start_col: Math.min(start.col, end.col),
    end_row: Math.max(start.row, end.row),
    end_col: Math.max(start.col, end.col),
  }
}

const unquoteSheetName = (value: string): string => {
  const trimmed = value.trim()
  if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
    return trimmed.slice(1, -1).replace(/''/g, "'")
  }
  return trimmed
}

const parseFormulaRangeRef = (
  formulaText: string,
  fallbackSheetId: string,
  sheetIdByName: Map<string, string>,
): SpreadsheetRangeAddress | null => {
  let formula = String(formulaText || '').trim()
  if (formula.startsWith('=')) formula = formula.slice(1).trim()
  const bangIndex = formula.lastIndexOf('!')
  const sheetId = bangIndex >= 0
    ? sheetIdByName.get(unquoteSheetName(formula.slice(0, bangIndex))) ?? fallbackSheetId
    : fallbackSheetId
  const rangeText = bangIndex >= 0 ? formula.slice(bangIndex + 1) : formula
  return parseA1RangeRef(rangeText, sheetId)
}

const extractFirstQualifiedTagText = (xml: string, localName: string): string | null => {
  const match = new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${localName}\\b[^>]*>([\\s\\S]*?)<\\/(?:[A-Za-z_][\\w.-]*:)?${localName}>`, 'i').exec(xml)
  return match == null ? null : match[1] ?? ''
}

const extractFirstQualifiedTag = (xml: string, localName: string): string | null => {
  const match = new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${localName}\\b[^>]*\\/?>`, 'i').exec(xml)
  return match == null ? null : match[0] ?? ''
}

const extractQualifiedTagBlocks = (xml: string, localName: string): string[] => {
  const blocks: string[] = []
  const pattern = new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${localName}\\b[^>]*>[\\s\\S]*?<\\/(?:[A-Za-z_][\\w.-]*:)?${localName}>`, 'gi')
  let match: RegExpExecArray | null
  while ((match = pattern.exec(xml)) != null) blocks.push(match[0] ?? '')
  return blocks
}

const extractFirstQualifiedTagBlock = (xml: string, localName: string): string | null =>
  extractQualifiedTagBlocks(xml, localName)[0] ?? null

const OOXML_CHART_LOCAL_NAMES = [
  'barChart',
  'lineChart',
  'areaChart',
  'pieChart',
  'doughnutChart',
  'scatterChart',
  'bubbleChart',
  'radarChart',
  'surfaceChart',
  'stockChart',
] as const

const getOoxmlChartLocalNames = (chartXml: string): string[] => (
  Array.from(chartXml.matchAll(/<(?:[A-Za-z_][\w.-]*:)?([A-Za-z_][\w.-]*Chart)\b/gi))
    .map((match) => match[1] ?? '')
    .filter((localName): localName is typeof OOXML_CHART_LOCAL_NAMES[number] => (
      OOXML_CHART_LOCAL_NAMES.includes(localName as typeof OOXML_CHART_LOCAL_NAMES[number])
    ))
)

const parseOoxmlChartTypeForLocalName = (
  chartXml: string,
  localName: string,
): SpreadsheetChartArtifactType | null => {
  if (localName === 'lineChart') return 'line'
  if (localName === 'areaChart') return 'area'
  if (localName === 'pieChart' || localName === 'doughnutChart') return 'pie'
  if (localName === 'scatterChart') return 'scatter'
  if (localName === 'barChart') {
    const barChart = extractFirstQualifiedTagBlock(chartXml, 'barChart')
    const barDirAttrs = parseAttributes(extractFirstQualifiedTag(barChart ?? '', 'barDir') ?? '')
    return barDirAttrs.val === 'bar' ? 'bar' : 'column'
  }
  return null
}

const parseOoxmlChartType = (
  chartXml: string,
  chartLocalNames = getOoxmlChartLocalNames(chartXml),
): SpreadsheetChartArtifactType | null => (
  chartLocalNames
    .map((localName) => parseOoxmlChartTypeForLocalName(chartXml, localName))
    .find((chartType): chartType is SpreadsheetChartArtifactType => chartType != null) ?? null
)

const getOoxmlChartComponentTypes = (
  chartXml: string,
  chartLocalNames: string[],
): SpreadsheetChartArtifactType[] => {
  const seen = new Set<SpreadsheetChartArtifactType>()
  const out: SpreadsheetChartArtifactType[] = []
  for (const localName of chartLocalNames) {
    const chartType = parseOoxmlChartTypeForLocalName(chartXml, localName)
    if (chartType == null || seen.has(chartType)) continue
    seen.add(chartType)
    out.push(chartType)
  }
  return out
}

const parseOoxmlRichTextTitle = (titleBlock: string): string => {
  const text = Array.from(titleBlock.matchAll(/<(?:[A-Za-z_][\w.-]*:)?t\b[^>]*>([\s\S]*?)<\/(?:[A-Za-z_][\w.-]*:)?t>/gi))
    .map((match) => decodeXmlText(match[1] ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim()
  return text
}

const parseOoxmlChartTitle = (chartXml: string, fallback: string): string => {
  const titleBlock = extractFirstQualifiedTagBlock(chartXml, 'title') ?? ''
  return parseOoxmlRichTextTitle(titleBlock) || fallback
}

type OoxmlChartTextStyleMetadata = {
  fontColor?: string
  fontSize?: number
  fontBold?: boolean
  fontItalic?: boolean
  textRotation?: number
  textAlign?: 'left' | 'center' | 'right'
  textVerticalAlign?: 'top' | 'middle' | 'bottom'
  textWrap?: boolean
  textAutoFit?: 'normal' | 'shape' | 'none'
  textAutoFitFontScale?: number
  textHorizontalOverflow?: 'clip' | 'overflow'
  textVerticalOverflow?: 'clip' | 'ellipsis' | 'overflow'
  textInsetTop?: number
  textInsetRight?: number
  textInsetBottom?: number
  textInsetLeft?: number
}

const parseOoxmlChartTextAlignment = (textPropertiesBlock: string): OoxmlChartTextStyleMetadata['textAlign'] => {
  const paragraphPropertiesTag = extractFirstQualifiedTag(textPropertiesBlock, 'pPr') ?? ''
  const align = String(parseAttributes(paragraphPropertiesTag).algn ?? '').trim()
  switch (align) {
    case 'l':
      return 'left'
    case 'ctr':
      return 'center'
    case 'r':
      return 'right'
    default:
      return undefined
  }
}

const parseOoxmlChartTextRotation = (bodyPropertiesTag: string): number | undefined => {
  const attrs = parseAttributes(bodyPropertiesTag)
  const rawRotation = Number(attrs.rot)
  if (Number.isFinite(rawRotation)) {
    let degrees = rawRotation / 60000
    while (degrees > 180) degrees -= 360
    while (degrees < -180) degrees += 360
    return Math.round(degrees * 10) / 10
  }
  switch (String(attrs.vert ?? '').trim()) {
    case 'vert':
    case 'wordArtVert':
      return 90
    case 'vert270':
    case 'wordArtVertRtl':
      return -90
    default:
      return undefined
  }
}

const parseOoxmlChartTextVerticalAlignment = (bodyPropertiesTag: string): OoxmlChartTextStyleMetadata['textVerticalAlign'] => {
  const anchor = String(parseAttributes(bodyPropertiesTag).anchor ?? '').trim()
  switch (anchor) {
    case 't':
      return 'top'
    case 'ctr':
      return 'middle'
    case 'b':
      return 'bottom'
    default:
      return undefined
  }
}

const parseOoxmlChartTextWrap = (bodyPropertiesTag: string): boolean | undefined => {
  const wrap = String(parseAttributes(bodyPropertiesTag).wrap ?? '').trim()
  if (!wrap) return undefined
  return wrap !== 'none'
}

const parseOoxmlChartTextHorizontalOverflow = (bodyPropertiesTag: string): OoxmlChartTextStyleMetadata['textHorizontalOverflow'] => {
  const overflow = String(parseAttributes(bodyPropertiesTag).horzOverflow ?? '').trim()
  if (overflow === 'clip' || overflow === 'overflow') return overflow
  return undefined
}

const parseOoxmlChartTextVerticalOverflow = (bodyPropertiesTag: string): OoxmlChartTextStyleMetadata['textVerticalOverflow'] => {
  const overflow = String(parseAttributes(bodyPropertiesTag).vertOverflow ?? '').trim()
  if (overflow === 'clip' || overflow === 'ellipsis' || overflow === 'overflow') return overflow
  return undefined
}

const parseOoxmlChartTextInset = (value: unknown): number | undefined => {
  const emu = Number(value)
  if (!Number.isFinite(emu) || emu < 0) return undefined
  const points = emu / 12700
  const px = points * (96 / 72)
  return Math.max(0, Math.min(32, Math.round(px * 10) / 10))
}

const parseOoxmlChartTextAutoFit = (textPropertiesBlock: string): Pick<OoxmlChartTextStyleMetadata, 'textAutoFit' | 'textAutoFitFontScale'> => {
  const normalAutoFitTag = extractFirstQualifiedTag(textPropertiesBlock, 'normAutofit')
  if (normalAutoFitTag) {
    const fontScale = Number(parseAttributes(normalAutoFitTag).fontScale)
    const normalizedScale = Number.isFinite(fontScale) && fontScale > 0
      ? Math.max(20, Math.min(100, Math.round((fontScale / 1000) * 10) / 10))
      : undefined
    return {
      textAutoFit: 'normal',
      ...(normalizedScale == null ? {} : { textAutoFitFontScale: normalizedScale }),
    }
  }
  if (extractFirstQualifiedTag(textPropertiesBlock, 'spAutoFit')) return { textAutoFit: 'shape' }
  if (extractFirstQualifiedTag(textPropertiesBlock, 'noAutofit')) return { textAutoFit: 'none' }
  return {}
}

const parseOoxmlChartTextStyle = (
  textOwnerBlock: string,
  themeColors: OoxmlThemeColors,
): OoxmlChartTextStyleMetadata => {
  const textPropertiesBlock = extractFirstQualifiedTagBlock(textOwnerBlock, 'txPr') ?? textOwnerBlock
  const bodyPropertiesTag = extractFirstQualifiedTag(textPropertiesBlock, 'bodyPr') ?? ''
  const runPropertiesTag = extractFirstQualifiedTag(textPropertiesBlock, 'defRPr') ?? extractFirstQualifiedTag(textPropertiesBlock, 'rPr') ?? ''
  const runPropertiesBlock = extractFirstQualifiedTagBlock(textPropertiesBlock, 'defRPr') ?? extractFirstQualifiedTagBlock(textPropertiesBlock, 'rPr') ?? runPropertiesTag
  const attrs = parseAttributes(runPropertiesTag)
  const size = Number(attrs.sz)
  const fontSize = Number.isFinite(size) && size > 0 ? Math.min(48, Math.max(6, Math.round((size / 100) * 10) / 10)) : undefined
  const fontColor = parseOoxmlDrawingColor(runPropertiesBlock, themeColors)
  const textRotation = parseOoxmlChartTextRotation(bodyPropertiesTag)
  const textAlign = parseOoxmlChartTextAlignment(textPropertiesBlock)
  const textVerticalAlign = parseOoxmlChartTextVerticalAlignment(bodyPropertiesTag)
  const textWrap = parseOoxmlChartTextWrap(bodyPropertiesTag)
  const textAutoFit = parseOoxmlChartTextAutoFit(textPropertiesBlock)
  const textHorizontalOverflow = parseOoxmlChartTextHorizontalOverflow(bodyPropertiesTag)
  const textVerticalOverflow = parseOoxmlChartTextVerticalOverflow(bodyPropertiesTag)
  const bodyAttrs = parseAttributes(bodyPropertiesTag)
  return {
    ...(fontColor ? { fontColor } : {}),
    ...(fontSize == null ? {} : { fontSize }),
    ...(parseOoxmlBooleanVal(attrs.b) == null ? {} : { fontBold: parseOoxmlBooleanVal(attrs.b) }),
    ...(parseOoxmlBooleanVal(attrs.i) == null ? {} : { fontItalic: parseOoxmlBooleanVal(attrs.i) }),
    ...(textRotation == null ? {} : { textRotation }),
    ...(textAlign == null ? {} : { textAlign }),
    ...(textVerticalAlign == null ? {} : { textVerticalAlign }),
    ...(textWrap == null ? {} : { textWrap }),
    ...textAutoFit,
    ...(textHorizontalOverflow == null ? {} : { textHorizontalOverflow }),
    ...(textVerticalOverflow == null ? {} : { textVerticalOverflow }),
    ...(parseOoxmlChartTextInset(bodyAttrs.tIns) == null ? {} : { textInsetTop: parseOoxmlChartTextInset(bodyAttrs.tIns) }),
    ...(parseOoxmlChartTextInset(bodyAttrs.rIns) == null ? {} : { textInsetRight: parseOoxmlChartTextInset(bodyAttrs.rIns) }),
    ...(parseOoxmlChartTextInset(bodyAttrs.bIns) == null ? {} : { textInsetBottom: parseOoxmlChartTextInset(bodyAttrs.bIns) }),
    ...(parseOoxmlChartTextInset(bodyAttrs.lIns) == null ? {} : { textInsetLeft: parseOoxmlChartTextInset(bodyAttrs.lIns) }),
  }
}

const prefixedOoxmlChartTextStyle = (
  style: OoxmlChartTextStyleMetadata,
  prefix: 'chartTitle' | 'categoryAxisTitle' | 'valueAxisTitle' | 'secondaryValueAxisTitle' | 'dataLabel',
): Record<string, string | number | boolean> => ({
  ...(style.fontColor ? { [`${prefix}FontColor`]: style.fontColor } : {}),
  ...(style.fontSize == null ? {} : { [`${prefix}FontSize`]: style.fontSize }),
  ...(style.fontBold == null ? {} : { [`${prefix}FontBold`]: style.fontBold }),
  ...(style.fontItalic == null ? {} : { [`${prefix}FontItalic`]: style.fontItalic }),
  ...(style.textRotation == null ? {} : { [`${prefix}TextRotation`]: style.textRotation }),
  ...(style.textAlign == null ? {} : { [`${prefix}TextAlign`]: style.textAlign }),
  ...(style.textVerticalAlign == null ? {} : { [`${prefix}TextVerticalAlign`]: style.textVerticalAlign }),
  ...(style.textWrap == null ? {} : { [`${prefix}TextWrap`]: style.textWrap }),
  ...(style.textAutoFit == null ? {} : { [`${prefix}TextAutoFit`]: style.textAutoFit }),
  ...(style.textAutoFitFontScale == null ? {} : { [`${prefix}TextAutoFitFontScale`]: style.textAutoFitFontScale }),
  ...(style.textHorizontalOverflow == null ? {} : { [`${prefix}TextHorizontalOverflow`]: style.textHorizontalOverflow }),
  ...(style.textVerticalOverflow == null ? {} : { [`${prefix}TextVerticalOverflow`]: style.textVerticalOverflow }),
  ...(style.textInsetTop == null ? {} : { [`${prefix}TextInsetTop`]: style.textInsetTop }),
  ...(style.textInsetRight == null ? {} : { [`${prefix}TextInsetRight`]: style.textInsetRight }),
  ...(style.textInsetBottom == null ? {} : { [`${prefix}TextInsetBottom`]: style.textInsetBottom }),
  ...(style.textInsetLeft == null ? {} : { [`${prefix}TextInsetLeft`]: style.textInsetLeft }),
})

const parseOoxmlChartTitleTextStyle = (
  chartXml: string,
  themeColors: OoxmlThemeColors,
): Record<string, string | number | boolean> => {
  const titleBlock = extractFirstQualifiedTagBlock(chartXml, 'title') ?? ''
  return prefixedOoxmlChartTextStyle(parseOoxmlChartTextStyle(titleBlock, themeColors), 'chartTitle')
}

type OoxmlChartShapeMetadata = {
  fillColor?: string
  fillOpacity?: number
  lineColor?: string
  lineWidth?: number
  fillPattern?: OoxmlChartSeriesReference['fillPattern']
  fillForegroundColor?: string
  fillBackgroundColor?: string
}

const parseOoxmlShapeFillPattern = (shapeProperties: string): OoxmlChartSeriesReference['fillPattern'] | undefined => {
  const patternAttrs = parseAttributes(extractFirstQualifiedTag(shapeProperties, 'pattFill') ?? '')
  switch (String(patternAttrs.prst ?? '').trim()) {
    case 'pct5':
    case 'pct10':
    case 'pct20':
    case 'pct25':
    case 'pct30':
    case 'pct40':
    case 'pct50':
    case 'pct60':
    case 'pct70':
    case 'pct75':
    case 'pct80':
    case 'pct90':
      return 'percent'
    case 'ltHorz':
    case 'horz':
    case 'dkHorz':
      return 'horizontal'
    case 'ltVert':
    case 'vert':
    case 'dkVert':
      return 'vertical'
    case 'ltDnDiag':
    case 'dnDiag':
    case 'dkDnDiag':
      return 'diagonalDown'
    case 'ltUpDiag':
    case 'upDiag':
    case 'dkUpDiag':
      return 'diagonalUp'
    case 'smGrid':
    case 'lgGrid':
    case 'smCheck':
    case 'lgCheck':
      return 'cross'
    case 'diagCross':
      return 'diagonalCross'
    default:
      return undefined
  }
}

const parseOoxmlChartShapeMetadata = (
  shapeProperties: string,
  themeColors: OoxmlThemeColors,
): OoxmlChartShapeMetadata => ({
  ...(parseOoxmlShapeFillColor(shapeProperties, themeColors) ? { fillColor: parseOoxmlShapeFillColor(shapeProperties, themeColors) } : {}),
  ...(parseOoxmlShapeFillOpacity(shapeProperties) != null ? { fillOpacity: parseOoxmlShapeFillOpacity(shapeProperties) } : {}),
  ...(parseOoxmlShapeLineColor(shapeProperties, themeColors) ? { lineColor: parseOoxmlShapeLineColor(shapeProperties, themeColors) } : {}),
  ...(parseOoxmlShapeLineWidth(shapeProperties) != null ? { lineWidth: parseOoxmlShapeLineWidth(shapeProperties) } : {}),
  ...(parseOoxmlShapeFillPattern(shapeProperties) ? { fillPattern: parseOoxmlShapeFillPattern(shapeProperties) } : {}),
  ...(parseOoxmlShapePatternColor(shapeProperties, 'fgClr', themeColors) ? { fillForegroundColor: parseOoxmlShapePatternColor(shapeProperties, 'fgClr', themeColors) } : {}),
  ...(parseOoxmlShapePatternColor(shapeProperties, 'bgClr', themeColors) ? { fillBackgroundColor: parseOoxmlShapePatternColor(shapeProperties, 'bgClr', themeColors) } : {}),
})

const parseOoxmlChartStyleMetadata = (chartXml: string, themeColors: OoxmlThemeColors): {
  chartStyleId: number | null
  roundedCorners: boolean | null
  chartArea: OoxmlChartShapeMetadata
  plotArea: OoxmlChartShapeMetadata
} => {
  const chartTagIndex = chartXml.search(/<(?:[A-Za-z_][\w.-]*:)?chart\b/i)
  const chartSpacePrefix = chartTagIndex < 0 ? chartXml : chartXml.slice(0, chartTagIndex)
  const chartAreaShapeProperties = extractFirstQualifiedTagBlock(chartSpacePrefix, 'spPr') ?? ''
  const plotAreaBlock = extractFirstQualifiedTagBlock(chartXml, 'plotArea') ?? ''
  let plotAreaDirectChildScope = plotAreaBlock
  for (const localName of [...OOXML_CHART_LOCAL_NAMES, 'catAx', 'dateAx', 'valAx']) {
    for (const block of extractQualifiedTagBlocks(plotAreaDirectChildScope, localName)) {
      plotAreaDirectChildScope = plotAreaDirectChildScope.replace(block, '')
    }
  }
  const plotAreaShapeProperties = extractFirstQualifiedTagBlock(plotAreaDirectChildScope, 'spPr') ?? ''
  const styleAttrs = parseAttributes(extractFirstQualifiedTag(chartXml, 'style') ?? '')
  const styleId = Number(styleAttrs.val)
  const roundedCornersAttrs = parseAttributes(extractFirstQualifiedTag(chartXml, 'roundedCorners') ?? '')
  return {
    chartStyleId: Number.isFinite(styleId) ? styleId : null,
    roundedCorners: parseOoxmlBooleanVal(roundedCornersAttrs.val) ?? null,
    chartArea: parseOoxmlChartShapeMetadata(chartAreaShapeProperties, themeColors),
    plotArea: parseOoxmlChartShapeMetadata(plotAreaShapeProperties, themeColors),
  }
}

type OoxmlChartPlotSemanticsMetadata = {
  chartGrouping?: 'standard' | 'clustered' | 'stacked' | 'percentStacked'
  chartVaryColors?: boolean
  barGapWidth?: number
  barOverlap?: number
  lineSmooth?: boolean
  pieFirstSliceAngle?: number
  doughnutHoleSize?: number
}

const parseOoxmlChartGrouping = (value: unknown): OoxmlChartPlotSemanticsMetadata['chartGrouping'] => {
  switch (String(value ?? '').trim()) {
    case 'standard':
      return 'standard'
    case 'clustered':
      return 'clustered'
    case 'stacked':
      return 'stacked'
    case 'percentStacked':
      return 'percentStacked'
    default:
      return undefined
  }
}

const parseOoxmlChartBoundedNumber = (
  chartBlock: string,
  localName: string,
  min: number,
  max: number,
): number | undefined => {
  const attrs = parseAttributes(extractFirstQualifiedTag(chartBlock, localName) ?? '')
  const value = Number(attrs.val)
  if (!Number.isFinite(value)) return undefined
  return Math.min(max, Math.max(min, value))
}

const parseOoxmlChartPlotSemanticsMetadata = (
  chartXml: string,
  chartLocalNames: string[],
): OoxmlChartPlotSemanticsMetadata => {
  const primaryChartLocalName = chartLocalNames.find((localName) => parseOoxmlChartTypeForLocalName(chartXml, localName) != null)
  if (primaryChartLocalName == null) return {}
  const chartBlock = extractFirstQualifiedTagBlock(chartXml, primaryChartLocalName) ?? ''
  const groupingAttrs = parseAttributes(extractFirstQualifiedTag(chartBlock, 'grouping') ?? '')
  const varyColorsAttrs = parseAttributes(extractFirstQualifiedTag(chartBlock, 'varyColors') ?? '')
  const smoothAttrs = parseAttributes(extractFirstQualifiedTag(chartBlock, 'smooth') ?? '')
  return {
    ...(parseOoxmlChartGrouping(groupingAttrs.val) ? { chartGrouping: parseOoxmlChartGrouping(groupingAttrs.val) } : {}),
    ...(parseOoxmlBooleanVal(varyColorsAttrs.val) == null ? {} : { chartVaryColors: parseOoxmlBooleanVal(varyColorsAttrs.val) }),
    ...(primaryChartLocalName === 'barChart' && parseOoxmlChartBoundedNumber(chartBlock, 'gapWidth', 0, 500) != null
      ? { barGapWidth: parseOoxmlChartBoundedNumber(chartBlock, 'gapWidth', 0, 500) }
      : {}),
    ...(primaryChartLocalName === 'barChart' && parseOoxmlChartBoundedNumber(chartBlock, 'overlap', -100, 100) != null
      ? { barOverlap: parseOoxmlChartBoundedNumber(chartBlock, 'overlap', -100, 100) }
      : {}),
    ...(primaryChartLocalName === 'lineChart' && parseOoxmlBooleanVal(smoothAttrs.val) != null
      ? { lineSmooth: parseOoxmlBooleanVal(smoothAttrs.val) }
      : {}),
    ...((primaryChartLocalName === 'pieChart' || primaryChartLocalName === 'doughnutChart') && parseOoxmlChartBoundedNumber(chartBlock, 'firstSliceAng', 0, 360) != null
      ? { pieFirstSliceAngle: parseOoxmlChartBoundedNumber(chartBlock, 'firstSliceAng', 0, 360) }
      : {}),
    ...(primaryChartLocalName === 'doughnutChart' && parseOoxmlChartBoundedNumber(chartBlock, 'holeSize', 10, 90) != null
      ? { doughnutHoleSize: parseOoxmlChartBoundedNumber(chartBlock, 'holeSize', 10, 90) }
      : {}),
  }
}

type OoxmlAxisLocalName = 'catAx' | 'dateAx' | 'valAx'

const parseOoxmlCategoryAxisLocalName = (chartXml: string): 'catAx' | 'dateAx' => (
  /<(?:[A-Za-z_][\w.-]*:)?dateAx\b/i.test(chartXml) ? 'dateAx' : 'catAx'
)

const parseOoxmlAxisTitle = (chartXml: string, axisLocalName: OoxmlAxisLocalName): string => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  const titleBlock = extractFirstQualifiedTagBlock(axisBlock, 'title') ?? ''
  return parseOoxmlRichTextTitle(titleBlock)
}

const parseOoxmlAxisTitleTextStyle = (
  chartXml: string,
  axisLocalName: OoxmlAxisLocalName,
  themeColors: OoxmlThemeColors,
  prefix: 'categoryAxisTitle' | 'valueAxisTitle',
): Record<string, string | number | boolean> => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  const titleBlock = extractFirstQualifiedTagBlock(axisBlock, 'title') ?? ''
  return prefixedOoxmlChartTextStyle(parseOoxmlChartTextStyle(titleBlock, themeColors), prefix)
}

const parseOoxmlAxisTitleFromBlock = (axisBlock: string): string => {
  const titleBlock = extractFirstQualifiedTagBlock(axisBlock, 'title') ?? ''
  return parseOoxmlRichTextTitle(titleBlock)
}

const parseOoxmlAxisTitleTextStyleFromBlock = (
  axisBlock: string,
  themeColors: OoxmlThemeColors,
  prefix: 'secondaryValueAxisTitle',
): Record<string, string | number | boolean> => {
  const titleBlock = extractFirstQualifiedTagBlock(axisBlock, 'title') ?? ''
  return prefixedOoxmlChartTextStyle(parseOoxmlChartTextStyle(titleBlock, themeColors), prefix)
}

const parseOoxmlAxisIdFromBlock = (axisBlock: string): string | null => {
  const attrs = parseAttributes(extractFirstQualifiedTag(axisBlock, 'axId') ?? '')
  const id = String(attrs.val ?? '').trim()
  return id || null
}

const parseOoxmlAxisPosition = (chartXml: string, axisLocalName: OoxmlAxisLocalName): 'bottom' | 'top' | 'left' | 'right' | null => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisPositionFromBlock(axisBlock)
}

const parseOoxmlAxisPositionFromBlock = (axisBlock: string): 'bottom' | 'top' | 'left' | 'right' | null => {
  const attrs = parseAttributes(extractFirstQualifiedTag(axisBlock, 'axPos') ?? '')
  switch (String(attrs.val ?? '').trim()) {
    case 'b':
      return 'bottom'
    case 't':
      return 'top'
    case 'l':
      return 'left'
    case 'r':
      return 'right'
    default:
      return null
  }
}

const parseOoxmlAxisCrossing = (chartXml: string, axisLocalName: OoxmlAxisLocalName): {
  crosses: 'autoZero' | 'min' | 'max' | null
  crossesAt: number | null
} => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisCrossingFromBlock(axisBlock)
}

const parseOoxmlAxisCrossingFromBlock = (axisBlock: string): {
  crosses: 'autoZero' | 'min' | 'max' | null
  crossesAt: number | null
} => {
  const crossesAttrs = parseAttributes(extractFirstQualifiedTag(axisBlock, 'crosses') ?? '')
  const crossesAtAttrs = parseAttributes(extractFirstQualifiedTag(axisBlock, 'crossesAt') ?? '')
  const crossesAt = Number(crossesAtAttrs.val)
  const crosses = (() => {
    switch (String(crossesAttrs.val ?? '').trim()) {
      case 'autoZero':
        return 'autoZero'
      case 'min':
        return 'min'
      case 'max':
        return 'max'
      default:
        return null
    }
  })()
  return {
    crosses,
    crossesAt: Number.isFinite(crossesAt) ? crossesAt : null,
  }
}

const parseOoxmlAxisTickLabelPosition = (chartXml: string, axisLocalName: OoxmlAxisLocalName): 'nextTo' | 'high' | 'low' | 'none' | null => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisTickLabelPositionFromBlock(axisBlock)
}

const parseOoxmlAxisTickLabelPositionFromBlock = (axisBlock: string): 'nextTo' | 'high' | 'low' | 'none' | null => {
  const attrs = parseAttributes(extractFirstQualifiedTag(axisBlock, 'tickLblPos') ?? '')
  switch (String(attrs.val ?? '').trim()) {
    case 'nextTo':
      return 'nextTo'
    case 'high':
      return 'high'
    case 'low':
      return 'low'
    case 'none':
      return 'none'
    default:
      return null
  }
}

const parseOoxmlAxisTickMark = (
  chartXml: string,
  axisLocalName: OoxmlAxisLocalName,
  tickMarkLocalName: 'majorTickMark' | 'minorTickMark',
): 'none' | 'inside' | 'outside' | 'cross' | null => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisTickMarkFromBlock(axisBlock, tickMarkLocalName)
}

const parseOoxmlAxisTickMarkFromBlock = (
  axisBlock: string,
  tickMarkLocalName: 'majorTickMark' | 'minorTickMark',
): 'none' | 'inside' | 'outside' | 'cross' | null => {
  const attrs = parseAttributes(extractFirstQualifiedTag(axisBlock, tickMarkLocalName) ?? '')
  switch (String(attrs.val ?? '').trim()) {
    case 'none':
      return 'none'
    case 'in':
      return 'inside'
    case 'out':
      return 'outside'
    case 'cross':
      return 'cross'
    default:
      return null
  }
}

const parseOoxmlAxisNumberFormatFromBlock = (axisBlock: string): {
  numberFormat: string | null
  numberFormatLinkedToSource: boolean | null
} => {
  const attrs = parseAttributes(extractFirstQualifiedTag(axisBlock, 'numFmt') ?? '')
  return {
    numberFormat: typeof attrs.formatCode === 'string' && attrs.formatCode.trim() ? attrs.formatCode.trim() : null,
    numberFormatLinkedToSource: parseOoxmlBooleanVal(attrs.sourceLinked) ?? null,
  }
}

const parseOoxmlAxisNumberFormat = (chartXml: string, axisLocalName: OoxmlAxisLocalName): {
  numberFormat: string | null
  numberFormatLinkedToSource: boolean | null
} => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisNumberFormatFromBlock(axisBlock)
}

const parseOoxmlAxisGridlinesFromBlock = (axisBlock: string): {
  hasMajorGridlines: boolean
  hasMinorGridlines: boolean
} => ({
  hasMajorGridlines: /<(?:[A-Za-z_][\w.-]*:)?majorGridlines\b/i.test(axisBlock),
  hasMinorGridlines: /<(?:[A-Za-z_][\w.-]*:)?minorGridlines\b/i.test(axisBlock),
})

const parseOoxmlAxisGridlines = (chartXml: string, axisLocalName: OoxmlAxisLocalName): {
  hasMajorGridlines: boolean
  hasMinorGridlines: boolean
} => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisGridlinesFromBlock(axisBlock)
}

const parseOoxmlAxisDeletedFromBlock = (axisBlock: string): boolean | null => {
  const tag = extractFirstQualifiedTag(axisBlock, 'delete')
  if (!tag) return null
  return parseOoxmlBooleanVal(parseAttributes(tag).val) ?? true
}

const parseOoxmlAxisDeleted = (chartXml: string, axisLocalName: OoxmlAxisLocalName): boolean | null => {
  const axisBlock = extractFirstQualifiedTagBlock(chartXml, axisLocalName) ?? ''
  return parseOoxmlAxisDeletedFromBlock(axisBlock)
}

const parseOoxmlDateAxisTimeUnit = (value: unknown): 'days' | 'months' | 'years' | null => {
  switch (String(value ?? '').trim()) {
    case 'days':
      return 'days'
    case 'months':
      return 'months'
    case 'years':
      return 'years'
    default:
      return null
  }
}

const parseOoxmlCategoryAxisDateSettings = (chartXml: string): {
  categoryAxisKind: 'category' | 'date' | null
  categoryAxisBaseTimeUnit: 'days' | 'months' | 'years' | null
  categoryAxisMajorTimeUnit: 'days' | 'months' | 'years' | null
  categoryAxisMinorTimeUnit: 'days' | 'months' | 'years' | null
  categoryAxisMajorUnit: number | null
  categoryAxisMinorUnit: number | null
} => {
  const dateAxisBlock = extractFirstQualifiedTagBlock(chartXml, 'dateAx') ?? ''
  if (!dateAxisBlock) {
    return {
      categoryAxisKind: /<(?:[A-Za-z_][\w.-]*:)?catAx\b/i.test(chartXml) ? 'category' : null,
      categoryAxisBaseTimeUnit: null,
      categoryAxisMajorTimeUnit: null,
      categoryAxisMinorTimeUnit: null,
      categoryAxisMajorUnit: null,
      categoryAxisMinorUnit: null,
    }
  }
  const baseTimeUnitAttrs = parseAttributes(extractFirstQualifiedTag(dateAxisBlock, 'baseTimeUnit') ?? '')
  const majorTimeUnitAttrs = parseAttributes(extractFirstQualifiedTag(dateAxisBlock, 'majorTimeUnit') ?? '')
  const minorTimeUnitAttrs = parseAttributes(extractFirstQualifiedTag(dateAxisBlock, 'minorTimeUnit') ?? '')
  const majorUnitAttrs = parseAttributes(extractFirstQualifiedTag(dateAxisBlock, 'majorUnit') ?? '')
  const minorUnitAttrs = parseAttributes(extractFirstQualifiedTag(dateAxisBlock, 'minorUnit') ?? '')
  const majorUnit = Number(majorUnitAttrs.val)
  const minorUnit = Number(minorUnitAttrs.val)
  return {
    categoryAxisKind: 'date',
    categoryAxisBaseTimeUnit: parseOoxmlDateAxisTimeUnit(baseTimeUnitAttrs.val),
    categoryAxisMajorTimeUnit: parseOoxmlDateAxisTimeUnit(majorTimeUnitAttrs.val),
    categoryAxisMinorTimeUnit: parseOoxmlDateAxisTimeUnit(minorTimeUnitAttrs.val),
    categoryAxisMajorUnit: Number.isFinite(majorUnit) && majorUnit > 0 ? majorUnit : null,
    categoryAxisMinorUnit: Number.isFinite(minorUnit) && minorUnit > 0 ? minorUnit : null,
  }
}

const parseOoxmlValueAxisDisplayUnit = (chartXml: string): {
  valueAxisDisplayUnit: 'hundreds' | 'thousands' | 'tenThousands' | 'hundredThousands' | 'millions' | 'tenMillions' | 'hundredMillions' | 'billions' | 'trillions' | 'custom' | null
  valueAxisDisplayUnitCustom: number | null
} => {
  const valueAxisBlock = extractFirstQualifiedTagBlock(chartXml, 'valAx') ?? ''
  return parseOoxmlValueAxisDisplayUnitFromBlock(valueAxisBlock)
}

const parseOoxmlValueAxisDisplayUnitFromBlock = (valueAxisBlock: string): {
  valueAxisDisplayUnit: 'hundreds' | 'thousands' | 'tenThousands' | 'hundredThousands' | 'millions' | 'tenMillions' | 'hundredMillions' | 'billions' | 'trillions' | 'custom' | null
  valueAxisDisplayUnitCustom: number | null
} => {
  const displayUnitsBlock = extractFirstQualifiedTagBlock(valueAxisBlock, 'dispUnits') ?? ''
  const builtInAttrs = parseAttributes(extractFirstQualifiedTag(displayUnitsBlock, 'builtInUnit') ?? '')
  const customAttrs = parseAttributes(extractFirstQualifiedTag(displayUnitsBlock, 'custUnit') ?? '')
  const customUnit = Number(customAttrs.val)
  const builtInUnit = (() => {
    switch (String(builtInAttrs.val ?? '').trim()) {
      case 'hundreds':
        return 'hundreds'
      case 'thousands':
        return 'thousands'
      case 'tenThousands':
        return 'tenThousands'
      case 'hundredThousands':
        return 'hundredThousands'
      case 'millions':
        return 'millions'
      case 'tenMillions':
        return 'tenMillions'
      case 'hundredMillions':
        return 'hundredMillions'
      case 'billions':
        return 'billions'
      case 'trillions':
        return 'trillions'
      default:
        return null
    }
  })()
  if (Number.isFinite(customUnit) && customUnit > 0) {
    return {
      valueAxisDisplayUnit: 'custom',
      valueAxisDisplayUnitCustom: customUnit,
    }
  }
  return {
    valueAxisDisplayUnit: builtInUnit,
    valueAxisDisplayUnitCustom: null,
  }
}

const parseOoxmlLegendPosition = (chartXml: string): 'top' | 'bottom' | 'left' | 'right' => {
  const legendBlock = extractFirstQualifiedTagBlock(chartXml, 'legend') ?? ''
  const attrs = parseAttributes(extractFirstQualifiedTag(legendBlock, 'legendPos') ?? '')
  switch (String(attrs.val ?? '').trim()) {
    case 'b':
      return 'bottom'
    case 'l':
      return 'left'
    case 'r':
    case 'tr':
      return 'right'
    case 't':
    default:
      return 'top'
  }
}

const parseOoxmlValueAxisScale = (chartXml: string): {
  valueAxisMin: number | null
  valueAxisMax: number | null
  valueAxisLogBase: number | null
  valueAxisCrosses: 'autoZero' | 'min' | 'max' | null
  valueAxisCrossesAt: number | null
} => {
  const valueAxisBlock = extractFirstQualifiedTagBlock(chartXml, 'valAx') ?? ''
  const crossing = parseOoxmlAxisCrossingFromBlock(valueAxisBlock)
  return parseOoxmlValueAxisScaleFromBlock(valueAxisBlock, crossing)
}

const parseOoxmlValueAxisScaleFromBlock = (
  valueAxisBlock: string,
  crossing = parseOoxmlAxisCrossingFromBlock(valueAxisBlock),
): {
  valueAxisMin: number | null
  valueAxisMax: number | null
  valueAxisLogBase: number | null
  valueAxisCrosses: 'autoZero' | 'min' | 'max' | null
  valueAxisCrossesAt: number | null
} => {
  const scalingBlock = extractFirstQualifiedTagBlock(valueAxisBlock, 'scaling') ?? ''
  const minAttrs = parseAttributes(extractFirstQualifiedTag(scalingBlock, 'min') ?? '')
  const maxAttrs = parseAttributes(extractFirstQualifiedTag(scalingBlock, 'max') ?? '')
  const logBaseAttrs = parseAttributes(extractFirstQualifiedTag(scalingBlock, 'logBase') ?? '')
  const min = Number(minAttrs.val)
  const max = Number(maxAttrs.val)
  const logBase = Number(logBaseAttrs.val)
  return {
    valueAxisMin: Number.isFinite(min) ? min : null,
    valueAxisMax: Number.isFinite(max) ? max : null,
    valueAxisLogBase: Number.isFinite(logBase) && logBase > 1 ? logBase : null,
    valueAxisCrosses: crossing.crosses,
    valueAxisCrossesAt: crossing.crossesAt,
  }
}

const parseOoxmlSecondaryValueAxisMetadata = (chartXml: string, themeColors: OoxmlThemeColors): {
  valueAxisCount: number
  hasSecondaryValueAxis: boolean
  secondaryValueAxisId: string | null
  secondaryValueAxisPosition: 'bottom' | 'top' | 'left' | 'right' | null
  secondaryValueAxisTitle: string
  secondaryValueAxisTitleFontColor?: string
  secondaryValueAxisTitleFontSize?: number
  secondaryValueAxisTitleFontBold?: boolean
  secondaryValueAxisTitleFontItalic?: boolean
  secondaryValueAxisTitleTextRotation?: number
  secondaryValueAxisTitleTextAlign?: 'left' | 'center' | 'right'
  secondaryValueAxisTitleTextVerticalAlign?: 'top' | 'middle' | 'bottom'
  secondaryValueAxisTitleTextWrap?: boolean
  secondaryValueAxisTitleTextAutoFit?: 'normal' | 'shape' | 'none'
  secondaryValueAxisTitleTextAutoFitFontScale?: number
  secondaryValueAxisTitleTextHorizontalOverflow?: 'clip' | 'overflow'
  secondaryValueAxisTitleTextVerticalOverflow?: 'clip' | 'ellipsis' | 'overflow'
  secondaryValueAxisTitleTextInsetTop?: number
  secondaryValueAxisTitleTextInsetRight?: number
  secondaryValueAxisTitleTextInsetBottom?: number
  secondaryValueAxisTitleTextInsetLeft?: number
  secondaryValueAxisMin: number | null
  secondaryValueAxisMax: number | null
  secondaryValueAxisLogBase: number | null
  secondaryValueAxisDisplayUnit: 'hundreds' | 'thousands' | 'tenThousands' | 'hundredThousands' | 'millions' | 'tenMillions' | 'hundredMillions' | 'billions' | 'trillions' | 'custom' | null
  secondaryValueAxisDisplayUnitCustom: number | null
  secondaryValueAxisCrosses: 'autoZero' | 'min' | 'max' | null
  secondaryValueAxisCrossesAt: number | null
  secondaryValueAxisTickLabelPosition: 'nextTo' | 'high' | 'low' | 'none' | null
  secondaryValueAxisMajorTickMark: 'none' | 'inside' | 'outside' | 'cross' | null
  secondaryValueAxisMinorTickMark: 'none' | 'inside' | 'outside' | 'cross' | null
  secondaryValueAxisNumberFormat: string | null
  secondaryValueAxisNumberFormatLinkedToSource: boolean | null
  secondaryValueAxisHasMajorGridlines: boolean
  secondaryValueAxisHasMinorGridlines: boolean
  secondaryValueAxisDeleted: boolean | null
} => {
  const valueAxisBlocks = extractQualifiedTagBlocks(chartXml, 'valAx')
  const secondaryAxisBlock = valueAxisBlocks[1] ?? ''
  const secondaryScale = parseOoxmlValueAxisScaleFromBlock(secondaryAxisBlock)
  const secondaryDisplayUnit = parseOoxmlValueAxisDisplayUnitFromBlock(secondaryAxisBlock)
  const secondaryNumberFormat = parseOoxmlAxisNumberFormatFromBlock(secondaryAxisBlock)
  const secondaryGridlines = parseOoxmlAxisGridlinesFromBlock(secondaryAxisBlock)
  const secondaryTitleStyle = secondaryAxisBlock ? parseOoxmlAxisTitleTextStyleFromBlock(secondaryAxisBlock, themeColors, 'secondaryValueAxisTitle') : {}
  return {
    valueAxisCount: valueAxisBlocks.length,
    hasSecondaryValueAxis: valueAxisBlocks.length > 1,
    secondaryValueAxisId: secondaryAxisBlock ? parseOoxmlAxisIdFromBlock(secondaryAxisBlock) : null,
    secondaryValueAxisPosition: secondaryAxisBlock ? parseOoxmlAxisPositionFromBlock(secondaryAxisBlock) : null,
    secondaryValueAxisTitle: secondaryAxisBlock ? parseOoxmlAxisTitleFromBlock(secondaryAxisBlock) : '',
    ...secondaryTitleStyle,
    secondaryValueAxisMin: secondaryScale.valueAxisMin,
    secondaryValueAxisMax: secondaryScale.valueAxisMax,
    secondaryValueAxisLogBase: secondaryScale.valueAxisLogBase,
    secondaryValueAxisDisplayUnit: secondaryDisplayUnit.valueAxisDisplayUnit,
    secondaryValueAxisDisplayUnitCustom: secondaryDisplayUnit.valueAxisDisplayUnitCustom,
    secondaryValueAxisCrosses: secondaryScale.valueAxisCrosses,
    secondaryValueAxisCrossesAt: secondaryScale.valueAxisCrossesAt,
    secondaryValueAxisTickLabelPosition: secondaryAxisBlock ? parseOoxmlAxisTickLabelPositionFromBlock(secondaryAxisBlock) : null,
    secondaryValueAxisMajorTickMark: secondaryAxisBlock ? parseOoxmlAxisTickMarkFromBlock(secondaryAxisBlock, 'majorTickMark') : null,
    secondaryValueAxisMinorTickMark: secondaryAxisBlock ? parseOoxmlAxisTickMarkFromBlock(secondaryAxisBlock, 'minorTickMark') : null,
    secondaryValueAxisNumberFormat: secondaryNumberFormat.numberFormat,
    secondaryValueAxisNumberFormatLinkedToSource: secondaryNumberFormat.numberFormatLinkedToSource,
    secondaryValueAxisHasMajorGridlines: secondaryGridlines.hasMajorGridlines,
    secondaryValueAxisHasMinorGridlines: secondaryGridlines.hasMinorGridlines,
    secondaryValueAxisDeleted: secondaryAxisBlock ? parseOoxmlAxisDeletedFromBlock(secondaryAxisBlock) : null,
  }
}

type OoxmlChartSeriesReference = {
  index: number
  order: number
  chartType?: SpreadsheetChartArtifactType
  valueAxisId?: string
  name?: string
  color?: string
  lineColor?: string
  lineWidth?: number
  markerSize?: number
  markerShape?: 'circle' | 'square' | 'diamond' | 'triangle' | 'x'
  markerFillColor?: string
  markerLineColor?: string
  lineDash?: 'solid' | 'dash' | 'dot' | 'dashDot'
  fillOpacity?: number
  fillPattern?: 'percent' | 'horizontal' | 'vertical' | 'diagonalDown' | 'diagonalUp' | 'cross' | 'diagonalCross'
  fillForegroundColor?: string
  fillBackgroundColor?: string
  trendlineType?: 'linear' | 'exponential' | 'logarithmic' | 'movingAverage' | 'polynomial' | 'power'
  trendlineOrder?: number
  trendlinePeriod?: number
  trendlineShowEquation?: boolean
  trendlineShowRSquared?: boolean
  errorBarDirection?: 'x' | 'y'
  errorBarType?: 'both' | 'minus' | 'plus'
  errorBarValueType?: 'fixedValue' | 'percentage' | 'standardDeviation' | 'standardError' | 'custom'
  errorBarValue?: number
  errorBarNoEndCap?: boolean
  dataLabelPosition?: OoxmlChartDataLabelMetadata['dataLabelPosition']
  dataLabelNumberFormat?: string
  dataLabelNumberFormatLinkedToSource?: boolean
  dataLabelSeparator?: string
  dataLabelFontColor?: string
  dataLabelFontSize?: number
  dataLabelFontBold?: boolean
  dataLabelFontItalic?: boolean
  dataLabelTextRotation?: number
  dataLabelTextAlign?: 'left' | 'center' | 'right'
  dataLabelTextVerticalAlign?: 'top' | 'middle' | 'bottom'
  dataLabelTextWrap?: boolean
  dataLabelTextAutoFit?: 'normal' | 'shape' | 'none'
  dataLabelTextAutoFitFontScale?: number
  dataLabelTextHorizontalOverflow?: 'clip' | 'overflow'
  dataLabelTextVerticalOverflow?: 'clip' | 'ellipsis' | 'overflow'
  dataLabelTextInsetTop?: number
  dataLabelTextInsetRight?: number
  dataLabelTextInsetBottom?: number
  dataLabelTextInsetLeft?: number
  dataLabelShowLegendKey?: boolean
  dataLabelShowValue?: boolean
  dataLabelShowCategoryName?: boolean
  dataLabelShowSeriesName?: boolean
  dataLabelShowPercent?: boolean
  dataLabelShowBubbleSize?: boolean
  dataLabelShowLeaderLines?: boolean
  explosion?: number
  pointIndexes?: number[]
  pointColors?: string[]
  pointLineColors?: string[]
  pointFillOpacities?: Array<number | ''>
  pointFillPatterns?: string[]
  pointFillForegroundColors?: string[]
  pointFillBackgroundColors?: string[]
  pointExplosions?: Array<number | ''>
  pointStyles?: OoxmlChartSeriesPointMetadata[]
  nameFormula?: string
  categoryFormula?: string
  valueFormula?: string
  xFormula?: string
  yFormula?: string
  nameRange?: SpreadsheetRangeAddress
  categoryRange?: SpreadsheetRangeAddress
  valueRange?: SpreadsheetRangeAddress
  xRange?: SpreadsheetRangeAddress
  yRange?: SpreadsheetRangeAddress
}

const parseOoxmlChartSeriesValueAxisIds = (chartXml: string): string[] => {
  const axisIds: string[] = []
  for (const localName of OOXML_CHART_LOCAL_NAMES) {
    for (const chartBlock of extractQualifiedTagBlocks(chartXml, localName)) {
      const chartAxisIds = Array.from(chartBlock.matchAll(/<(?:[A-Za-z_][\w.-]*:)?axId\b[^>]*>/gi))
        .map((match) => String(parseAttributes(match[0] ?? '').val ?? '').trim())
        .filter(Boolean)
      const valueAxisId = chartAxisIds.length >= 2 ? chartAxisIds[1] ?? '' : ''
      const seriesBlocks = extractQualifiedTagBlocks(chartBlock, 'ser')
      for (let index = 0; index < seriesBlocks.length; index += 1) {
        axisIds.push(valueAxisId)
      }
    }
  }
  return axisIds
}

const parseOoxmlChartSeriesChartTypes = (chartXml: string): SpreadsheetChartArtifactType[] => {
  const chartTypes: SpreadsheetChartArtifactType[] = []
  for (const localName of OOXML_CHART_LOCAL_NAMES) {
    for (const chartBlock of extractQualifiedTagBlocks(chartXml, localName)) {
      const chartType = parseOoxmlChartTypeForLocalName(chartBlock, localName)
      const seriesBlocks = extractQualifiedTagBlocks(chartBlock, 'ser')
      for (let index = 0; index < seriesBlocks.length; index += 1) {
        if (chartType) chartTypes.push(chartType)
      }
    }
  }
  return chartTypes
}

type OoxmlChartSeriesPointMetadata = {
  index: number
  color?: string
  lineColor?: string
  fillOpacity?: number
  fillPattern?: OoxmlChartSeriesReference['fillPattern']
  fillForegroundColor?: string
  fillBackgroundColor?: string
  explosion?: number
}

type OoxmlChartDataLabelMetadata = {
  dataLabelPosition?: 'bestFit' | 'center' | 'insideBase' | 'insideEnd' | 'outsideEnd' | 'left' | 'right' | 'top' | 'bottom'
  dataLabelNumberFormat?: string
  dataLabelNumberFormatLinkedToSource?: boolean
  dataLabelSeparator?: string
  dataLabelFontColor?: string
  dataLabelFontSize?: number
  dataLabelFontBold?: boolean
  dataLabelFontItalic?: boolean
  dataLabelTextRotation?: number
  dataLabelTextAlign?: 'left' | 'center' | 'right'
  dataLabelTextVerticalAlign?: 'top' | 'middle' | 'bottom'
  dataLabelTextWrap?: boolean
  dataLabelTextAutoFit?: 'normal' | 'shape' | 'none'
  dataLabelTextAutoFitFontScale?: number
  dataLabelTextHorizontalOverflow?: 'clip' | 'overflow'
  dataLabelTextVerticalOverflow?: 'clip' | 'ellipsis' | 'overflow'
  dataLabelTextInsetTop?: number
  dataLabelTextInsetRight?: number
  dataLabelTextInsetBottom?: number
  dataLabelTextInsetLeft?: number
  dataLabelShowLegendKey?: boolean
  dataLabelShowValue?: boolean
  dataLabelShowCategoryName?: boolean
  dataLabelShowSeriesName?: boolean
  dataLabelShowPercent?: boolean
  dataLabelShowBubbleSize?: boolean
  dataLabelShowLeaderLines?: boolean
}

const parseOoxmlSeriesFormula = (seriesBlock: string, localName: 'tx' | 'cat' | 'val' | 'xVal' | 'yVal'): string | undefined => {
  const block = extractFirstQualifiedTagBlock(seriesBlock, localName) ?? ''
  const formula = decodeXmlText(extractFirstQualifiedTagText(block, 'f') ?? '').trim()
  return formula || undefined
}

const parseOoxmlSeriesText = (seriesBlock: string, localName: 'tx'): string | undefined => {
  const block = extractFirstQualifiedTagBlock(seriesBlock, localName) ?? ''
  const text = Array.from(block.matchAll(/<(?:[A-Za-z_][\w.-]*:)?(?:v|t)\b[^>]*>([\s\S]*?)<\/(?:[A-Za-z_][\w.-]*:)?(?:v|t)>/gi))
    .map((match) => decodeXmlText(match[1] ?? '').trim())
    .find(Boolean)
  return text || undefined
}

const themeColorIndexForSchemeName = (schemeName: string): number | null => {
  const index = THEME_COLOR_ORDER.findIndex((name) => name === schemeName)
  return index >= 0 ? index : null
}

const applyDrawingMlColorTransforms = (rgb: string, colorBlock: string): string => {
  const channels = parseHexColor(rgb)
  if (channels == null) return rgb
  const transformed = [...channels] as [number, number, number]
  const lumMod = Number(parseAttributes(extractFirstQualifiedTag(colorBlock, 'lumMod') ?? '').val)
  const lumOff = Number(parseAttributes(extractFirstQualifiedTag(colorBlock, 'lumOff') ?? '').val)
  if (Number.isFinite(lumMod)) {
    const factor = lumMod / 100000
    transformed[0] *= factor
    transformed[1] *= factor
    transformed[2] *= factor
  }
  if (Number.isFinite(lumOff)) {
    const offset = 255 * (lumOff / 100000)
    transformed[0] += offset
    transformed[1] += offset
    transformed[2] += offset
  }
  const tint = Number(parseAttributes(extractFirstQualifiedTag(colorBlock, 'tint') ?? '').val)
  if (Number.isFinite(tint)) {
    const factor = tint / 100000
    transformed[0] += (255 - transformed[0]) * factor
    transformed[1] += (255 - transformed[1]) * factor
    transformed[2] += (255 - transformed[2]) * factor
  }
  const shade = Number(parseAttributes(extractFirstQualifiedTag(colorBlock, 'shade') ?? '').val)
  if (Number.isFinite(shade)) {
    const factor = shade / 100000
    transformed[0] *= factor
    transformed[1] *= factor
    transformed[2] *= factor
  }
  return formatHexColor(transformed)
}

const parseOoxmlDrawingColor = (colorBlock: string, themeColors: OoxmlThemeColors): string | undefined => {
  const srgbAttrs = parseAttributes(extractFirstQualifiedTag(colorBlock, 'srgbClr') ?? '')
  const srgb = normalizeRgbColor(srgbAttrs.val ?? '')
  if (srgb != null) return applyDrawingMlColorTransforms(srgb, colorBlock)

  const sysAttrs = parseAttributes(extractFirstQualifiedTag(colorBlock, 'sysClr') ?? '')
  const systemRgb = normalizeRgbColor(sysAttrs.lastClr ?? '')
  if (systemRgb != null) return applyDrawingMlColorTransforms(systemRgb, colorBlock)

  const schemeAttrs = parseAttributes(extractFirstQualifiedTag(colorBlock, 'schemeClr') ?? '')
  const schemeIndex = themeColorIndexForSchemeName(String(schemeAttrs.val ?? '').trim())
  const themeColor = schemeIndex == null
    ? undefined
    : themeColors.get(schemeIndex) ?? DEFAULT_OOXML_THEME_COLORS.get(schemeIndex)
  return themeColor == null ? undefined : applyDrawingMlColorTransforms(themeColor.rgb, colorBlock)
}

const parseOoxmlShapeFillColor = (shapeProperties: string, themeColors: OoxmlThemeColors): string | undefined => {
  const patternFill = extractFirstQualifiedTagBlock(shapeProperties, 'pattFill') ?? ''
  const foregroundColor = extractFirstQualifiedTagBlock(patternFill, 'fgClr') ?? ''
  if (foregroundColor) return parseOoxmlDrawingColor(foregroundColor, themeColors)
  const lineIndex = shapeProperties.search(/<(?:[A-Za-z_][\w.-]*:)?ln\b/i)
  const fillScope = lineIndex < 0 ? shapeProperties : shapeProperties.slice(0, lineIndex)
  const solidFill = extractFirstQualifiedTagBlock(fillScope, 'solidFill') ?? ''
  return parseOoxmlDrawingColor(solidFill, themeColors)
}

const parseOoxmlShapePatternColor = (
  shapeProperties: string,
  localName: 'fgClr' | 'bgClr',
  themeColors: OoxmlThemeColors,
): string | undefined => {
  const patternFill = extractFirstQualifiedTagBlock(shapeProperties, 'pattFill') ?? ''
  const colorBlock = extractFirstQualifiedTagBlock(patternFill, localName) ?? ''
  return parseOoxmlDrawingColor(colorBlock, themeColors)
}

const parseOoxmlShapeLineColor = (shapeProperties: string, themeColors: OoxmlThemeColors): string | undefined => {
  const lineBlock = extractFirstQualifiedTagBlock(shapeProperties, 'ln') ?? ''
  const solidFill = extractFirstQualifiedTagBlock(lineBlock, 'solidFill') ?? ''
  return parseOoxmlDrawingColor(solidFill, themeColors)
}

const parseOoxmlShapeLineWidth = (shapeProperties: string): number | undefined => {
  const lineAttrs = parseAttributes(extractFirstQualifiedTag(shapeProperties, 'ln') ?? '')
  const widthEmu = Number(lineAttrs.w)
  if (!Number.isFinite(widthEmu) || widthEmu <= 0) return undefined
  return Math.min(16, Math.max(0.5, Math.round((widthEmu / 12700) * 10) / 10))
}

const parseOoxmlShapeFillOpacity = (shapeProperties: string): number | undefined => {
  const lineIndex = shapeProperties.search(/<(?:[A-Za-z_][\w.-]*:)?ln\b/i)
  const fillScope = lineIndex < 0 ? shapeProperties : shapeProperties.slice(0, lineIndex)
  const solidFill = extractFirstQualifiedTagBlock(fillScope, 'solidFill') ?? ''
  const alphaAttrs = parseAttributes(extractFirstQualifiedTag(solidFill, 'alpha') ?? '')
  const alpha = Number(alphaAttrs.val)
  if (!Number.isFinite(alpha) || alpha < 0) return undefined
  return Math.min(1, Math.max(0, Math.round((alpha / 100000) * 1000) / 1000))
}

const parseOoxmlSeriesColor = (seriesBlock: string, themeColors: OoxmlThemeColors): string | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  return parseOoxmlShapeFillColor(shapeProperties, themeColors)
}

const parseOoxmlSeriesPatternColor = (seriesBlock: string, localName: 'fgClr' | 'bgClr', themeColors: OoxmlThemeColors): string | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  return parseOoxmlShapePatternColor(shapeProperties, localName, themeColors)
}

const parseOoxmlSeriesLineColor = (seriesBlock: string, themeColors: OoxmlThemeColors): string | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  return parseOoxmlShapeLineColor(shapeProperties, themeColors)
}

const parseOoxmlSeriesMarkerColor = (seriesBlock: string, localName: 'fill' | 'line', themeColors: OoxmlThemeColors): string | undefined => {
  const markerBlock = extractFirstQualifiedTagBlock(seriesBlock, 'marker') ?? ''
  const markerProperties = extractFirstQualifiedTagBlock(markerBlock, 'spPr') ?? ''
  const colorBlock = localName === 'fill'
    ? extractFirstQualifiedTagBlock(markerProperties, 'solidFill') ?? ''
    : extractFirstQualifiedTagBlock(extractFirstQualifiedTagBlock(markerProperties, 'ln') ?? '', 'solidFill') ?? ''
  return parseOoxmlDrawingColor(colorBlock, themeColors)
}

const parseOoxmlSeriesFillOpacity = (seriesBlock: string): number | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  const solidFill = extractFirstQualifiedTagBlock(shapeProperties, 'solidFill') ?? ''
  const alphaAttrs = parseAttributes(extractFirstQualifiedTag(solidFill, 'alpha') ?? '')
  const alpha = Number(alphaAttrs.val)
  if (!Number.isFinite(alpha) || alpha < 0) return undefined
  return Math.min(1, Math.max(0, Math.round((alpha / 100000) * 1000) / 1000))
}

const parseOoxmlSeriesLineWidth = (seriesBlock: string): number | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  return parseOoxmlShapeLineWidth(shapeProperties)
}

const parseOoxmlSeriesMarkerSize = (seriesBlock: string): number | undefined => {
  const markerBlock = extractFirstQualifiedTagBlock(seriesBlock, 'marker') ?? ''
  const sizeAttrs = parseAttributes(extractFirstQualifiedTag(markerBlock, 'size') ?? '')
  const size = Number(sizeAttrs.val)
  if (!Number.isFinite(size) || size <= 0) return undefined
  return Math.min(16, Math.max(2, Math.round(size * 10) / 10))
}

const parseOoxmlSeriesMarkerShape = (seriesBlock: string): OoxmlChartSeriesReference['markerShape'] | undefined => {
  const markerBlock = extractFirstQualifiedTagBlock(seriesBlock, 'marker') ?? ''
  const symbolAttrs = parseAttributes(extractFirstQualifiedTag(markerBlock, 'symbol') ?? '')
  switch (String(symbolAttrs.val ?? '').trim()) {
    case 'square':
      return 'square'
    case 'diamond':
      return 'diamond'
    case 'triangle':
      return 'triangle'
    case 'x':
      return 'x'
    case 'circle':
      return 'circle'
    default:
      return undefined
  }
}

const parseOoxmlSeriesLineDash = (seriesBlock: string): OoxmlChartSeriesReference['lineDash'] | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  const lineBlock = extractFirstQualifiedTagBlock(shapeProperties, 'ln') ?? ''
  const dashAttrs = parseAttributes(extractFirstQualifiedTag(lineBlock, 'prstDash') ?? '')
  switch (String(dashAttrs.val ?? '').trim()) {
    case 'dash':
    case 'sysDash':
    case 'lgDash':
      return 'dash'
    case 'dot':
    case 'sysDot':
      return 'dot'
    case 'dashDot':
    case 'sysDashDot':
    case 'lgDashDot':
      return 'dashDot'
    case 'solid':
      return 'solid'
    default:
      return undefined
  }
}

const parseOoxmlSeriesFillPattern = (seriesBlock: string): OoxmlChartSeriesReference['fillPattern'] | undefined => {
  const shapeProperties = extractFirstQualifiedTagBlock(seriesBlock, 'spPr') ?? ''
  const patternAttrs = parseAttributes(extractFirstQualifiedTag(shapeProperties, 'pattFill') ?? '')
  switch (String(patternAttrs.prst ?? '').trim()) {
    case 'pct5':
    case 'pct10':
    case 'pct20':
    case 'pct25':
    case 'pct30':
    case 'pct40':
    case 'pct50':
    case 'pct60':
    case 'pct70':
    case 'pct75':
    case 'pct80':
    case 'pct90':
      return 'percent'
    case 'ltHorz':
    case 'horz':
    case 'dkHorz':
      return 'horizontal'
    case 'ltVert':
    case 'vert':
    case 'dkVert':
      return 'vertical'
    case 'ltDnDiag':
    case 'dnDiag':
    case 'dkDnDiag':
      return 'diagonalDown'
    case 'ltUpDiag':
    case 'upDiag':
    case 'dkUpDiag':
      return 'diagonalUp'
    case 'smGrid':
    case 'lgGrid':
    case 'smCheck':
    case 'lgCheck':
      return 'cross'
    case 'diagCross':
      return 'diagonalCross'
    default:
      return undefined
  }
}

const parseOoxmlSeriesExplosion = (seriesBlock: string): number | undefined => {
  const attrs = parseAttributes(extractFirstQualifiedTag(seriesBlock, 'explosion') ?? '')
  const value = Number(attrs.val)
  if (!Number.isFinite(value) || value < 0) return undefined
  return Math.min(400, Math.round(value))
}

const parseOoxmlBooleanVal = (value: unknown): boolean | undefined => {
  const normalized = String(value ?? '').trim()
  if (normalized === '1' || normalized === 'true') return true
  if (normalized === '0' || normalized === 'false') return false
  return undefined
}

const parseOoxmlBooleanTag = (parentBlock: string, localName: string): boolean | undefined => {
  const tag = extractFirstQualifiedTag(parentBlock, localName)
  if (!tag) return undefined
  const attrs = parseAttributes(tag)
  return parseOoxmlBooleanVal(attrs.val) ?? true
}

const parseOoxmlSeriesPointMetadata = (
  seriesBlock: string,
  themeColors: OoxmlThemeColors,
): OoxmlChartSeriesPointMetadata[] => {
  return extractQualifiedTagBlocks(seriesBlock, 'dPt')
    .map((pointBlock): OoxmlChartSeriesPointMetadata | null => {
      const indexAttrs = parseAttributes(extractFirstQualifiedTag(pointBlock, 'idx') ?? '')
      const index = Number(indexAttrs.val)
      if (!Number.isInteger(index) || index < 0) return null
      const shapeProperties = extractFirstQualifiedTagBlock(pointBlock, 'spPr') ?? ''
      const explosion = parseOoxmlSeriesExplosion(pointBlock)
      return {
        index,
        ...(parseOoxmlShapeFillColor(shapeProperties, themeColors) ? { color: parseOoxmlShapeFillColor(shapeProperties, themeColors) } : {}),
        ...(parseOoxmlShapeLineColor(shapeProperties, themeColors) ? { lineColor: parseOoxmlShapeLineColor(shapeProperties, themeColors) } : {}),
        ...(parseOoxmlShapeFillOpacity(shapeProperties) != null ? { fillOpacity: parseOoxmlShapeFillOpacity(shapeProperties) } : {}),
        ...(parseOoxmlShapeFillPattern(shapeProperties) ? { fillPattern: parseOoxmlShapeFillPattern(shapeProperties) } : {}),
        ...(parseOoxmlShapePatternColor(shapeProperties, 'fgClr', themeColors) ? { fillForegroundColor: parseOoxmlShapePatternColor(shapeProperties, 'fgClr', themeColors) } : {}),
        ...(parseOoxmlShapePatternColor(shapeProperties, 'bgClr', themeColors) ? { fillBackgroundColor: parseOoxmlShapePatternColor(shapeProperties, 'bgClr', themeColors) } : {}),
        ...(explosion == null ? {} : { explosion }),
      }
    })
    .filter((point): point is OoxmlChartSeriesPointMetadata => point != null)
}

const parseOoxmlDataLabelPosition = (value: unknown): OoxmlChartDataLabelMetadata['dataLabelPosition'] => {
  switch (String(value ?? '').trim()) {
    case 'bestFit':
      return 'bestFit'
    case 'ctr':
      return 'center'
    case 'inBase':
      return 'insideBase'
    case 'inEnd':
      return 'insideEnd'
    case 'outEnd':
      return 'outsideEnd'
    case 'l':
      return 'left'
    case 'r':
      return 'right'
    case 't':
      return 'top'
    case 'b':
      return 'bottom'
    default:
      return undefined
  }
}

const parseOoxmlChartDataLabelTextStyle = (
  dataLabelsBlock: string,
  themeColors: OoxmlThemeColors,
): Pick<OoxmlChartDataLabelMetadata, 'dataLabelFontColor' | 'dataLabelFontSize' | 'dataLabelFontBold' | 'dataLabelFontItalic' | 'dataLabelTextRotation' | 'dataLabelTextAlign' | 'dataLabelTextVerticalAlign' | 'dataLabelTextWrap' | 'dataLabelTextAutoFit' | 'dataLabelTextAutoFitFontScale' | 'dataLabelTextHorizontalOverflow' | 'dataLabelTextVerticalOverflow' | 'dataLabelTextInsetTop' | 'dataLabelTextInsetRight' | 'dataLabelTextInsetBottom' | 'dataLabelTextInsetLeft'> => {
  return prefixedOoxmlChartTextStyle(parseOoxmlChartTextStyle(dataLabelsBlock, themeColors), 'dataLabel') as Pick<OoxmlChartDataLabelMetadata, 'dataLabelFontColor' | 'dataLabelFontSize' | 'dataLabelFontBold' | 'dataLabelFontItalic' | 'dataLabelTextRotation' | 'dataLabelTextAlign' | 'dataLabelTextVerticalAlign' | 'dataLabelTextWrap' | 'dataLabelTextAutoFit' | 'dataLabelTextAutoFitFontScale' | 'dataLabelTextHorizontalOverflow' | 'dataLabelTextVerticalOverflow' | 'dataLabelTextInsetTop' | 'dataLabelTextInsetRight' | 'dataLabelTextInsetBottom' | 'dataLabelTextInsetLeft'>
}

const parseOoxmlChartDataLabelMetadataFromBlock = (
  dataLabelsBlock: string,
  themeColors: OoxmlThemeColors,
): OoxmlChartDataLabelMetadata => {
  if (!dataLabelsBlock) return {}
  const positionAttrs = parseAttributes(extractFirstQualifiedTag(dataLabelsBlock, 'dLblPos') ?? '')
  const numberFormatAttrs = parseAttributes(extractFirstQualifiedTag(dataLabelsBlock, 'numFmt') ?? '')
  const separator = decodeXmlText(extractFirstQualifiedTagText(dataLabelsBlock, 'separator') ?? '').trim()
  const textStyle = parseOoxmlChartDataLabelTextStyle(dataLabelsBlock, themeColors)
  return {
    ...(parseOoxmlDataLabelPosition(positionAttrs.val) == null ? {} : { dataLabelPosition: parseOoxmlDataLabelPosition(positionAttrs.val) }),
    ...(typeof numberFormatAttrs.formatCode === 'string' && numberFormatAttrs.formatCode.trim() ? { dataLabelNumberFormat: numberFormatAttrs.formatCode.trim() } : {}),
    ...(parseOoxmlBooleanVal(numberFormatAttrs.sourceLinked) == null ? {} : { dataLabelNumberFormatLinkedToSource: parseOoxmlBooleanVal(numberFormatAttrs.sourceLinked) }),
    ...(separator ? { dataLabelSeparator: separator } : {}),
    ...textStyle,
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showLegendKey') == null ? {} : { dataLabelShowLegendKey: parseOoxmlBooleanTag(dataLabelsBlock, 'showLegendKey') }),
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showVal') == null ? {} : { dataLabelShowValue: parseOoxmlBooleanTag(dataLabelsBlock, 'showVal') }),
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showCatName') == null ? {} : { dataLabelShowCategoryName: parseOoxmlBooleanTag(dataLabelsBlock, 'showCatName') }),
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showSerName') == null ? {} : { dataLabelShowSeriesName: parseOoxmlBooleanTag(dataLabelsBlock, 'showSerName') }),
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showPercent') == null ? {} : { dataLabelShowPercent: parseOoxmlBooleanTag(dataLabelsBlock, 'showPercent') }),
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showBubbleSize') == null ? {} : { dataLabelShowBubbleSize: parseOoxmlBooleanTag(dataLabelsBlock, 'showBubbleSize') }),
    ...(parseOoxmlBooleanTag(dataLabelsBlock, 'showLeaderLines') == null ? {} : { dataLabelShowLeaderLines: parseOoxmlBooleanTag(dataLabelsBlock, 'showLeaderLines') }),
  }
}

const parseOoxmlChartDataLabelMetadata = (
  chartXml: string,
  themeColors: OoxmlThemeColors,
): OoxmlChartDataLabelMetadata => (
  parseOoxmlChartDataLabelMetadataFromBlock(extractFirstQualifiedTagBlock(chartXml, 'dLbls') ?? '', themeColors)
)

const parseOoxmlSeriesDataLabelMetadata = (
  seriesBlock: string,
  themeColors: OoxmlThemeColors,
): OoxmlChartDataLabelMetadata => (
  parseOoxmlChartDataLabelMetadataFromBlock(extractFirstQualifiedTagBlock(seriesBlock, 'dLbls') ?? '', themeColors)
)

const parseOoxmlSeriesTrendlineMetadata = (seriesBlock: string): Pick<OoxmlChartSeriesReference,
  'trendlineType'
  | 'trendlineOrder'
  | 'trendlinePeriod'
  | 'trendlineShowEquation'
  | 'trendlineShowRSquared'
> => {
  const trendlineBlock = extractFirstQualifiedTagBlock(seriesBlock, 'trendline') ?? ''
  if (!trendlineBlock) return {}
  const trendlineTypeAttrs = parseAttributes(extractFirstQualifiedTag(trendlineBlock, 'trendlineType') ?? '')
  const orderAttrs = parseAttributes(extractFirstQualifiedTag(trendlineBlock, 'order') ?? '')
  const periodAttrs = parseAttributes(extractFirstQualifiedTag(trendlineBlock, 'period') ?? '')
  const equationAttrs = parseAttributes(extractFirstQualifiedTag(trendlineBlock, 'dispEq') ?? '')
  const rSquaredAttrs = parseAttributes(extractFirstQualifiedTag(trendlineBlock, 'dispRSqr') ?? '')
  const trendlineType = (() => {
    switch (String(trendlineTypeAttrs.val ?? '').trim()) {
      case 'exp':
        return 'exponential'
      case 'log':
        return 'logarithmic'
      case 'movingAvg':
        return 'movingAverage'
      case 'poly':
        return 'polynomial'
      case 'power':
        return 'power'
      case 'linear':
        return 'linear'
      default:
        return undefined
    }
  })()
  const order = Number(orderAttrs.val)
  const period = Number(periodAttrs.val)
  return {
    ...(trendlineType == null ? {} : { trendlineType }),
    ...(Number.isFinite(order) && order > 0 ? { trendlineOrder: order } : {}),
    ...(Number.isFinite(period) && period > 0 ? { trendlinePeriod: period } : {}),
    ...(parseOoxmlBooleanVal(equationAttrs.val) == null ? {} : { trendlineShowEquation: parseOoxmlBooleanVal(equationAttrs.val) }),
    ...(parseOoxmlBooleanVal(rSquaredAttrs.val) == null ? {} : { trendlineShowRSquared: parseOoxmlBooleanVal(rSquaredAttrs.val) }),
  }
}

const parseOoxmlSeriesErrorBarMetadata = (seriesBlock: string): Pick<OoxmlChartSeriesReference,
  'errorBarDirection'
  | 'errorBarType'
  | 'errorBarValueType'
  | 'errorBarValue'
  | 'errorBarNoEndCap'
> => {
  const errorBarsBlock = extractFirstQualifiedTagBlock(seriesBlock, 'errBars') ?? ''
  if (!errorBarsBlock) return {}
  const directionAttrs = parseAttributes(extractFirstQualifiedTag(errorBarsBlock, 'errDir') ?? '')
  const typeAttrs = parseAttributes(extractFirstQualifiedTag(errorBarsBlock, 'errBarType') ?? '')
  const valueTypeAttrs = parseAttributes(extractFirstQualifiedTag(errorBarsBlock, 'errValType') ?? '')
  const valueAttrs = parseAttributes(extractFirstQualifiedTag(errorBarsBlock, 'val') ?? '')
  const noEndCapAttrs = parseAttributes(extractFirstQualifiedTag(errorBarsBlock, 'noEndCap') ?? '')
  const direction = (() => {
    switch (String(directionAttrs.val ?? '').trim()) {
      case 'x':
        return 'x'
      case 'y':
        return 'y'
      default:
        return undefined
    }
  })()
  const errorBarType = (() => {
    switch (String(typeAttrs.val ?? '').trim()) {
      case 'both':
      case 'minus':
      case 'plus':
        return typeAttrs.val as 'both' | 'minus' | 'plus'
      default:
        return undefined
    }
  })()
  const valueType = (() => {
    switch (String(valueTypeAttrs.val ?? '').trim()) {
      case 'cust':
        return 'custom'
      case 'fixedVal':
        return 'fixedValue'
      case 'percentage':
        return 'percentage'
      case 'stdDev':
        return 'standardDeviation'
      case 'stdErr':
        return 'standardError'
      default:
        return undefined
    }
  })()
  const value = Number(valueAttrs.val)
  return {
    ...(direction == null ? {} : { errorBarDirection: direction }),
    ...(errorBarType == null ? {} : { errorBarType }),
    ...(valueType == null ? {} : { errorBarValueType: valueType }),
    ...(Number.isFinite(value) ? { errorBarValue: value } : {}),
    ...(parseOoxmlBooleanVal(noEndCapAttrs.val) == null ? {} : { errorBarNoEndCap: parseOoxmlBooleanVal(noEndCapAttrs.val) }),
  }
}

const parseOoxmlChartSeriesReferences = (
  chartXml: string,
  fallbackSheetId: string,
  sheetIdByName: Map<string, string>,
  themeColors: OoxmlThemeColors,
  chartType: SpreadsheetChartArtifactType,
): OoxmlChartSeriesReference[] => {
  const seriesValueAxisIds = parseOoxmlChartSeriesValueAxisIds(chartXml)
  const seriesChartTypes = parseOoxmlChartSeriesChartTypes(chartXml)
  return extractQualifiedTagBlocks(chartXml, 'ser').map((serBlock, index) => {
    const idxAttrs = parseAttributes(extractFirstQualifiedTag(serBlock, 'idx') ?? '')
    const orderAttrs = parseAttributes(extractFirstQualifiedTag(serBlock, 'order') ?? '')
    const nameFormula = parseOoxmlSeriesFormula(serBlock, 'tx')
    const categoryFormula = parseOoxmlSeriesFormula(serBlock, 'cat')
    const valueFormula = parseOoxmlSeriesFormula(serBlock, 'val')
    const xFormula = parseOoxmlSeriesFormula(serBlock, 'xVal')
    const yFormula = parseOoxmlSeriesFormula(serBlock, 'yVal')
    const lineColor = parseOoxmlSeriesLineColor(serBlock, themeColors)
    const markerFillColor = parseOoxmlSeriesMarkerColor(serBlock, 'fill', themeColors)
    const markerLineColor = parseOoxmlSeriesMarkerColor(serBlock, 'line', themeColors)
    const fillColor = parseOoxmlSeriesColor(serBlock, themeColors)
    const trendlineMetadata = parseOoxmlSeriesTrendlineMetadata(serBlock)
    const errorBarMetadata = parseOoxmlSeriesErrorBarMetadata(serBlock)
    const dataLabelMetadata = parseOoxmlSeriesDataLabelMetadata(serBlock, themeColors)
    const pointStyles = parseOoxmlSeriesPointMetadata(serBlock, themeColors)
    const explosion = parseOoxmlSeriesExplosion(serBlock)
    const seriesChartType = seriesChartTypes[index] ?? chartType
    const color = seriesChartType === 'line' || seriesChartType === 'scatter'
      ? lineColor ?? markerLineColor ?? markerFillColor ?? fillColor
      : fillColor ?? lineColor ?? markerFillColor ?? markerLineColor
    return {
      index: Number.isInteger(Number(idxAttrs.val)) ? Number(idxAttrs.val) : index,
      order: Number.isInteger(Number(orderAttrs.val)) ? Number(orderAttrs.val) : index,
      chartType: seriesChartType,
      valueAxisId: seriesValueAxisIds[index] || undefined,
      name: parseOoxmlSeriesText(serBlock, 'tx'),
      color,
      lineColor,
      lineWidth: parseOoxmlSeriesLineWidth(serBlock),
      markerSize: parseOoxmlSeriesMarkerSize(serBlock),
      markerShape: parseOoxmlSeriesMarkerShape(serBlock),
      markerFillColor,
      markerLineColor,
      lineDash: parseOoxmlSeriesLineDash(serBlock),
      fillOpacity: parseOoxmlSeriesFillOpacity(serBlock),
      fillPattern: parseOoxmlSeriesFillPattern(serBlock),
      fillForegroundColor: parseOoxmlSeriesPatternColor(serBlock, 'fgClr', themeColors),
      fillBackgroundColor: parseOoxmlSeriesPatternColor(serBlock, 'bgClr', themeColors),
      ...trendlineMetadata,
      ...errorBarMetadata,
      ...dataLabelMetadata,
      ...(explosion == null ? {} : { explosion }),
      ...(pointStyles.length === 0 ? {} : {
        pointIndexes: pointStyles.map((point) => point.index),
        pointColors: pointStyles.map((point) => point.color ?? ''),
        pointLineColors: pointStyles.map((point) => point.lineColor ?? ''),
        pointFillOpacities: pointStyles.map((point) => point.fillOpacity ?? ''),
        pointFillPatterns: pointStyles.map((point) => point.fillPattern ?? ''),
        pointFillForegroundColors: pointStyles.map((point) => point.fillForegroundColor ?? ''),
        pointFillBackgroundColors: pointStyles.map((point) => point.fillBackgroundColor ?? ''),
        pointExplosions: pointStyles.map((point) => point.explosion ?? ''),
        pointStyles,
      }),
      nameFormula,
      categoryFormula,
      valueFormula,
      xFormula,
      yFormula,
      nameRange: nameFormula ? parseFormulaRangeRef(nameFormula, fallbackSheetId, sheetIdByName) ?? undefined : undefined,
      categoryRange: categoryFormula ? parseFormulaRangeRef(categoryFormula, fallbackSheetId, sheetIdByName) ?? undefined : undefined,
      valueRange: valueFormula ? parseFormulaRangeRef(valueFormula, fallbackSheetId, sheetIdByName) ?? undefined : undefined,
      xRange: xFormula ? parseFormulaRangeRef(xFormula, fallbackSheetId, sheetIdByName) ?? undefined : undefined,
      yRange: yFormula ? parseFormulaRangeRef(yFormula, fallbackSheetId, sheetIdByName) ?? undefined : undefined,
    }
  })
}

const parseOoxmlChartReferenceFormulas = (chartXml: string, seriesReferences?: OoxmlChartSeriesReference[]): string[] => {
  const seriesFormulas = (seriesReferences ?? []).flatMap((series) => [
    series.nameFormula,
    series.categoryFormula,
    series.valueFormula,
    series.xFormula,
    series.yFormula,
  ]).filter((formula): formula is string => Boolean(formula))
  if (seriesFormulas.length > 0) return seriesFormulas
  const formulas: string[] = []
  for (const serBlock of extractQualifiedTagBlocks(chartXml, 'ser')) {
    for (const localName of ['tx', 'cat', 'val', 'xVal', 'yVal'] as const) {
      const formula = parseOoxmlSeriesFormula(serBlock, localName)
      if (formula) formulas.push(formula)
    }
  }
  if (formulas.length > 0) return formulas
  return Array.from(chartXml.matchAll(/<(?:[A-Za-z_][\w.-]*:)?f\b[^>]*>([\s\S]*?)<\/(?:[A-Za-z_][\w.-]*:)?f>/gi))
    .map((match) => decodeXmlText(match[1] ?? '').trim())
    .filter(Boolean)
}

const chartRangeIsSingleRow = (range: SpreadsheetRangeAddress | undefined): boolean => (
  Boolean(range && range.start_row === range.end_row && range.end_col > range.start_col)
)

const chartRangeIsSingleColumn = (range: SpreadsheetRangeAddress | undefined): boolean => (
  Boolean(range && range.start_col === range.end_col && range.end_row > range.start_row)
)

const inferOoxmlChartSeriesOrientation = (seriesReferences: OoxmlChartSeriesReference[]): 'columns' | 'rows' => {
  const valueRanges = seriesReferences
    .flatMap((series) => [series.valueRange, series.yRange])
    .filter((range): range is SpreadsheetRangeAddress => range != null)
  if (valueRanges.length === 0) return 'columns'
  const rowOriented = valueRanges.every(chartRangeIsSingleRow)
  const columnOriented = valueRanges.every(chartRangeIsSingleColumn)
  if (rowOriented && !columnOriented) return 'rows'
  return 'columns'
}

const rangeTouchesRow = (range: SpreadsheetRangeAddress | undefined, row: number): boolean => (
  Boolean(range && range.start_row <= row && range.end_row >= row)
)

const rangeTouchesCol = (range: SpreadsheetRangeAddress | undefined, col: number): boolean => (
  Boolean(range && range.start_col <= col && range.end_col >= col)
)

const inferOoxmlChartFirstRowHeaders = (
  sourceRange: SpreadsheetRangeAddress,
  seriesReferences: OoxmlChartSeriesReference[],
): boolean => (
  seriesReferences.some((series) => rangeTouchesRow(series.nameRange, sourceRange.start_row)) ||
  seriesReferences.some((series) => rangeTouchesRow(series.categoryRange ?? series.xRange, sourceRange.start_row)) ||
  sourceRange.end_row > sourceRange.start_row
)

const inferOoxmlChartFirstColumnCategories = (
  sourceRange: SpreadsheetRangeAddress,
  seriesReferences: OoxmlChartSeriesReference[],
): boolean => (
  seriesReferences.some((series) => rangeTouchesCol(series.categoryRange ?? series.xRange, sourceRange.start_col)) ||
  sourceRange.end_col > sourceRange.start_col
)

const buildOoxmlChartImportDiagnostics = (
  chartType: SpreadsheetChartArtifactType,
  seriesReferences: OoxmlChartSeriesReference[],
  chartLocalNames: string[],
  chartComponentTypes: SpreadsheetChartArtifactType[],
  valueAxisMetadata = parseOoxmlSecondaryValueAxisMetadata('', DEFAULT_OOXML_THEME_COLORS),
  dataLabelMetadata: OoxmlChartDataLabelMetadata = {},
  axisMetadata: {
    categoryAxisNumberFormat?: string | null
    valueAxisNumberFormat?: string | null
    categoryAxisHasMajorGridlines?: boolean
    categoryAxisHasMinorGridlines?: boolean
    valueAxisHasMajorGridlines?: boolean
    valueAxisHasMinorGridlines?: boolean
    categoryAxisDeleted?: boolean | null
    valueAxisDeleted?: boolean | null
  } = {},
  chartStyleMetadata: ReturnType<typeof parseOoxmlChartStyleMetadata> = {
    chartStyleId: null,
    roundedCorners: null,
    chartArea: {},
    plotArea: {},
  },
  plotSemanticsMetadata: OoxmlChartPlotSemanticsMetadata = {},
): string[] => {
  const diagnostics: string[] = []
  if (chartComponentTypes.length > 1) {
    diagnostics.push('Combo chart package is imported as one native range-chart artifact using the first supported OOXML plot in document order.')
  }
  if (valueAxisMetadata.hasSecondaryValueAxis) {
    diagnostics.push('Secondary value axis is recorded as native chart metadata but rendered on the primary value-axis scale.')
  }
  if (seriesReferences.some((series) => series.trendlineType != null)) {
    diagnostics.push('Excel trendlines are recorded as native chart metadata but are not rendered in the current native range-chart overlay.')
  }
  if (seriesReferences.some((series) => series.errorBarType != null || series.errorBarValueType != null)) {
    diagnostics.push('Excel error bars are recorded as native chart metadata but are not rendered in the current native range-chart overlay.')
  }
  if (seriesReferences.some((series) => (
    series.explosion != null ||
    (series.pointStyles?.length ?? 0) > 0
  ))) {
    diagnostics.push('Excel per-point chart styling and pie slice explosion are recorded as native chart metadata but are not fully rendered in the current native range-chart overlay.')
  }
  if (
    dataLabelMetadata.dataLabelPosition != null ||
    dataLabelMetadata.dataLabelNumberFormat != null ||
    dataLabelMetadata.dataLabelShowCategoryName === true ||
    dataLabelMetadata.dataLabelShowSeriesName === true ||
    dataLabelMetadata.dataLabelShowPercent === true ||
    dataLabelMetadata.dataLabelShowBubbleSize === true ||
    seriesReferences.some((series) => (
      series.dataLabelPosition != null ||
      series.dataLabelNumberFormat != null ||
      series.dataLabelShowCategoryName === true ||
      series.dataLabelShowSeriesName === true ||
      series.dataLabelShowPercent === true ||
      series.dataLabelShowBubbleSize === true
    ))
  ) {
    diagnostics.push('Excel data-label detail is recorded as native chart metadata but the current native range-chart overlay only renders basic value labels.')
  }
  if (
    axisMetadata.categoryAxisNumberFormat != null ||
    axisMetadata.valueAxisNumberFormat != null ||
    axisMetadata.categoryAxisHasMajorGridlines === true ||
    axisMetadata.categoryAxisHasMinorGridlines === true ||
    axisMetadata.valueAxisHasMajorGridlines === true ||
    axisMetadata.valueAxisHasMinorGridlines === true ||
    axisMetadata.categoryAxisDeleted === true ||
    axisMetadata.valueAxisDeleted === true ||
    valueAxisMetadata.secondaryValueAxisNumberFormat != null ||
    valueAxisMetadata.secondaryValueAxisHasMajorGridlines === true ||
    valueAxisMetadata.secondaryValueAxisHasMinorGridlines === true ||
    valueAxisMetadata.secondaryValueAxisDeleted === true
  ) {
    diagnostics.push('Excel axis number formats, gridlines, and hidden-axis flags are recorded as native chart metadata but only partially rendered in the current native range-chart overlay.')
  }
  if (
    chartStyleMetadata.chartStyleId != null ||
    chartStyleMetadata.roundedCorners != null ||
    Object.keys(chartStyleMetadata.chartArea).length > 0 ||
    Object.keys(chartStyleMetadata.plotArea).length > 0
  ) {
    diagnostics.push('Excel chart-area and plot-area styling is recorded as native chart metadata but is not fully rendered in the current native range-chart overlay.')
  }
  if (Object.keys(plotSemanticsMetadata).length > 0) {
    diagnostics.push('Excel chart plot options such as grouping, gap/overlap, smoothing, and pie/doughnut angles are recorded as native chart metadata but only partially rendered in the current native range-chart overlay.')
  }
  if (chartLocalNames.some((localName) => localName === 'bubbleChart' || localName === 'radarChart' || localName === 'surfaceChart' || localName === 'stockChart')) {
    diagnostics.push('Unsupported Excel chart subtypes are not fully represented by the native range-chart model.')
  }
  if (seriesReferences.length === 0) {
    diagnostics.push('No chart series references were found.')
  }
  if (chartType === 'scatter' && seriesReferences.some((series) => series.xRange == null || series.yRange == null)) {
    diagnostics.push('Scatter chart series without explicit X/Y ranges are imported as a best-effort range chart.')
  }
  if (chartType === 'pie' && seriesReferences.length > 1) {
    diagnostics.push('Multi-series pie chart is imported as a native range chart; detailed pie split semantics are not preserved.')
  }
  if (seriesReferences.some((series) => series.valueRange == null && series.yRange == null)) {
    diagnostics.push('At least one chart series did not expose a value range.')
  }
  return diagnostics
}

const firstDefinedRange = (...ranges: Array<SpreadsheetRangeAddress | undefined>): SpreadsheetRangeAddress | undefined =>
  ranges.find((range) => range != null)

const countRangeCells = (range: SpreadsheetRangeAddress | undefined): number | undefined => {
  if (range == null) return undefined
  return Math.max(1, range.end_row - range.start_row + 1) * Math.max(1, range.end_col - range.start_col + 1)
}

const mergeRangeBounds = (ranges: SpreadsheetRangeAddress[]): SpreadsheetRangeAddress | null => {
  if (ranges.length === 0) return null
  const sheetId = ranges[0]!.sheet_id
  const sameSheet = ranges.filter((range) => range.sheet_id === sheetId)
  if (sameSheet.length === 0) return null
  return {
    sheet_id: sheetId,
    start_row: Math.min(...sameSheet.map((range) => range.start_row)),
    start_col: Math.min(...sameSheet.map((range) => range.start_col)),
    end_row: Math.max(...sameSheet.map((range) => range.end_row)),
    end_col: Math.max(...sameSheet.map((range) => range.end_col)),
  }
}

const parseDrawingMarker = (anchorXml: string, markerName: 'from' | 'to'): { row: number; col: number } | null => {
  const marker = extractFirstQualifiedTagBlock(anchorXml, markerName)
  if (marker == null) return null
  const row = Number(decodeXmlText(extractFirstQualifiedTagText(marker, 'row') ?? '').trim())
  const col = Number(decodeXmlText(extractFirstQualifiedTagText(marker, 'col') ?? '').trim())
  if (!Number.isInteger(row) || row < 0 || !Number.isInteger(col) || col < 0) return null
  return { row, col }
}

const defaultChartPlacementTo = (from: { row: number; col: number }): { row: number; col: number } => ({
  row: from.row + 10,
  col: from.col + 6,
})

const parseChartAnchorsFromDrawing = (
  drawingXml: string | null,
  drawingPartName: string,
  drawingRelationships: Relationship[],
): Array<{
  chartPartName: string
  placement: Omit<SpreadsheetRangeAddress, 'sheet_id'>
  relationshipId: string
}> => {
  if (drawingXml == null) return []
  const relationshipById = new Map(drawingRelationships.map((relationship) => [relationship.id, relationship]))
  const out: Array<{
    chartPartName: string
    placement: Omit<SpreadsheetRangeAddress, 'sheet_id'>
    relationshipId: string
  }> = []
  for (const localName of ['twoCellAnchor', 'oneCellAnchor']) {
    for (const anchor of extractQualifiedTagBlocks(drawingXml, localName)) {
      const chartAttrs = parseAttributes(extractFirstQualifiedTag(anchor, 'chart') ?? '')
      const relationshipId = chartAttrs['r:id']
      if (!relationshipId) continue
      const relationship = relationshipById.get(relationshipId)
      if (relationship == null || relationship.type !== CHART_REL_TYPE || relationship.target_mode === 'External') continue
      const from = parseDrawingMarker(anchor, 'from')
      if (from == null) continue
      const to = localName === 'twoCellAnchor'
        ? parseDrawingMarker(anchor, 'to') ?? defaultChartPlacementTo(from)
        : defaultChartPlacementTo(from)
      out.push({
        chartPartName: resolveRelationshipTarget(drawingPartName, relationship.target),
        relationshipId,
        placement: {
          start_row: Math.min(from.row, to.row),
          start_col: Math.min(from.col, to.col),
          end_row: Math.max(from.row, to.row),
          end_col: Math.max(from.col, to.col),
        },
      })
    }
  }
  return out
}

type NativeChartImportResult = {
  artifacts: Record<string, SpreadsheetDerivedArtifact>
  warnings: SpreadsheetExcelIoWarning[]
}

const parseNativeChartImport = (args: {
  files: Record<string, Uint8Array>
  worksheets: SpreadsheetExcelOoxmlWorksheetPart[]
  sheetIds: string[]
  sheetIdByName: Map<string, string>
  themeColors: OoxmlThemeColors
}): NativeChartImportResult => {
  const artifacts: Record<string, SpreadsheetDerivedArtifact> = {}
  const warnings: SpreadsheetExcelIoWarning[] = []
  for (const [worksheetIndex, worksheet] of args.worksheets.entries()) {
    const sheetId = args.sheetIds[worksheetIndex]
    if (!sheetId || !worksheet.part_name) continue
    const worksheetRelationships = parseRelationships(readXml(args.files, relationshipsPartNameFor(worksheet.part_name)))
    const drawingRelationships = worksheetRelationships.filter((relationship) => relationship.type === DRAWING_REL_TYPE && relationship.target_mode !== 'External')
    let chartIndex = 0
    for (const drawingRelationship of drawingRelationships) {
      const drawingPartName = resolveRelationshipTarget(worksheet.part_name, drawingRelationship.target)
      const drawingXml = readXml(args.files, drawingPartName)
      const drawingPartRelationships = parseRelationships(readXml(args.files, relationshipsPartNameFor(drawingPartName)))
      for (const anchor of parseChartAnchorsFromDrawing(drawingXml, drawingPartName, drawingPartRelationships)) {
        const chartXml = readXml(args.files, anchor.chartPartName)
        if (chartXml == null) continue
        const chartLocalNames = getOoxmlChartLocalNames(chartXml)
        const chartType = parseOoxmlChartType(chartXml, chartLocalNames)
        const chartComponentTypes = getOoxmlChartComponentTypes(chartXml, chartLocalNames)
        if (chartType == null) {
          warnings.push({
            code: 'xlsx-chart-unsupported',
            severity: 'warning',
            message: 'Excel chart could not be mapped to a native FuseGrid range chart; workbook data still imports.',
            sheet_name: worksheet.name,
            part_name: anchor.chartPartName,
          })
          continue
        }
        const seriesReferences = parseOoxmlChartSeriesReferences(chartXml, sheetId, args.sheetIdByName, args.themeColors, chartType)
        const referencedRanges = parseOoxmlChartReferenceFormulas(chartXml, seriesReferences)
          .map((formula) => parseFormulaRangeRef(formula, sheetId, args.sheetIdByName))
          .filter((range): range is SpreadsheetRangeAddress => range != null)
        const sourceRange = mergeRangeBounds(referencedRanges)
        if (sourceRange == null) {
          warnings.push({
            code: 'xlsx-chart-source-unresolved',
            severity: 'warning',
            message: 'Excel chart references could not be resolved to workbook ranges; workbook data still imports.',
            sheet_name: worksheet.name,
            part_name: anchor.chartPartName,
          })
          continue
        }
        chartIndex += 1
        const artifactId = `ooxml_chart_${sheetId}_${chartIndex}`
        const rowCount = sourceRange.end_row - sourceRange.start_row + 1
        const columnCount = sourceRange.end_col - sourceRange.start_col + 1
        const valueAxisMetadata = parseOoxmlSecondaryValueAxisMetadata(chartXml, args.themeColors)
        const dataLabelMetadata = parseOoxmlChartDataLabelMetadata(chartXml, args.themeColors)
        const chartStyleMetadata = parseOoxmlChartStyleMetadata(chartXml, args.themeColors)
        const plotSemanticsMetadata = parseOoxmlChartPlotSemanticsMetadata(chartXml, chartLocalNames)
        const categoryAxisLocalName = parseOoxmlCategoryAxisLocalName(chartXml)
        const categoryAxisDateSettings = parseOoxmlCategoryAxisDateSettings(chartXml)
        const valueAxisScale = parseOoxmlValueAxisScale(chartXml)
        const valueAxisDisplayUnit = parseOoxmlValueAxisDisplayUnit(chartXml)
        const categoryAxisCrossing = parseOoxmlAxisCrossing(chartXml, categoryAxisLocalName)
        const categoryAxisNumberFormat = parseOoxmlAxisNumberFormat(chartXml, categoryAxisLocalName)
        const valueAxisNumberFormat = parseOoxmlAxisNumberFormat(chartXml, 'valAx')
        const categoryAxisGridlines = parseOoxmlAxisGridlines(chartXml, categoryAxisLocalName)
        const valueAxisGridlines = parseOoxmlAxisGridlines(chartXml, 'valAx')
        const categoryAxisDeleted = parseOoxmlAxisDeleted(chartXml, categoryAxisLocalName)
        const valueAxisDeleted = parseOoxmlAxisDeleted(chartXml, 'valAx')
        const importDiagnostics = buildOoxmlChartImportDiagnostics(
          chartType,
          seriesReferences,
          chartLocalNames,
          chartComponentTypes,
          valueAxisMetadata,
          dataLabelMetadata,
          {
            categoryAxisNumberFormat: categoryAxisNumberFormat.numberFormat,
            valueAxisNumberFormat: valueAxisNumberFormat.numberFormat,
            categoryAxisHasMajorGridlines: categoryAxisGridlines.hasMajorGridlines,
            categoryAxisHasMinorGridlines: categoryAxisGridlines.hasMinorGridlines,
            valueAxisHasMajorGridlines: valueAxisGridlines.hasMajorGridlines,
            valueAxisHasMinorGridlines: valueAxisGridlines.hasMinorGridlines,
            categoryAxisDeleted,
            valueAxisDeleted,
          },
          chartStyleMetadata,
          plotSemanticsMetadata,
        )
        const primarySeries = seriesReferences[0]
        const primaryCategoryRange = firstDefinedRange(primarySeries?.categoryRange, primarySeries?.xRange)
        const primaryValueRange = firstDefinedRange(primarySeries?.valueRange, primarySeries?.yRange)
        if (importDiagnostics.length > 0) {
          warnings.push({
            code: 'xlsx-chart-import-partial',
            severity: 'warning',
            message: importDiagnostics.join(' '),
            sheet_name: worksheet.name,
            part_name: anchor.chartPartName,
          })
        }
        artifacts[artifactId] = {
          id: artifactId,
          sheet_id: sheetId,
          kind: 'chart',
          chart_type: chartType,
          title: parseOoxmlChartTitle(chartXml, `Chart ${chartIndex}`),
          source_range: sourceRange,
          placement: {
            sheet_id: sheetId,
            ...anchor.placement,
          },
          metadata: {
            excel_range_chart: {
              sourceKind: 'excel_ooxml_chart',
              rowCount,
              columnCount,
              seriesOrientation: inferOoxmlChartSeriesOrientation(seriesReferences),
              firstRowHeaders: inferOoxmlChartFirstRowHeaders(sourceRange, seriesReferences),
              firstColumnCategories: inferOoxmlChartFirstColumnCategories(sourceRange, seriesReferences),
              chartType,
              chartComponents: chartComponentTypes,
              chartLocalNames,
              chartStyleId: chartStyleMetadata.chartStyleId,
              ...parseOoxmlChartTitleTextStyle(chartXml, args.themeColors),
              chartRoundedCorners: chartStyleMetadata.roundedCorners,
              chartGrouping: plotSemanticsMetadata.chartGrouping ?? null,
              chartVaryColors: plotSemanticsMetadata.chartVaryColors ?? null,
              barGapWidth: plotSemanticsMetadata.barGapWidth ?? null,
              barOverlap: plotSemanticsMetadata.barOverlap ?? null,
              lineSmooth: plotSemanticsMetadata.lineSmooth ?? null,
              pieFirstSliceAngle: plotSemanticsMetadata.pieFirstSliceAngle ?? null,
              doughnutHoleSize: plotSemanticsMetadata.doughnutHoleSize ?? null,
              chartAreaFillColor: chartStyleMetadata.chartArea.fillColor ?? '',
              chartAreaFillOpacity: chartStyleMetadata.chartArea.fillOpacity ?? '',
              chartAreaLineColor: chartStyleMetadata.chartArea.lineColor ?? '',
              chartAreaLineWidth: chartStyleMetadata.chartArea.lineWidth ?? '',
              chartAreaFillPattern: chartStyleMetadata.chartArea.fillPattern ?? '',
              chartAreaFillForegroundColor: chartStyleMetadata.chartArea.fillForegroundColor ?? '',
              chartAreaFillBackgroundColor: chartStyleMetadata.chartArea.fillBackgroundColor ?? '',
              plotAreaFillColor: chartStyleMetadata.plotArea.fillColor ?? '',
              plotAreaFillOpacity: chartStyleMetadata.plotArea.fillOpacity ?? '',
              plotAreaLineColor: chartStyleMetadata.plotArea.lineColor ?? '',
              plotAreaLineWidth: chartStyleMetadata.plotArea.lineWidth ?? '',
              plotAreaFillPattern: chartStyleMetadata.plotArea.fillPattern ?? '',
              plotAreaFillForegroundColor: chartStyleMetadata.plotArea.fillForegroundColor ?? '',
              plotAreaFillBackgroundColor: chartStyleMetadata.plotArea.fillBackgroundColor ?? '',
              showLegend: /<(?:[A-Za-z_][\w.-]*:)?legend\b/i.test(chartXml),
              legendPosition: parseOoxmlLegendPosition(chartXml),
              showDataLabels: /<(?:[A-Za-z_][\w.-]*:)?dLbls\b/i.test(chartXml),
              dataLabelPosition: dataLabelMetadata.dataLabelPosition ?? null,
              dataLabelNumberFormat: dataLabelMetadata.dataLabelNumberFormat ?? null,
              dataLabelNumberFormatLinkedToSource: dataLabelMetadata.dataLabelNumberFormatLinkedToSource ?? null,
              dataLabelSeparator: dataLabelMetadata.dataLabelSeparator ?? null,
              dataLabelFontColor: dataLabelMetadata.dataLabelFontColor ?? null,
              dataLabelFontSize: dataLabelMetadata.dataLabelFontSize ?? null,
              dataLabelFontBold: dataLabelMetadata.dataLabelFontBold ?? null,
              dataLabelFontItalic: dataLabelMetadata.dataLabelFontItalic ?? null,
              dataLabelTextRotation: dataLabelMetadata.dataLabelTextRotation ?? null,
              dataLabelTextAlign: dataLabelMetadata.dataLabelTextAlign ?? null,
              dataLabelTextVerticalAlign: dataLabelMetadata.dataLabelTextVerticalAlign ?? null,
              dataLabelTextWrap: dataLabelMetadata.dataLabelTextWrap ?? null,
              dataLabelTextAutoFit: dataLabelMetadata.dataLabelTextAutoFit ?? null,
              dataLabelTextAutoFitFontScale: dataLabelMetadata.dataLabelTextAutoFitFontScale ?? null,
              dataLabelTextHorizontalOverflow: dataLabelMetadata.dataLabelTextHorizontalOverflow ?? null,
              dataLabelTextVerticalOverflow: dataLabelMetadata.dataLabelTextVerticalOverflow ?? null,
              dataLabelTextInsetTop: dataLabelMetadata.dataLabelTextInsetTop ?? null,
              dataLabelTextInsetRight: dataLabelMetadata.dataLabelTextInsetRight ?? null,
              dataLabelTextInsetBottom: dataLabelMetadata.dataLabelTextInsetBottom ?? null,
              dataLabelTextInsetLeft: dataLabelMetadata.dataLabelTextInsetLeft ?? null,
              dataLabelShowLegendKey: dataLabelMetadata.dataLabelShowLegendKey ?? null,
              dataLabelShowValue: dataLabelMetadata.dataLabelShowValue ?? null,
              dataLabelShowCategoryName: dataLabelMetadata.dataLabelShowCategoryName ?? null,
              dataLabelShowSeriesName: dataLabelMetadata.dataLabelShowSeriesName ?? null,
              dataLabelShowPercent: dataLabelMetadata.dataLabelShowPercent ?? null,
              dataLabelShowBubbleSize: dataLabelMetadata.dataLabelShowBubbleSize ?? null,
              dataLabelShowLeaderLines: dataLabelMetadata.dataLabelShowLeaderLines ?? null,
              showCategoryAxis: /<(?:[A-Za-z_][\w.-]*:)?(?:catAx|dateAx)\b/i.test(chartXml),
              showValueAxis: /<(?:[A-Za-z_][\w.-]*:)?valAx\b/i.test(chartXml),
              categoryAxisKind: categoryAxisDateSettings.categoryAxisKind,
              categoryAxisPosition: parseOoxmlAxisPosition(chartXml, categoryAxisLocalName),
              valueAxisPosition: parseOoxmlAxisPosition(chartXml, 'valAx'),
              categoryAxisTitle: parseOoxmlAxisTitle(chartXml, categoryAxisLocalName),
              valueAxisTitle: parseOoxmlAxisTitle(chartXml, 'valAx'),
              ...parseOoxmlAxisTitleTextStyle(chartXml, categoryAxisLocalName, args.themeColors, 'categoryAxisTitle'),
              ...parseOoxmlAxisTitleTextStyle(chartXml, 'valAx', args.themeColors, 'valueAxisTitle'),
              categoryAxisCrosses: categoryAxisCrossing.crosses,
              categoryAxisCrossesAt: categoryAxisCrossing.crossesAt,
              categoryAxisNumberFormat: categoryAxisNumberFormat.numberFormat,
              categoryAxisNumberFormatLinkedToSource: categoryAxisNumberFormat.numberFormatLinkedToSource,
              valueAxisNumberFormat: valueAxisNumberFormat.numberFormat,
              valueAxisNumberFormatLinkedToSource: valueAxisNumberFormat.numberFormatLinkedToSource,
              categoryAxisHasMajorGridlines: categoryAxisGridlines.hasMajorGridlines,
              categoryAxisHasMinorGridlines: categoryAxisGridlines.hasMinorGridlines,
              valueAxisHasMajorGridlines: valueAxisGridlines.hasMajorGridlines,
              valueAxisHasMinorGridlines: valueAxisGridlines.hasMinorGridlines,
              categoryAxisDeleted,
              valueAxisDeleted,
              categoryAxisTickLabelPosition: parseOoxmlAxisTickLabelPosition(chartXml, categoryAxisLocalName),
              valueAxisTickLabelPosition: parseOoxmlAxisTickLabelPosition(chartXml, 'valAx'),
              categoryAxisMajorTickMark: parseOoxmlAxisTickMark(chartXml, categoryAxisLocalName, 'majorTickMark'),
              categoryAxisMinorTickMark: parseOoxmlAxisTickMark(chartXml, categoryAxisLocalName, 'minorTickMark'),
              valueAxisMajorTickMark: parseOoxmlAxisTickMark(chartXml, 'valAx', 'majorTickMark'),
              valueAxisMinorTickMark: parseOoxmlAxisTickMark(chartXml, 'valAx', 'minorTickMark'),
              categoryAxisBaseTimeUnit: categoryAxisDateSettings.categoryAxisBaseTimeUnit,
              categoryAxisMajorTimeUnit: categoryAxisDateSettings.categoryAxisMajorTimeUnit,
              categoryAxisMinorTimeUnit: categoryAxisDateSettings.categoryAxisMinorTimeUnit,
              categoryAxisMajorUnit: categoryAxisDateSettings.categoryAxisMajorUnit,
              categoryAxisMinorUnit: categoryAxisDateSettings.categoryAxisMinorUnit,
              valueAxisMin: valueAxisScale.valueAxisMin,
              valueAxisMax: valueAxisScale.valueAxisMax,
              valueAxisLogBase: valueAxisScale.valueAxisLogBase,
              valueAxisDisplayUnit: valueAxisDisplayUnit.valueAxisDisplayUnit,
              valueAxisDisplayUnitCustom: valueAxisDisplayUnit.valueAxisDisplayUnitCustom,
              valueAxisCrosses: valueAxisScale.valueAxisCrosses,
              valueAxisCrossesAt: valueAxisScale.valueAxisCrossesAt,
              valueAxisCount: valueAxisMetadata.valueAxisCount,
              hasSecondaryValueAxis: valueAxisMetadata.hasSecondaryValueAxis,
              secondaryValueAxisId: valueAxisMetadata.secondaryValueAxisId,
              secondaryValueAxisPosition: valueAxisMetadata.secondaryValueAxisPosition,
              secondaryValueAxisTitle: valueAxisMetadata.secondaryValueAxisTitle,
              secondaryValueAxisTitleFontColor: valueAxisMetadata.secondaryValueAxisTitleFontColor ?? '',
              secondaryValueAxisTitleFontSize: valueAxisMetadata.secondaryValueAxisTitleFontSize ?? '',
              secondaryValueAxisTitleFontBold: valueAxisMetadata.secondaryValueAxisTitleFontBold ?? '',
              secondaryValueAxisTitleFontItalic: valueAxisMetadata.secondaryValueAxisTitleFontItalic ?? '',
              secondaryValueAxisTitleTextRotation: valueAxisMetadata.secondaryValueAxisTitleTextRotation ?? '',
              secondaryValueAxisTitleTextAlign: valueAxisMetadata.secondaryValueAxisTitleTextAlign ?? '',
              secondaryValueAxisTitleTextVerticalAlign: valueAxisMetadata.secondaryValueAxisTitleTextVerticalAlign ?? '',
              secondaryValueAxisTitleTextWrap: valueAxisMetadata.secondaryValueAxisTitleTextWrap ?? '',
              secondaryValueAxisTitleTextAutoFit: valueAxisMetadata.secondaryValueAxisTitleTextAutoFit ?? '',
              secondaryValueAxisTitleTextAutoFitFontScale: valueAxisMetadata.secondaryValueAxisTitleTextAutoFitFontScale ?? '',
              secondaryValueAxisTitleTextHorizontalOverflow: valueAxisMetadata.secondaryValueAxisTitleTextHorizontalOverflow ?? '',
              secondaryValueAxisTitleTextVerticalOverflow: valueAxisMetadata.secondaryValueAxisTitleTextVerticalOverflow ?? '',
              secondaryValueAxisTitleTextInsetTop: valueAxisMetadata.secondaryValueAxisTitleTextInsetTop ?? '',
              secondaryValueAxisTitleTextInsetRight: valueAxisMetadata.secondaryValueAxisTitleTextInsetRight ?? '',
              secondaryValueAxisTitleTextInsetBottom: valueAxisMetadata.secondaryValueAxisTitleTextInsetBottom ?? '',
              secondaryValueAxisTitleTextInsetLeft: valueAxisMetadata.secondaryValueAxisTitleTextInsetLeft ?? '',
              secondaryValueAxisMin: valueAxisMetadata.secondaryValueAxisMin,
              secondaryValueAxisMax: valueAxisMetadata.secondaryValueAxisMax,
              secondaryValueAxisLogBase: valueAxisMetadata.secondaryValueAxisLogBase,
              secondaryValueAxisDisplayUnit: valueAxisMetadata.secondaryValueAxisDisplayUnit,
              secondaryValueAxisDisplayUnitCustom: valueAxisMetadata.secondaryValueAxisDisplayUnitCustom,
              secondaryValueAxisCrosses: valueAxisMetadata.secondaryValueAxisCrosses,
              secondaryValueAxisCrossesAt: valueAxisMetadata.secondaryValueAxisCrossesAt,
              secondaryValueAxisTickLabelPosition: valueAxisMetadata.secondaryValueAxisTickLabelPosition,
              secondaryValueAxisMajorTickMark: valueAxisMetadata.secondaryValueAxisMajorTickMark,
              secondaryValueAxisMinorTickMark: valueAxisMetadata.secondaryValueAxisMinorTickMark,
              secondaryValueAxisNumberFormat: valueAxisMetadata.secondaryValueAxisNumberFormat,
              secondaryValueAxisNumberFormatLinkedToSource: valueAxisMetadata.secondaryValueAxisNumberFormatLinkedToSource,
              secondaryValueAxisHasMajorGridlines: valueAxisMetadata.secondaryValueAxisHasMajorGridlines,
              secondaryValueAxisHasMinorGridlines: valueAxisMetadata.secondaryValueAxisHasMinorGridlines,
              secondaryValueAxisDeleted: valueAxisMetadata.secondaryValueAxisDeleted,
              seriesCount: seriesReferences.length,
              seriesColors: seriesReferences.map((series) => series.color ?? ''),
              seriesLineColors: seriesReferences.map((series) => series.lineColor ?? ''),
              seriesLineWidths: seriesReferences.map((series) => series.lineWidth ?? 0),
              seriesMarkerSizes: seriesReferences.map((series) => series.markerSize ?? 0),
              seriesMarkerShapes: seriesReferences.map((series) => series.markerShape ?? ''),
              seriesMarkerFillColors: seriesReferences.map((series) => series.markerFillColor ?? ''),
              seriesMarkerLineColors: seriesReferences.map((series) => series.markerLineColor ?? ''),
              seriesLineDashes: seriesReferences.map((series) => series.lineDash ?? ''),
              seriesFillOpacities: seriesReferences.map((series) => series.fillOpacity ?? ''),
              seriesFillPatterns: seriesReferences.map((series) => series.fillPattern ?? ''),
              seriesFillForegroundColors: seriesReferences.map((series) => series.fillForegroundColor ?? ''),
              seriesFillBackgroundColors: seriesReferences.map((series) => series.fillBackgroundColor ?? ''),
              seriesTrendlineTypes: seriesReferences.map((series) => series.trendlineType ?? ''),
              seriesTrendlineOrders: seriesReferences.map((series) => series.trendlineOrder ?? ''),
              seriesTrendlinePeriods: seriesReferences.map((series) => series.trendlinePeriod ?? ''),
              seriesTrendlineShowEquations: seriesReferences.map((series) => series.trendlineShowEquation ?? ''),
              seriesTrendlineShowRSquared: seriesReferences.map((series) => series.trendlineShowRSquared ?? ''),
              seriesErrorBarDirections: seriesReferences.map((series) => series.errorBarDirection ?? ''),
              seriesErrorBarTypes: seriesReferences.map((series) => series.errorBarType ?? ''),
              seriesErrorBarValueTypes: seriesReferences.map((series) => series.errorBarValueType ?? ''),
              seriesErrorBarValues: seriesReferences.map((series) => series.errorBarValue ?? ''),
              seriesErrorBarNoEndCaps: seriesReferences.map((series) => series.errorBarNoEndCap ?? ''),
              seriesChartTypes: seriesReferences.map((series) => series.chartType ?? ''),
              seriesValueAxisIds: seriesReferences.map((series) => series.valueAxisId ?? ''),
              seriesDataLabelPositions: seriesReferences.map((series) => series.dataLabelPosition ?? ''),
              seriesDataLabelNumberFormats: seriesReferences.map((series) => series.dataLabelNumberFormat ?? ''),
              seriesDataLabelNumberFormatLinkedToSources: seriesReferences.map((series) => series.dataLabelNumberFormatLinkedToSource ?? ''),
              seriesDataLabelSeparators: seriesReferences.map((series) => series.dataLabelSeparator ?? ''),
              seriesDataLabelFontColors: seriesReferences.map((series) => series.dataLabelFontColor ?? ''),
              seriesDataLabelFontSizes: seriesReferences.map((series) => series.dataLabelFontSize ?? ''),
              seriesDataLabelFontBolds: seriesReferences.map((series) => series.dataLabelFontBold ?? ''),
              seriesDataLabelFontItalics: seriesReferences.map((series) => series.dataLabelFontItalic ?? ''),
              seriesDataLabelTextRotations: seriesReferences.map((series) => series.dataLabelTextRotation ?? ''),
              seriesDataLabelTextAligns: seriesReferences.map((series) => series.dataLabelTextAlign ?? ''),
              seriesDataLabelTextVerticalAligns: seriesReferences.map((series) => series.dataLabelTextVerticalAlign ?? ''),
              seriesDataLabelTextWraps: seriesReferences.map((series) => series.dataLabelTextWrap ?? ''),
              seriesDataLabelTextAutoFits: seriesReferences.map((series) => series.dataLabelTextAutoFit ?? ''),
              seriesDataLabelTextAutoFitFontScales: seriesReferences.map((series) => series.dataLabelTextAutoFitFontScale ?? ''),
              seriesDataLabelTextHorizontalOverflows: seriesReferences.map((series) => series.dataLabelTextHorizontalOverflow ?? ''),
              seriesDataLabelTextVerticalOverflows: seriesReferences.map((series) => series.dataLabelTextVerticalOverflow ?? ''),
              seriesDataLabelTextInsetTops: seriesReferences.map((series) => series.dataLabelTextInsetTop ?? ''),
              seriesDataLabelTextInsetRights: seriesReferences.map((series) => series.dataLabelTextInsetRight ?? ''),
              seriesDataLabelTextInsetBottoms: seriesReferences.map((series) => series.dataLabelTextInsetBottom ?? ''),
              seriesDataLabelTextInsetLefts: seriesReferences.map((series) => series.dataLabelTextInsetLeft ?? ''),
              seriesDataLabelShowLegendKeys: seriesReferences.map((series) => series.dataLabelShowLegendKey ?? ''),
              seriesDataLabelShowValues: seriesReferences.map((series) => series.dataLabelShowValue ?? ''),
              seriesDataLabelShowCategoryNames: seriesReferences.map((series) => series.dataLabelShowCategoryName ?? ''),
              seriesDataLabelShowSeriesNames: seriesReferences.map((series) => series.dataLabelShowSeriesName ?? ''),
              seriesDataLabelShowPercents: seriesReferences.map((series) => series.dataLabelShowPercent ?? ''),
              seriesDataLabelShowBubbleSizes: seriesReferences.map((series) => series.dataLabelShowBubbleSize ?? ''),
              seriesDataLabelShowLeaderLines: seriesReferences.map((series) => series.dataLabelShowLeaderLines ?? ''),
              seriesExplosions: seriesReferences.map((series) => series.explosion ?? ''),
              seriesPointIndexes: seriesReferences.map((series) => series.pointIndexes ?? []),
              seriesPointColors: seriesReferences.map((series) => series.pointColors ?? []),
              seriesPointLineColors: seriesReferences.map((series) => series.pointLineColors ?? []),
              seriesPointFillOpacities: seriesReferences.map((series) => series.pointFillOpacities ?? []),
              seriesPointFillPatterns: seriesReferences.map((series) => series.pointFillPatterns ?? []),
              seriesPointFillForegroundColors: seriesReferences.map((series) => series.pointFillForegroundColors ?? []),
              seriesPointFillBackgroundColors: seriesReferences.map((series) => series.pointFillBackgroundColors ?? []),
              seriesPointExplosions: seriesReferences.map((series) => series.pointExplosions ?? []),
              series: seriesReferences.map((series) => ({
                index: series.index,
                order: series.order,
                ...(series.chartType ? { chartType: series.chartType } : {}),
                ...(series.valueAxisId ? { valueAxisId: series.valueAxisId } : {}),
                ...(series.name ? { name: series.name } : {}),
                ...(series.color ? { color: series.color } : {}),
                ...(series.lineColor ? { lineColor: series.lineColor } : {}),
                ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                ...(series.markerSize ? { markerSize: series.markerSize } : {}),
                ...(series.markerShape ? { markerShape: series.markerShape } : {}),
                ...(series.markerFillColor ? { markerFillColor: series.markerFillColor } : {}),
                ...(series.markerLineColor ? { markerLineColor: series.markerLineColor } : {}),
                ...(series.lineDash ? { lineDash: series.lineDash } : {}),
                ...(series.fillOpacity != null ? { fillOpacity: series.fillOpacity } : {}),
                ...(series.fillPattern ? { fillPattern: series.fillPattern } : {}),
                ...(series.fillForegroundColor ? { fillForegroundColor: series.fillForegroundColor } : {}),
                ...(series.fillBackgroundColor ? { fillBackgroundColor: series.fillBackgroundColor } : {}),
                ...(series.trendlineType ? { trendlineType: series.trendlineType } : {}),
                ...(series.trendlineOrder ? { trendlineOrder: series.trendlineOrder } : {}),
                ...(series.trendlinePeriod ? { trendlinePeriod: series.trendlinePeriod } : {}),
                ...(series.trendlineShowEquation != null ? { trendlineShowEquation: series.trendlineShowEquation } : {}),
                ...(series.trendlineShowRSquared != null ? { trendlineShowRSquared: series.trendlineShowRSquared } : {}),
                ...(series.errorBarDirection ? { errorBarDirection: series.errorBarDirection } : {}),
                ...(series.errorBarType ? { errorBarType: series.errorBarType } : {}),
                ...(series.errorBarValueType ? { errorBarValueType: series.errorBarValueType } : {}),
                ...(series.errorBarValue != null ? { errorBarValue: series.errorBarValue } : {}),
                ...(series.errorBarNoEndCap != null ? { errorBarNoEndCap: series.errorBarNoEndCap } : {}),
                ...(series.dataLabelPosition ? { dataLabelPosition: series.dataLabelPosition } : {}),
                ...(series.dataLabelNumberFormat ? { dataLabelNumberFormat: series.dataLabelNumberFormat } : {}),
                ...(series.dataLabelNumberFormatLinkedToSource != null ? { dataLabelNumberFormatLinkedToSource: series.dataLabelNumberFormatLinkedToSource } : {}),
                ...(series.dataLabelSeparator ? { dataLabelSeparator: series.dataLabelSeparator } : {}),
                ...(series.dataLabelFontColor ? { dataLabelFontColor: series.dataLabelFontColor } : {}),
                ...(series.dataLabelFontSize ? { dataLabelFontSize: series.dataLabelFontSize } : {}),
                ...(series.dataLabelFontBold != null ? { dataLabelFontBold: series.dataLabelFontBold } : {}),
                ...(series.dataLabelFontItalic != null ? { dataLabelFontItalic: series.dataLabelFontItalic } : {}),
                ...(series.dataLabelTextRotation != null ? { dataLabelTextRotation: series.dataLabelTextRotation } : {}),
                ...(series.dataLabelTextAlign != null ? { dataLabelTextAlign: series.dataLabelTextAlign } : {}),
                ...(series.dataLabelTextVerticalAlign != null ? { dataLabelTextVerticalAlign: series.dataLabelTextVerticalAlign } : {}),
                ...(series.dataLabelTextWrap != null ? { dataLabelTextWrap: series.dataLabelTextWrap } : {}),
                ...(series.dataLabelTextAutoFit != null ? { dataLabelTextAutoFit: series.dataLabelTextAutoFit } : {}),
                ...(series.dataLabelTextAutoFitFontScale != null ? { dataLabelTextAutoFitFontScale: series.dataLabelTextAutoFitFontScale } : {}),
                ...(series.dataLabelTextHorizontalOverflow != null ? { dataLabelTextHorizontalOverflow: series.dataLabelTextHorizontalOverflow } : {}),
                ...(series.dataLabelTextVerticalOverflow != null ? { dataLabelTextVerticalOverflow: series.dataLabelTextVerticalOverflow } : {}),
                ...(series.dataLabelTextInsetTop != null ? { dataLabelTextInsetTop: series.dataLabelTextInsetTop } : {}),
                ...(series.dataLabelTextInsetRight != null ? { dataLabelTextInsetRight: series.dataLabelTextInsetRight } : {}),
                ...(series.dataLabelTextInsetBottom != null ? { dataLabelTextInsetBottom: series.dataLabelTextInsetBottom } : {}),
                ...(series.dataLabelTextInsetLeft != null ? { dataLabelTextInsetLeft: series.dataLabelTextInsetLeft } : {}),
                ...(series.dataLabelShowLegendKey != null ? { dataLabelShowLegendKey: series.dataLabelShowLegendKey } : {}),
                ...(series.dataLabelShowValue != null ? { dataLabelShowValue: series.dataLabelShowValue } : {}),
                ...(series.dataLabelShowCategoryName != null ? { dataLabelShowCategoryName: series.dataLabelShowCategoryName } : {}),
                ...(series.dataLabelShowSeriesName != null ? { dataLabelShowSeriesName: series.dataLabelShowSeriesName } : {}),
                ...(series.dataLabelShowPercent != null ? { dataLabelShowPercent: series.dataLabelShowPercent } : {}),
                ...(series.dataLabelShowBubbleSize != null ? { dataLabelShowBubbleSize: series.dataLabelShowBubbleSize } : {}),
                ...(series.dataLabelShowLeaderLines != null ? { dataLabelShowLeaderLines: series.dataLabelShowLeaderLines } : {}),
                ...(series.explosion != null ? { explosion: series.explosion } : {}),
                ...(series.pointStyles && series.pointStyles.length > 0 ? { pointStyles: series.pointStyles } : {}),
                ...(series.nameFormula ? { nameFormula: series.nameFormula } : {}),
                ...(series.categoryFormula ? { categoryFormula: series.categoryFormula } : {}),
                ...(series.valueFormula ? { valueFormula: series.valueFormula } : {}),
                ...(series.xFormula ? { xFormula: series.xFormula } : {}),
                ...(series.yFormula ? { yFormula: series.yFormula } : {}),
              })),
              ...(chartType === 'pie' ? {
                pieCategoryFormula: primarySeries?.categoryFormula ?? primarySeries?.xFormula ?? null,
                pieValueFormula: primarySeries?.valueFormula ?? primarySeries?.yFormula ?? null,
                pieSliceCount: countRangeCells(primaryValueRange ?? primaryCategoryRange) ?? 0,
              } : {}),
              ...(importDiagnostics.length > 0 ? { importDiagnostics } : {}),
              ooxml_chart_part_name: anchor.chartPartName,
              ooxml_drawing_part_name: drawingPartName,
              ooxml_chart_relationship_id: anchor.relationshipId,
              import_strategy: 'native_range_chart',
            },
          },
        }
      }
    }
  }
  return { artifacts, warnings }
}

const parseWorksheetDimensions = (xml: string | null): { row_count: number; col_count: number; formula_count: number } => {
  if (xml == null) return { row_count: 0, col_count: 0, formula_count: 0 }
  const dimensionMatch = /<dimension\b[^>]*\bref\s*=\s*(["'])(.*?)\1[^>]*>/i.exec(xml)
  const dimensionText = dimensionMatch?.[2] ?? ''
  const dimensionEnd = dimensionText.split(':').pop()
  const dimensionCell = dimensionEnd ? parseCellRef(dimensionEnd) : null
  let maxRow = dimensionCell?.row ?? -1
  let maxCol = dimensionCell?.col ?? -1
  for (const match of xml.matchAll(/<c\b[^>]*\br\s*=\s*(["'])(.*?)\1[^>]*>/g)) {
    const cell = parseCellRef(match[2]!)
    if (!cell) continue
    maxRow = Math.max(maxRow, cell.row)
    maxCol = Math.max(maxCol, cell.col)
  }
  return {
    row_count: maxRow + 1,
    col_count: maxCol + 1,
    formula_count: Array.from(xml.matchAll(/<f(?:\s|>)/g)).length,
  }
}

const parseCellValue = (
  type: string | undefined,
  rawValue: string | null,
  sharedStrings: string[],
): { raw_input: string; computed_value: SpreadsheetCellValue } => {
  const valueText = rawValue == null ? '' : decodeXmlText(rawValue)
  if (type === 's') {
    const index = Number(valueText)
    const text = Number.isInteger(index) ? sharedStrings[index] ?? '' : ''
    return { raw_input: text, computed_value: { kind: 'text', value: text } }
  }
  if (type === 'str' || type === 'inlineStr') {
    return { raw_input: valueText, computed_value: { kind: 'text', value: valueText } }
  }
  if (type === 'b') {
    const value = valueText === '1' || valueText.toLowerCase() === 'true'
    return { raw_input: value ? 'TRUE' : 'FALSE', computed_value: { kind: 'boolean', value } }
  }
  if (type === 'e') {
    const code = SPREADSHEET_ENGINE_ERROR_CODES.includes(valueText as SpreadsheetEngineErrorCode)
      ? valueText as SpreadsheetEngineErrorCode
      : '#VALUE!'
    return { raw_input: valueText, computed_value: { kind: 'error', code } }
  }
  if (valueText.trim() === '') return { raw_input: '', computed_value: { kind: 'blank' } }
  const numericValue = Number(valueText)
  if (Number.isFinite(numericValue)) {
    return { raw_input: valueText, computed_value: { kind: 'number', value: numericValue } }
  }
  return { raw_input: valueText, computed_value: { kind: 'text', value: valueText } }
}

const extractFirstTagText = (xml: string, tagName: string): string | null => {
  const match = new RegExp(`<${tagName}\\b[^>]*>([\\s\\S]*?)<\\/${tagName}>`).exec(xml)
  return match == null ? null : match[1] ?? ''
}

const decodeOptionalXmlText = (value: string | null): string | undefined => (
  value == null ? undefined : decodeXmlText(value).trim()
)

type OoxmlCellFormula = {
  attrs: Record<string, string>
  text: string
}

type OoxmlSharedFormulaBase = {
  row: number
  col: number
  formula: string
}

const FORMULA_METADATA_ATTR_KEYS = [
  't',
  'ref',
  'aca',
  'ca',
  'bx',
  'dt2D',
  'dtr',
  'del1',
  'del2',
  'r1',
  'r2',
] as const

const extractCellFormula = (body: string): OoxmlCellFormula | null => {
  const match = /<f\b([^>]*?)(?:\/>|>([\s\S]*?)<\/f>)/i.exec(body)
  if (match == null) return null
  return {
    attrs: parseAttributes(match[1] ?? ''),
    text: decodeXmlText(match[2] ?? '').trim(),
  }
}

const translateSharedFormula = (formula: string, rowDelta: number, colDelta: number): string => (
  formula.replace(/(^|[^A-Za-z0-9_.$])(\$?)([A-Za-z]{1,3})(\$?)([1-9]\d*)/g, (
    _match,
    prefix: string,
    colAbsolute: string,
    colLetters: string,
    rowAbsolute: string,
    rowDigits: string,
  ) => {
    const sourceCol = columnIndexFromLetters(colLetters)
    const sourceRow = Number(rowDigits) - 1
    const targetCol = colAbsolute ? sourceCol : sourceCol + colDelta
    const targetRow = rowAbsolute ? sourceRow : sourceRow + rowDelta
    if (targetCol < 0 || targetRow < 0) return `${prefix}${colAbsolute}${colLetters}${rowAbsolute}${rowDigits}`
    return `${prefix}${colAbsolute}${columnIndexToName(targetCol)}${rowAbsolute}${targetRow + 1}`
  })
)

const resolveCellFormulaText = (
  formula: OoxmlCellFormula | null,
  cell: { row: number; col: number },
  sharedFormulaBases: Map<string, OoxmlSharedFormulaBase>,
): string | null => {
  if (formula == null) return null
  if (formula.attrs.t !== 'shared') return formula.text
  const sharedIndex = String(formula.attrs.si ?? '').trim()
  if (!sharedIndex) return formula.text || null
  const existingBase = sharedFormulaBases.get(sharedIndex)
  if (formula.text) {
    sharedFormulaBases.set(sharedIndex, {
      row: cell.row,
      col: cell.col,
      formula: formula.text,
    })
    return formula.text
  }
  if (existingBase == null) return null
  return translateSharedFormula(
    existingBase.formula,
    cell.row - existingBase.row,
    cell.col - existingBase.col,
  )
}

const buildCellFormulaMetadata = (formula: OoxmlCellFormula | null): Record<string, unknown> | null => {
  if (formula == null) return null
  const type = String(formula.attrs.t ?? '').trim()
  if (!type || type === 'shared') return null
  const attrs: Record<string, unknown> = {}
  for (const key of FORMULA_METADATA_ATTR_KEYS) {
    const value = String(formula.attrs[key] ?? '').trim()
    if (value) attrs[key] = value
  }
  if (Object.keys(attrs).length === 0) return null
  return compactUnknownRecord({
    type,
    ref: attrs.ref,
    attributes: attrs,
  })
}

const buildCellOoxmlMetadata = (attrs: Record<string, string>): Record<string, unknown> | null => {
  const metadata = compactUnknownRecord({
    cell_metadata_index: attrs.cm,
    value_metadata_index: attrs.vm,
  })
  return Object.keys(metadata).length > 0 ? metadata : null
}

const parseWorksheetCells = (
  xml: string | null,
  sheetId: string,
  sharedStrings: string[],
  styles: Record<string, Record<string, unknown>>,
): Record<string, Record<string, SpreadsheetCellRecord>> => {
  const chunks: Record<string, Record<string, SpreadsheetCellRecord>> = {}
  if (xml == null) return chunks
  const cellEntries = Array.from(xml.matchAll(/<c\b([^>]*)>([\s\S]*?)<\/c>|<c\b([^>]*)\/>/g))
    .map((match) => {
      const attrs = parseAttributes(match[1] ?? match[3] ?? '')
      const cell = parseCellRef(attrs.r ?? '')
      if (cell == null) return null
      return { attrs, cell, body: match[2] ?? '' }
    })
    .filter((entry): entry is { attrs: Record<string, string>; cell: { row: number; col: number }; body: string } => entry != null)
  const sharedFormulaBases = new Map<string, OoxmlSharedFormulaBase>()
  for (const { body, cell } of cellEntries) {
    const formula = extractCellFormula(body)
    const sharedIndex = formula?.attrs.t === 'shared' ? String(formula.attrs.si ?? '').trim() : ''
    if (formula != null && sharedIndex && formula.text) {
      sharedFormulaBases.set(sharedIndex, {
        row: cell.row,
        col: cell.col,
        formula: formula.text,
      })
    }
  }
  for (const { attrs, cell, body } of cellEntries) {
    const formula = extractCellFormula(body)
    const formulaText = resolveCellFormulaText(formula, cell, sharedFormulaBases)
    const inlineText = attrs.t === 'inlineStr'
      ? Array.from(body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)).map((textMatch) => decodeXmlText(textMatch[1] ?? '')).join('')
      : null
    const valueText = inlineText ?? extractFirstTagText(body, 'v')
    const parsed = parseCellValue(attrs.t, valueText, sharedStrings)
    const rawFormula = formulaText == null ? null : `=${formulaText}`
    const styleIndex = attrs.s != null && attrs.s.trim().length > 0 ? Number(attrs.s) : null
    const record: SpreadsheetCellRecord = {
      raw_input: rawFormula ?? parsed.raw_input,
      computed_value: parsed.computed_value,
      revision: 0,
    }
    if (styleIndex != null && Number.isInteger(styleIndex) && styleIndex >= 0) {
      const styleId = `ooxml_style_${styleIndex}`
      const style = styles[styleId]
      if (isProtectionOnlyStyle(style)) {
        if (isPlainObject(style?.protection)) record.protection = style.protection
      } else {
        record.style_id = styleId
      }
    }
    const formulaMetadata = buildCellFormulaMetadata(formula)
    if (formulaMetadata != null) ensureCellRecordMetadata(record).ooxml_formula = formulaMetadata
    const cellOoxmlMetadata = buildCellOoxmlMetadata(attrs)
    if (cellOoxmlMetadata != null) ensureCellRecordMetadata(record).ooxml_cell_metadata = cellOoxmlMetadata
    const chunkId = buildSheetChunkId({
      sheet_id: sheetId,
      row_block: Math.floor(cell.row / SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS),
      col_block: Math.floor(cell.col / SPREADSHEET_ENGINE_SHEET_CHUNK_COLS),
    })
    chunks[chunkId] ??= {}
    chunks[chunkId]![`${cell.row}:${cell.col}`] = record
  }
  normalizeOoxmlDynamicArraySpills(chunks, sheetId)
  return chunks
}

type ChunkCellRecordRef = {
  chunk: Record<string, SpreadsheetCellRecord>
  key: string
  row: number
  col: number
  record: SpreadsheetCellRecord
}

const buildCellRecordRefs = (
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>,
): Map<string, ChunkCellRecordRef> => {
  const refs = new Map<string, ChunkCellRecordRef>()
  for (const chunk of Object.values(chunks)) {
    for (const [key, record] of Object.entries(chunk)) {
      const [rowText, colText] = key.split(':')
      const row = Number(rowText)
      const col = Number(colText)
      if (!Number.isInteger(row) || row < 0 || !Number.isInteger(col) || col < 0) continue
      refs.set(`${row}:${col}`, { chunk, key, row, col, record })
    }
  }
  return refs
}

const ooxmlMetadataIndexKey = (record: SpreadsheetCellRecord): string => {
  const metadata = getCellOoxmlMetadata(record)
  return [
    String(metadata?.cell_metadata_index ?? '').trim(),
    String(metadata?.value_metadata_index ?? '').trim(),
  ].join(':')
}

const rawInputMatchesCachedValue = (rawInput: string, value: SpreadsheetCellValue | null | undefined): boolean => {
  const normalized = String(rawInput ?? '').trim()
  if (!normalized) return true
  if (value == null || value.kind === 'blank') return false
  if (value.kind === 'number') return normalized === String(value.value)
  if (value.kind === 'boolean') return ['1', value.value ? 'TRUE' : 'FALSE'].includes(normalized.toUpperCase())
  if (value.kind === 'text') return normalized === value.value
  if (value.kind === 'error') return normalized === value.code
  return false
}

const isCacheOnlyDynamicArraySpillCell = (
  record: SpreadsheetCellRecord,
  ownerMetadataIndexKey: string,
): boolean => {
  if (getCellFormulaMetadata(record) != null) return false
  if (ooxmlMetadataIndexKey(record) !== ownerMetadataIndexKey) return false
  if (record.style_id || record.validation_id || record.comment || record.hyperlink || record.protection) return false
  return rawInputMatchesCachedValue(record.raw_input, record.computed_value)
}

const normalizeOoxmlDynamicArraySpills = (
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>,
  sheetId: string,
) => {
  const refs = buildCellRecordRefs(chunks)
  for (const owner of refs.values()) {
    const formulaMetadata = getCellFormulaMetadata(owner.record)
    if (String(formulaMetadata?.type ?? '').trim() !== 'array') continue
    const ownerMetadata = getCellOoxmlMetadata(owner.record)
    if (ownerMetadata == null) continue
    const ownerMetadataIndexKey = ooxmlMetadataIndexKey(owner.record)
    if (ownerMetadataIndexKey === ':') continue
    const rangeRef = String(formulaMetadata?.ref ?? '').trim()
    const range = parseA1RangeRef(rangeRef, sheetId)
    if (range == null || range.start_row !== owner.row || range.start_col !== owner.col) continue
    const rows: SpreadsheetCellValue[][] = []
    for (let row = range.start_row; row <= range.end_row; row += 1) {
      const values: SpreadsheetCellValue[] = []
      for (let col = range.start_col; col <= range.end_col; col += 1) {
        const ref = refs.get(`${row}:${col}`)
        values.push(ref?.record.computed_value ?? { kind: 'blank' })
      }
      rows.push(values)
    }
    owner.record.computed_value = { kind: 'dynamic_array', rows }
    for (let row = range.start_row; row <= range.end_row; row += 1) {
      for (let col = range.start_col; col <= range.end_col; col += 1) {
        if (row === owner.row && col === owner.col) continue
        const ref = refs.get(`${row}:${col}`)
        if (ref == null || !isCacheOnlyDynamicArraySpillCell(ref.record, ownerMetadataIndexKey)) continue
        delete ref.chunk[ref.key]
      }
    }
  }
}

const ensureChunkCellRecord = (
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>,
  sheetId: string,
  row: number,
  col: number,
): SpreadsheetCellRecord => {
  const chunkId = buildSheetChunkId({
    sheet_id: sheetId,
    row_block: Math.floor(row / SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS),
    col_block: Math.floor(col / SPREADSHEET_ENGINE_SHEET_CHUNK_COLS),
  })
  chunks[chunkId] ??= {}
  chunks[chunkId]![`${row}:${col}`] ??= { raw_input: '', computed_value: { kind: 'blank' }, revision: 0 }
  return chunks[chunkId]![`${row}:${col}`]!
}

const ensureCellRecordMetadata = (record: SpreadsheetCellRecord): Record<string, unknown> => {
  const metadata = record.metadata && typeof record.metadata === 'object' ? { ...record.metadata } : {}
  record.metadata = metadata
  return metadata
}

const parseMergeRanges = (xml: string | null, sheetId: string): Record<string, SpreadsheetRangeAddress> | undefined => {
  if (xml == null) return undefined
  const mergeRanges: Record<string, SpreadsheetRangeAddress> = {}
  let index = 0
  for (const match of xml.matchAll(/<mergeCell\b[^>]*>/g)) {
    const attrs = parseAttributes(match[0])
    const range = parseA1RangeRef(attrs.ref ?? '', sheetId)
    if (range == null) continue
    mergeRanges[`merge_${index}`] = range
    index += 1
  }
  return Object.keys(mergeRanges).length > 0 ? mergeRanges : undefined
}

const parseFrozenPanes = (xml: string | null): { rows: number; cols: number } | null => {
  if (xml == null) return null
  const paneMatch = /<pane\b[^>]*>/i.exec(xml)
  if (paneMatch == null) return null
  const attrs = parseAttributes(paneMatch[0])
  const state = String(attrs.state ?? '').toLowerCase()
  const rows = Math.max(0, Math.floor(Number(attrs.ySplit ?? 0)))
  const cols = Math.max(0, Math.floor(Number(attrs.xSplit ?? 0)))
  if (!state.includes('frozen') && rows === 0 && cols === 0) return null
  return { rows, cols }
}

const parseSheetTabColor = (
  xml: string | null,
  themeColors: OoxmlThemeColors,
): Record<string, unknown> | undefined => {
  const sheetPr = extractSectionXml(xml, 'sheetPr')
  const attrs = firstElementAttrs(sheetPr, 'tabColor')
  return attrs == null ? undefined : parseOoxmlColorAttrs(attrs, themeColors)
}

const parseSheetFormatProperties = (
  xml: string | null,
): SpreadsheetWorkbookSnapshot['sheets'][number]['sheet_format_properties'] | undefined => {
  if (xml == null) return undefined
  const sheetFormatAttrs = firstElementAttrs(xml, 'sheetFormatPr')
  const sheetPr = extractSectionXml(xml, 'sheetPr')
  const outlineAttrs = firstElementAttrs(sheetPr, 'outlinePr')
  const numberAttr = (attrs: Record<string, string> | null | undefined, name: string): number | undefined => {
    const value = Number(attrs?.[name])
    return Number.isFinite(value) && value >= 0 ? value : undefined
  }
  const intAttr = (attrs: Record<string, string> | null | undefined, name: string): number | undefined => {
    const value = Number(attrs?.[name])
    return Number.isInteger(value) && value >= 0 ? value : undefined
  }
  const properties = compactUnknownRecord({
    default_row_height_px: (() => {
      const height = numberAttr(sheetFormatAttrs, 'defaultRowHeight')
      return height == null ? undefined : Math.round(height * (4 / 3))
    })(),
    default_col_width_px: (() => {
      const width = numberAttr(sheetFormatAttrs, 'defaultColWidth')
      return width == null ? undefined : Math.round(width * 7)
    })(),
    base_col_width: intAttr(sheetFormatAttrs, 'baseColWidth'),
    zero_height: parseOoxmlBoolean(sheetFormatAttrs?.zeroHeight),
    thick_top: parseOoxmlBoolean(sheetFormatAttrs?.thickTop),
    thick_bottom: parseOoxmlBoolean(sheetFormatAttrs?.thickBottom),
    outline_level_row: intAttr(sheetFormatAttrs, 'outlineLevelRow'),
    outline_level_col: intAttr(sheetFormatAttrs, 'outlineLevelCol'),
    outline_summary_below: parseOoxmlBoolean(outlineAttrs?.summaryBelow),
    outline_summary_right: parseOoxmlBoolean(outlineAttrs?.summaryRight),
    show_outline_symbols: parseOoxmlBoolean(outlineAttrs?.showOutlineSymbols),
    apply_outline_styles: parseOoxmlBoolean(outlineAttrs?.applyStyles),
  })
  return Object.keys(properties).length > 0 ? properties : undefined
}

const parseSheetCalculationProperties = (
  xml: string | null,
): SpreadsheetSheetCalculationProperties | undefined => {
  if (xml == null) return undefined
  const attrs = firstElementAttrs(xml, 'sheetCalcPr')
  if (attrs == null) return undefined
  const properties = compactUnknownRecord({
    full_calc_on_load: parseOoxmlBoolean(attrs.fullCalcOnLoad),
  })
  return Object.keys(properties).length > 0 ? properties : undefined
}

const parseWorksheetOoxmlMetadata = (
  xml: string | null,
): SpreadsheetWorkbookSnapshot['sheets'][number]['ooxml_sheet_metadata'] | undefined => {
  if (xml == null) return undefined
  const optionalAttributes = (attrs: Record<string, string>): Record<string, string> | undefined => (
    Object.keys(attrs).length > 0 ? attrs : undefined
  )
  const phoneticRaw = firstRawElementXml(xml, 'phoneticPr')
  const smartTagsRaw = firstRawElementXml(xml, 'smartTags')
  const scenariosRaw = firstRawElementXml(xml, 'scenarios')
  const extensionListRaw = firstRawElementXml(xml, 'extLst')
  const extensionEntries = extensionListRaw == null
    ? []
    : parseElementEntries(extractSectionXml(extensionListRaw, 'extLst'), 'ext')

  const metadata = compactUnknownRecord({
    phonetic_properties: phoneticRaw == null ? undefined : {
      raw_xml: phoneticRaw,
      attributes: optionalAttributes(parseAttributes(phoneticRaw)),
    },
    smart_tags: smartTagsRaw == null ? undefined : {
      raw_xml: smartTagsRaw,
      attributes: optionalAttributes(firstElementAttrs(smartTagsRaw, 'smartTags') ?? {}),
    },
    scenarios: scenariosRaw == null ? undefined : {
      raw_xml: scenariosRaw,
      attributes: optionalAttributes(firstElementAttrs(scenariosRaw, 'scenarios') ?? {}),
    },
    extension_list: extensionListRaw == null ? undefined : {
      raw_xml: extensionListRaw,
      extensions: extensionEntries.map((entry) => ({
        uri: entry.attrs.uri ?? null,
        raw_xml: entry.raw,
      })),
    },
  })
  return Object.keys(metadata).length > 0 ? metadata : undefined
}

const parseSheetViews = (xml: string | null): NonNullable<SpreadsheetWorkbookSnapshot['sheets'][number]['sheet_views']> | undefined => {
  const sheetViewsXml = extractSectionXml(xml, 'sheetViews')
  const views = parseElementEntries(sheetViewsXml, 'sheetView')
    .map((entry) => {
      const attrs = entry.attrs
      const intAttr = (name: string): number | undefined => {
        const value = Number(attrs[name])
        return Number.isInteger(value) ? value : undefined
      }
      const positiveIntAttr = (name: string): number | undefined => {
        const value = intAttr(name)
        return value != null && value > 0 ? value : undefined
      }
      const selectionAttrs = firstElementAttrs(entry.body, 'selection')
      const view = compactUnknownRecord({
        workbook_view_id: intAttr('workbookViewId'),
        show_grid_lines: parseOoxmlBoolean(attrs.showGridLines),
        show_row_col_headers: parseOoxmlBoolean(attrs.showRowColHeaders),
        show_formulas: parseOoxmlBoolean(attrs.showFormulas),
        right_to_left: parseOoxmlBoolean(attrs.rightToLeft),
        tab_selected: parseOoxmlBoolean(attrs.tabSelected),
        zoom_scale: positiveIntAttr('zoomScale'),
        zoom_scale_normal: positiveIntAttr('zoomScaleNormal'),
        top_left_cell: attrs.topLeftCell,
        active_cell: selectionAttrs?.activeCell,
        selection_sqref: selectionAttrs?.sqref,
      })
      return Object.keys(view).length > 0 ? view : null
    })
    .filter((view): view is NonNullable<SpreadsheetWorkbookSnapshot['sheets'][number]['sheet_views']>[number] => view != null)
  return views.length > 0 ? views : undefined
}

const parseCustomSheetViews = (xml: string | null): SpreadsheetSheetCustomView[] | undefined => {
  const customSheetViewsXml = extractSectionXml(xml, 'customSheetViews')
  const views = parseElementEntries(customSheetViewsXml, 'customSheetView')
    .map((entry) => {
      const attrs = entry.attrs
      const intAttr = (name: string): number | undefined => {
        const value = Number(attrs[name])
        return Number.isInteger(value) ? value : undefined
      }
      const positiveIntAttr = (name: string): number | undefined => {
        const value = intAttr(name)
        return value != null && value > 0 ? value : undefined
      }
      const selectionAttrs = firstElementAttrs(entry.body, 'selection')
      const view = compactUnknownRecord({
        guid: attrs.guid,
        name: attrs.name,
        scale: positiveIntAttr('scale'),
        color_id: intAttr('colorId'),
        show_page_breaks: parseOoxmlBoolean(attrs.showPageBreaks),
        show_formulas: parseOoxmlBoolean(attrs.showFormulas),
        show_grid_lines: parseOoxmlBoolean(attrs.showGridLines),
        show_row_col_headers: parseOoxmlBoolean(attrs.showRowCol),
        outline_symbols: parseOoxmlBoolean(attrs.outlineSymbols),
        zero_values: parseOoxmlBoolean(attrs.zeroValues),
        fit_to_page: parseOoxmlBoolean(attrs.fitToPage),
        print_area: parseOoxmlBoolean(attrs.printArea),
        filter: parseOoxmlBoolean(attrs.filter),
        hidden_rows: parseOoxmlBoolean(attrs.hiddenRows),
        hidden_columns: parseOoxmlBoolean(attrs.hiddenColumns),
        state: attrs.state,
        top_left_cell: attrs.topLeftCell,
        active_cell: selectionAttrs?.activeCell,
        selection_sqref: selectionAttrs?.sqref,
      })
      return Object.keys(view).length > 0 ? view as SpreadsheetSheetCustomView : null
    })
    .filter((view): view is SpreadsheetSheetCustomView => view != null)
  return views.length > 0 ? views : undefined
}

const parseSheetPrintSettings = (
  xml: string | null,
): SpreadsheetWorkbookSnapshot['sheets'][number]['print_settings'] | undefined => {
  if (xml == null) return undefined
  const numberAttr = (attrs: Record<string, string>, name: string): number | undefined => {
    const value = Number(attrs[name])
    return Number.isFinite(value) && value >= 0 ? value : undefined
  }
  const intAttr = (attrs: Record<string, string>, name: string): number | undefined => {
    const value = Number(attrs[name])
    return Number.isInteger(value) && value >= 0 ? value : undefined
  }
  const parseBreaks = (sectionName: 'rowBreaks' | 'colBreaks'): NonNullable<NonNullable<SpreadsheetWorkbookSnapshot['sheets'][number]['print_settings']>['page_breaks']>['rows'] => {
    const sectionXml = extractSectionXml(xml, sectionName)
    const breaks = parseElementEntries(sectionXml, 'brk')
      .map(({ attrs }) => {
        const id = intAttr(attrs, 'id')
        if (id == null) return null
        return compactUnknownRecord({
          id,
          min: intAttr(attrs, 'min'),
          max: intAttr(attrs, 'max'),
          manual: parseOoxmlBoolean(attrs.man),
        })
      })
      .filter((entry): entry is { id: number; min?: number; max?: number; manual?: boolean } => entry != null)
    return breaks.length > 0 ? breaks : undefined
  }
  const printOptionsAttrs = firstElementAttrs(xml, 'printOptions')
  const printOptions = printOptionsAttrs == null ? undefined : compactUnknownRecord({
    horizontal_centered: parseOoxmlBoolean(printOptionsAttrs.horizontalCentered),
    vertical_centered: parseOoxmlBoolean(printOptionsAttrs.verticalCentered),
    headings: parseOoxmlBoolean(printOptionsAttrs.headings),
    grid_lines: parseOoxmlBoolean(printOptionsAttrs.gridLines),
    grid_lines_set: parseOoxmlBoolean(printOptionsAttrs.gridLinesSet),
  })
  const pageMarginsAttrs = firstElementAttrs(xml, 'pageMargins')
  const pageMargins = pageMarginsAttrs == null ? undefined : compactUnknownRecord({
    left: numberAttr(pageMarginsAttrs, 'left'),
    right: numberAttr(pageMarginsAttrs, 'right'),
    top: numberAttr(pageMarginsAttrs, 'top'),
    bottom: numberAttr(pageMarginsAttrs, 'bottom'),
    header: numberAttr(pageMarginsAttrs, 'header'),
    footer: numberAttr(pageMarginsAttrs, 'footer'),
  })
  const pageSetupElementAttrs = firstElementAttrs(xml, 'pageSetup')
  const pageSetupPropertiesAttrs = firstElementAttrs(extractSectionXml(xml, 'sheetPr'), 'pageSetUpPr')
  const pageSetupAttrs = pageSetupElementAttrs ?? {}
  const pageSetup = pageSetupElementAttrs == null && pageSetupPropertiesAttrs == null ? undefined : compactUnknownRecord({
    fit_to_page: parseOoxmlBoolean(pageSetupPropertiesAttrs?.fitToPage),
    paper_size: numberAttr(pageSetupAttrs, 'paperSize'),
    scale: numberAttr(pageSetupAttrs, 'scale'),
    fit_to_width: numberAttr(pageSetupAttrs, 'fitToWidth'),
    fit_to_height: numberAttr(pageSetupAttrs, 'fitToHeight'),
    orientation: pageSetupAttrs.orientation === 'portrait' || pageSetupAttrs.orientation === 'landscape'
      ? pageSetupAttrs.orientation
      : undefined,
    page_order: pageSetupAttrs.pageOrder === 'downThenOver' || pageSetupAttrs.pageOrder === 'overThenDown'
      ? pageSetupAttrs.pageOrder
      : undefined,
    horizontal_dpi: numberAttr(pageSetupAttrs, 'horizontalDpi'),
    vertical_dpi: numberAttr(pageSetupAttrs, 'verticalDpi'),
    first_page_number: numberAttr(pageSetupAttrs, 'firstPageNumber'),
    use_first_page_number: parseOoxmlBoolean(pageSetupAttrs.useFirstPageNumber),
    black_and_white: parseOoxmlBoolean(pageSetupAttrs.blackAndWhite),
    draft: parseOoxmlBoolean(pageSetupAttrs.draft),
    cell_comments: pageSetupAttrs.cellComments,
    errors: pageSetupAttrs.errors,
  })
  const headerFooterXml = extractSectionXml(xml, 'headerFooter')
  const headerFooterAttrs = firstElementAttrs(xml, 'headerFooter')
  const headerFooter = headerFooterAttrs == null && !headerFooterXml
    ? undefined
    : compactUnknownRecord({
      different_odd_even: parseOoxmlBoolean(headerFooterAttrs?.differentOddEven),
      different_first: parseOoxmlBoolean(headerFooterAttrs?.differentFirst),
      scale_with_doc: parseOoxmlBoolean(headerFooterAttrs?.scaleWithDoc),
      align_with_margins: parseOoxmlBoolean(headerFooterAttrs?.alignWithMargins),
      odd_header: decodeOptionalXmlText(extractFirstTagText(headerFooterXml, 'oddHeader')),
      odd_footer: decodeOptionalXmlText(extractFirstTagText(headerFooterXml, 'oddFooter')),
      even_header: decodeOptionalXmlText(extractFirstTagText(headerFooterXml, 'evenHeader')),
      even_footer: decodeOptionalXmlText(extractFirstTagText(headerFooterXml, 'evenFooter')),
      first_header: decodeOptionalXmlText(extractFirstTagText(headerFooterXml, 'firstHeader')),
      first_footer: decodeOptionalXmlText(extractFirstTagText(headerFooterXml, 'firstFooter')),
    })
  const pageBreaks = compactUnknownRecord({
    rows: parseBreaks('rowBreaks'),
    columns: parseBreaks('colBreaks'),
  })
  const settings = compactUnknownRecord({
    print_options: printOptions,
    page_margins: pageMargins,
    page_setup: pageSetup,
    header_footer: headerFooter,
    page_breaks: Object.keys(pageBreaks).length > 0 ? pageBreaks : undefined,
  })
  return Object.keys(settings).length > 0 ? settings : undefined
}

const parseIgnoredErrors = (xml: string | null): SpreadsheetSheetIgnoredError[] | undefined => {
  const ignoredErrorsXml = extractSectionXml(xml, 'ignoredErrors')
  const entries = parseElementEntries(ignoredErrorsXml, 'ignoredError')
    .map(({ attrs }) => {
      const range = String(attrs.sqref ?? '').trim()
      if (!range) return null
      const entry = compactUnknownRecord({
        range,
        eval_error: parseOoxmlBoolean(attrs.evalError),
        two_digit_text_year: parseOoxmlBoolean(attrs.twoDigitTextYear),
        number_stored_as_text: parseOoxmlBoolean(attrs.numberStoredAsText),
        formula: parseOoxmlBoolean(attrs.formula),
        formula_range: parseOoxmlBoolean(attrs.formulaRange),
        unlocked_formula: parseOoxmlBoolean(attrs.unlockedFormula),
        empty_cell_reference: parseOoxmlBoolean(attrs.emptyCellReference),
        list_data_validation: parseOoxmlBoolean(attrs.listDataValidation),
        calculated_column: parseOoxmlBoolean(attrs.calculatedColumn),
      })
      return entry as SpreadsheetSheetIgnoredError
    })
    .filter((entry): entry is SpreadsheetSheetIgnoredError => entry != null)
  return entries.length > 0 ? entries : undefined
}

const parseRowMetadata = (xml: string | null): Record<string, Record<string, unknown>> | undefined => {
  if (xml == null) return undefined
  const rows: Record<string, Record<string, unknown>> = {}
  for (const match of xml.matchAll(/<row\b[^>]*>/g)) {
    const attrs = parseAttributes(match[0])
    const rowNumber = Number(attrs.r)
    if (!Number.isInteger(rowNumber) || rowNumber <= 0) continue
    const metadata: Record<string, unknown> = {}
    const height = Number(attrs.ht)
    if (Number.isFinite(height) && height > 0) metadata.height_px = Math.round(height * (4 / 3))
    if (attrs.hidden === '1' || attrs.hidden === 'true') metadata.hidden = true
    const styleIndex = Number(attrs.s)
    if (Number.isInteger(styleIndex) && styleIndex >= 0) metadata.style_id = `ooxml_style_${styleIndex}`
    const outlineLevel = Number(attrs.outlineLevel)
    if (Number.isInteger(outlineLevel) && outlineLevel >= 0) metadata.outline_level = outlineLevel
    if (attrs.collapsed === '1' || attrs.collapsed === 'true') metadata.collapsed = true
    if (Object.keys(metadata).length > 0) rows[String(rowNumber - 1)] = metadata
  }
  return Object.keys(rows).length > 0 ? rows : undefined
}

const parseColumnMetadata = (xml: string | null): Record<string, Record<string, unknown>> | undefined => {
  if (xml == null) return undefined
  const columns: Record<string, Record<string, unknown>> = {}
  for (const match of xml.matchAll(/<col\b[^>]*>/g)) {
    const attrs = parseAttributes(match[0])
    const min = Number(attrs.min)
    const max = Number(attrs.max)
    if (!Number.isInteger(min) || !Number.isInteger(max) || min <= 0 || max < min) continue
    const metadata: Record<string, unknown> = {}
    const width = Number(attrs.width)
    if (Number.isFinite(width) && width > 0) metadata.width_px = Math.round(width * 7)
    if (attrs.hidden === '1' || attrs.hidden === 'true') metadata.hidden = true
    const styleIndex = Number(attrs.style)
    if (Number.isInteger(styleIndex) && styleIndex >= 0) metadata.style_id = `ooxml_style_${styleIndex}`
    const outlineLevel = Number(attrs.outlineLevel)
    if (Number.isInteger(outlineLevel) && outlineLevel >= 0) metadata.outline_level = outlineLevel
    if (attrs.collapsed === '1' || attrs.collapsed === 'true') metadata.collapsed = true
    if (Object.keys(metadata).length === 0) continue
    for (let col = min - 1; col <= max - 1; col += 1) columns[String(col)] = { ...metadata }
  }
  return Object.keys(columns).length > 0 ? columns : undefined
}

const ooxmlAutoFilterOperatorToProtocol = (operator: string | undefined): SpreadsheetAutoFilterOperator => {
  if (operator === 'notEqual') return 'not_equals'
  if (operator === 'greaterThan') return 'greater_than'
  if (operator === 'greaterThanOrEqual') return 'greater_than_or_equal'
  if (operator === 'lessThan') return 'less_than'
  if (operator === 'lessThanOrEqual') return 'less_than_or_equal'
  return 'equals'
}

const parseWorksheetAutoFilter = (
  xml: string | null,
  sheetId: string,
): SpreadsheetAutoFilter | null => {
  if (xml == null) return null
  const match = /<autoFilter\b([^>]*?)(?:\/>|>([\s\S]*?)<\/autoFilter>)/i.exec(xml)
  if (match == null) return null
  const attrs = parseAttributes(match[1] ?? '')
  const body = match[2] ?? ''
  const range = parseA1RangeRef(attrs.ref ?? '', sheetId)
  if (range == null) return null
  const columns: SpreadsheetAutoFilterColumnCriteria[] = []
  for (const columnMatch of body.matchAll(/<filterColumn\b([^>]*?)(?:\/>|>([\s\S]*?)<\/filterColumn>)/gi)) {
    const columnAttrs = parseAttributes(columnMatch[1] ?? '')
    const colOffset = Number(columnAttrs.colId)
    if (!Number.isInteger(colOffset) || colOffset < 0) continue
    const body = columnMatch[2] ?? ''
    const filterAttrs = firstElementAttrs(body, 'filters') ?? {}
    const values = Array.from(body.matchAll(/<filter\b([^>]*?)\/>/gi))
      .map((filterMatch) => parseAttributes(filterMatch[1] ?? '').val)
      .filter((value): value is string => typeof value === 'string')
    const customFiltersMatch = /<customFilters\b([^>]*?)(?:\/>|>([\s\S]*?)<\/customFilters>)/i.exec(body)
    const customFiltersAttrs = parseAttributes(customFiltersMatch?.[1] ?? '')
    const conditions = Array.from((customFiltersMatch?.[2] ?? '').matchAll(/<customFilter\b([^>]*?)\/>/gi))
      .map((conditionMatch) => {
        const conditionAttrs = parseAttributes(conditionMatch[1] ?? '')
        return typeof conditionAttrs.val === 'string'
          ? { operator: ooxmlAutoFilterOperatorToProtocol(conditionAttrs.operator), value: conditionAttrs.val }
          : null
      })
      .filter((condition): condition is { operator: SpreadsheetAutoFilterOperator; value: string } => condition != null)
    const criteria = compactUnknownRecord({
      col: range.start_col + colOffset,
      values: values.length > 0 ? values : undefined,
      blank: filterAttrs.blank === '1' || filterAttrs.blank === 'true' ? true : undefined,
      conditions: conditions.length > 0 ? conditions : undefined,
      condition_join: conditions.length > 1 ? customFiltersAttrs.and === '1' || customFiltersAttrs.and === 'true' ? 'and' : 'or' : undefined,
    }) as unknown as SpreadsheetAutoFilterColumnCriteria
    columns.push(criteria)
  }
  return {
    range,
    columns: columns.length > 0 ? columns.sort((left, right) => left.col - right.col) : undefined,
    sort_state: parseAutoFilterSortState(body, sheetId, range) ?? undefined,
  }
}

const parseAutoFilterSortState = (
  body: string,
  sheetId: string,
  fallbackRange: SpreadsheetRangeAddress,
): SpreadsheetAutoFilterSortState | null => {
  const match = /<sortState\b([^>]*?)(?:\/>|>([\s\S]*?)<\/sortState>)/i.exec(body)
  if (match == null) return null
  const attrs = parseAttributes(match[1] ?? '')
  const conditions = Array.from((match[2] ?? '').matchAll(/<sortCondition\b([^>]*?)\/>/gi))
    .map((conditionMatch): SpreadsheetAutoFilterSortCondition | null => {
      const conditionAttrs = parseAttributes(conditionMatch[1] ?? '')
      const ref = parseA1RangeRef(conditionAttrs.ref ?? '', sheetId)
      if (ref == null) return null
      return compactUnknownRecord({
        ref,
        descending: conditionAttrs.descending === '1' || conditionAttrs.descending === 'true' ? true : undefined,
        sort_by: conditionAttrs.sortBy,
        custom_list: conditionAttrs.customList,
        dxf_id: conditionAttrs.dxfId,
        icon_set: conditionAttrs.iconSet,
        icon_id: conditionAttrs.iconId,
      }) as unknown as SpreadsheetAutoFilterSortCondition
    })
    .filter((condition): condition is SpreadsheetAutoFilterSortCondition => condition != null)
  return compactUnknownRecord({
    ref: parseA1RangeRef(attrs.ref ?? '', sheetId) ?? fallbackRange,
    case_sensitive: attrs.caseSensitive === '1' || attrs.caseSensitive === 'true' ? true : undefined,
    column_sort: attrs.columnSort === '1' || attrs.columnSort === 'true' ? true : undefined,
    conditions: conditions.length > 0 ? conditions : undefined,
  }) as unknown as SpreadsheetAutoFilterSortState
}

const parseTableDefinition = (
  xml: string | null,
  sheetId: string,
  fallbackTableId: string,
): SpreadsheetTableDefinition | null => {
  if (xml == null) return null
  const tableMatch = /<table\b[^>]*>/i.exec(xml)
  if (tableMatch == null) return null
  const attrs = parseAttributes(tableMatch[0])
  const styleInfoMatch = /<tableStyleInfo\b[^>]*>/i.exec(xml)
  const styleAttrs = styleInfoMatch == null ? {} : parseAttributes(styleInfoMatch[0])
  const name = String(attrs.displayName ?? attrs.name ?? '').trim()
  const range = parseA1RangeRef(attrs.ref ?? '', sheetId)
  if (!name || range == null) return null
  const tableAutoFilter = parseWorksheetAutoFilter(xml, sheetId)
  const columns = Array.from(xml.matchAll(/<tableColumn\b([^>]*?)(?:\/>|>([\s\S]*?)<\/tableColumn>)/gi))
    .map((match) => ({
      attrs: parseAttributes(match[1] ?? ''),
      body: match[2] ?? '',
    }))
    .map(({ attrs: column, body }) => {
      const columnDefinition: SpreadsheetTableColumnDefinition = {
        id: column.id ?? null,
        name: column.name ?? null,
      }
      if (column.totalsRowLabel != null) columnDefinition.totals_row_label = column.totalsRowLabel
      if (column.totalsRowFunction != null) columnDefinition.totals_row_function = column.totalsRowFunction
      const totalsRowFormula = extractFirstTagText(body, 'totalsRowFormula')
      const calculatedColumnFormula = extractFirstTagText(body, 'calculatedColumnFormula')
      if (totalsRowFormula != null) columnDefinition.totals_row_formula = decodeXmlText(totalsRowFormula).trim()
      if (calculatedColumnFormula != null) {
        columnDefinition.calculated_column_formula = decodeXmlText(calculatedColumnFormula).trim()
      }
      return columnDefinition
    })
  return {
    id: String(attrs.id ?? fallbackTableId).trim() || fallbackTableId,
    name,
    range,
    has_headers: Number(attrs.headerRowCount ?? 1) !== 0,
    show_filter_buttons: true,
    show_totals: Number(attrs.totalsRowCount ?? 0) > 0,
    style_id: attrs.tableStyleName ?? styleAttrs.name ?? null,
    show_first_column: styleAttrs.showFirstColumn === '1' || styleAttrs.showFirstColumn === 'true' ? true : undefined,
    show_last_column: styleAttrs.showLastColumn === '1' || styleAttrs.showLastColumn === 'true' ? true : undefined,
    show_row_stripes: styleAttrs.showRowStripes === '0' || styleAttrs.showRowStripes === 'false' ? false : undefined,
    show_column_stripes: styleAttrs.showColumnStripes === '1' || styleAttrs.showColumnStripes === 'true' ? true : undefined,
    columns: columns.length > 0 ? columns : undefined,
    auto_filter: tableAutoFilter ?? undefined,
  }
}

const parseWorksheetTables = (
  files: Record<string, Uint8Array>,
  worksheetPartName: string,
  sheetId: string,
): Record<string, SpreadsheetTableDefinition> | undefined => {
  const relationships = parseRelationships(readXml(files, relationshipsPartNameFor(worksheetPartName)))
    .filter((relationship) => relationship.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/table')
  const tables: Record<string, SpreadsheetTableDefinition> = {}
  relationships.forEach((relationship, index) => {
    const tablePartName = resolveRelationshipTarget(worksheetPartName, relationship.target)
    const fallbackTableId = `table_${index + 1}`
    const table = parseTableDefinition(readXml(files, tablePartName), sheetId, fallbackTableId)
    if (table == null) return
    tables[table.id] = table
  })
  return Object.keys(tables).length > 0 ? tables : undefined
}

const parseDataValidationKind = (type: string | undefined): SpreadsheetValidationRule['kind'] => {
  if (type === 'whole') return 'wholeNumber'
  if (type === 'decimal') return 'decimal'
  if (type === 'date') return 'date'
  if (type === 'time') return 'time'
  if (type === 'textLength') return 'textLength'
  if (type === 'custom') return 'custom'
  return 'list'
}

const parseDataValidationOperator = (operator: string | undefined): SpreadsheetValidationRule['operator'] => {
  if (operator === 'notBetween') return 'notBetween'
  if (operator === 'equal') return 'equal'
  if (operator === 'notEqual') return 'notEqual'
  if (operator === 'greaterThan') return 'greaterThan'
  if (operator === 'lessThan') return 'lessThan'
  if (operator === 'greaterThanOrEqual') return 'greaterThanOrEqual'
  if (operator === 'lessThanOrEqual') return 'lessThanOrEqual'
  return 'between'
}

const parseDataValidationErrorStyle = (errorStyle: string | undefined): SpreadsheetValidationRule['error_style'] => {
  if (errorStyle === 'warning') return 'warning'
  if (errorStyle === 'information') return 'information'
  return 'stop'
}

const parseDataValidations = (
  xml: string | null,
  sheetId: string,
): {
  validation_rules: Record<string, SpreadsheetValidationRule>
  ranges: Array<{ range: SpreadsheetRangeAddress; validation_id: string }>
} => {
  const validation_rules: Record<string, SpreadsheetValidationRule> = {}
  const ranges: Array<{ range: SpreadsheetRangeAddress; validation_id: string }> = []
  if (xml == null) return { validation_rules, ranges }
  for (const match of xml.matchAll(/<dataValidation\b([^>]*?)(?:\/>|>([\s\S]*?)<\/dataValidation>)/g)) {
    const attrs = parseAttributes(match[1] ?? '')
    const body = match[2] ?? ''
    const formula1 = decodeXmlText(extractFirstTagText(body, 'formula1') ?? '').trim()
    const formula2 = decodeXmlText(extractFirstTagText(body, 'formula2') ?? '').trim()
    const kind = parseDataValidationKind(attrs.type)
    const quotedListMatch = /^"(.*)"$/.exec(formula1)
    const rule = canonicalizeSpreadsheetValidationRule({
      kind,
      operator: kind === 'list' || kind === 'custom' ? null : parseDataValidationOperator(attrs.operator),
      allow_blank: attrs.allowBlank === '1' || attrs.allowBlank === 'true',
      values: kind === 'list' && quotedListMatch ? quotedListMatch[1]!.split(',').map((value) => value.trim()) : null,
      formula1: kind === 'list' && quotedListMatch ? null : formula1,
      formula2,
      input_title: attrs.promptTitle ?? null,
      input_message: attrs.prompt ?? null,
      error_style: parseDataValidationErrorStyle(attrs.errorStyle),
      error_title: attrs.errorTitle ?? null,
      error_message: attrs.error ?? null,
    })
    validation_rules[rule.id] = rule
    for (const rangeText of String(attrs.sqref ?? '').split(/\s+/).filter(Boolean)) {
      const range = parseA1RangeRef(rangeText, sheetId)
      if (range != null) ranges.push({ range, validation_id: rule.id })
    }
  }
  return { validation_rules, ranges }
}

const applyValidationRangesToChunks = (
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>,
  sheetId: string,
  ranges: Array<{ range: SpreadsheetRangeAddress; validation_id: string }>,
): void => {
  for (const { range, validation_id } of ranges) {
    for (let row = range.start_row; row <= range.end_row; row += 1) {
      for (let col = range.start_col; col <= range.end_col; col += 1) {
        ensureChunkCellRecord(chunks, sheetId, row, col).validation_id = validation_id
      }
    }
  }
}

const parseSheetProtection = (xml: string | null): SpreadsheetSheetProtectionOptions | null => {
  if (xml == null) return null
  const match = /<sheetProtection\b[^>]*>/i.exec(xml)
  if (match == null) return null
  const attrs = parseAttributes(match[0])
  const enabled = attrs.sheet === '1' || attrs.sheet === 'true' || Object.keys(attrs).length > 0
  if (!enabled) return null
  return {
    enabled: true,
    allow_select_locked_cells: attrs.selectLockedCells === '1' || attrs.selectLockedCells === 'true',
    allow_select_unlocked_cells: attrs.selectUnlockedCells === '1' || attrs.selectUnlockedCells === 'true',
    allow_format_cells: attrs.formatCells === '0' || attrs.formatCells === 'false',
    allow_resize_rows: attrs.formatRows === '0' || attrs.formatRows === 'false',
    allow_resize_columns: attrs.formatColumns === '0' || attrs.formatColumns === 'false',
    allow_insert_rows: attrs.insertRows === '0' || attrs.insertRows === 'false',
    allow_insert_columns: attrs.insertColumns === '0' || attrs.insertColumns === 'false',
    allow_delete_rows: attrs.deleteRows === '0' || attrs.deleteRows === 'false',
    allow_delete_columns: attrs.deleteColumns === '0' || attrs.deleteColumns === 'false',
    ooxml_algorithm: attrs.algorithmName ?? null,
    ooxml_hash_value: attrs.hashValue ?? null,
    ooxml_salt_value: attrs.saltValue ?? null,
    ooxml_spin_count: attrs.spinCount ?? null,
  }
}

const parseProtectedRanges = (xml: string | null): SpreadsheetSheetProtectedRange[] | undefined => {
  const protectedRangesXml = extractSectionXml(xml, 'protectedRanges')
  const ranges = parseElementEntries(protectedRangesXml, 'protectedRange')
    .map(({ attrs }) => {
      const range = String(attrs.sqref ?? '').trim()
      if (!range) return null
      return compactUnknownRecord({
        name: attrs.name,
        range,
        security_descriptor: attrs.securityDescriptor,
        password_hash: attrs.password,
        algorithm: attrs.algorithmName,
        hash_value: attrs.hashValue,
        salt_value: attrs.saltValue,
        spin_count: attrs.spinCount,
      }) as SpreadsheetSheetProtectedRange
    })
    .filter((entry): entry is SpreadsheetSheetProtectedRange => entry != null)
  return ranges.length > 0 ? ranges : undefined
}

const parseRichText = (xml: string): string => {
  const textRuns = Array.from(xml.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g))
    .map((match) => decodeXmlText(match[1] ?? ''))
  if (textRuns.length > 0) return textRuns.join('')
  return decodeXmlText(xml.replace(/<[^>]+>/g, ''))
}

const extractFirstLocalTagText = (xml: string, localName: string): string | null => {
  const match = new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${localName}\\b[^>]*>([\\s\\S]*?)<\\/(?:[A-Za-z_][\\w.-]*:)?${localName}>`, 'i').exec(xml)
  return match == null ? null : match[1] ?? ''
}

const hasLocalTag = (xml: string, localName: string): boolean => (
  new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${localName}\\b`, 'i').test(xml)
)

const parseCommentGeometryByCell = (
  files: Record<string, Uint8Array>,
  worksheetPartName: string,
): Map<string, Record<string, unknown>> => {
  const relationships = parseRelationships(readXml(files, relationshipsPartNameFor(worksheetPartName)))
  const vmlRelationship = relationships.find((relationship) => (
    relationship.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/vmlDrawing'
  ))
  if (vmlRelationship == null) return new Map()
  const vmlXml = readXml(files, resolveRelationshipTarget(worksheetPartName, vmlRelationship.target))
  if (vmlXml == null) return new Map()
  const geometryByCell = new Map<string, Record<string, unknown>>()
  for (const match of vmlXml.matchAll(/<(?:[A-Za-z_][\w.-]*:)?shape\b([^>]*)>([\s\S]*?)<\/(?:[A-Za-z_][\w.-]*:)?shape>/g)) {
    const shapeAttrs = parseAttributes(match[1] ?? '')
    const body = match[2] ?? ''
    const row = Number(extractFirstLocalTagText(body, 'Row'))
    const col = Number(extractFirstLocalTagText(body, 'Column'))
    if (!Number.isInteger(row) || row < 0 || !Number.isInteger(col) || col < 0) continue
    const anchorText = extractFirstLocalTagText(body, 'Anchor')?.trim()
    const shape_anchor = anchorText == null || anchorText.length === 0
      ? undefined
      : anchorText.split(',').map((piece) => Number(piece.trim())).filter((value) => Number.isFinite(value))
    geometryByCell.set(`${row}:${col}`, compactUnknownRecord({
      visible: hasLocalTag(body, 'Visible'),
      shape_anchor: shape_anchor == null || shape_anchor.length === 0 ? undefined : shape_anchor,
      shape_style: shapeAttrs.style,
      fill_color: shapeAttrs.fillcolor,
      shape_id: shapeAttrs.id,
      move_with_cells: hasLocalTag(body, 'MoveWithCells'),
      size_with_cells: hasLocalTag(body, 'SizeWithCells'),
    }))
  }
  return geometryByCell
}

const applyCommentsToChunks = (
  files: Record<string, Uint8Array>,
  worksheetPartName: string,
  sheetId: string,
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>,
): void => {
  const relationships = parseRelationships(readXml(files, relationshipsPartNameFor(worksheetPartName)))
  const commentsRelationship = relationships
    .find((relationship) => relationship.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments')
  if (commentsRelationship == null) return
  const commentsXml = readXml(files, resolveRelationshipTarget(worksheetPartName, commentsRelationship.target))
  if (commentsXml == null) return
  const commentGeometryByCell = parseCommentGeometryByCell(files, worksheetPartName)
  const authors = Array.from(commentsXml.matchAll(/<author\b[^>]*>([\s\S]*?)<\/author>/g))
    .map((match) => decodeXmlText(match[1] ?? '').trim())
  for (const match of commentsXml.matchAll(/<comment\b([^>]*)>([\s\S]*?)<\/comment>/g)) {
    const attrs = parseAttributes(match[1] ?? '')
    const cell = parseCellRef(attrs.ref ?? '')
    if (cell == null) continue
    const text = parseRichText(match[2] ?? '').trim()
    if (!text) continue
    const author = authors[Number(attrs.authorId)] ?? ''
    const record = ensureChunkCellRecord(chunks, sheetId, cell.row, cell.col)
    record.comment = author ? `${author}: ${text}` : text
    ensureCellRecordMetadata(record).ooxml_comment = compactUnknownRecord({
      author,
      text,
      ...commentGeometryByCell.get(`${cell.row}:${cell.col}`),
    })
  }
}

const applyHyperlinksToChunks = (
  files: Record<string, Uint8Array>,
  worksheetXml: string | null,
  worksheetPartName: string,
  sheetId: string,
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>,
): void => {
  if (worksheetXml == null) return
  const relationshipsById = new Map(parseRelationships(readXml(files, relationshipsPartNameFor(worksheetPartName)))
    .map((relationship) => [relationship.id, relationship]))
  for (const match of worksheetXml.matchAll(/<hyperlink\b[^>]*>/g)) {
    const attrs = parseAttributes(match[0])
    const range = parseA1RangeRef(attrs.ref ?? '', sheetId)
    if (range == null) continue
    const relationship = attrs['r:id'] == null ? null : relationshipsById.get(attrs['r:id'])
    const isInternalLocation = attrs.location != null && attrs.location.trim().length > 0
    const target = relationship == null
      ? isInternalLocation
        ? `#${attrs.location}`
        : attrs.display ?? attrs.ref
      : relationship.target_mode === 'External'
        ? relationship.target
        : resolveRelationshipTarget(worksheetPartName, relationship.target)
    const tooltip = attrs.tooltip?.trim()
    const display = attrs.display?.trim()
    const hyperlink = [target, tooltip ? `tooltip=${tooltip}` : null, display ? `display=${display}` : null]
      .filter(Boolean)
      .join('\n')
    const metadata = compactUnknownRecord(relationship == null && isInternalLocation ? {
      kind: 'internal',
      location: attrs.location,
      display,
      tooltip,
    } : {
      kind: relationship?.target_mode === 'External' ? 'external' : 'package',
      relationship_id: attrs['r:id'],
      target,
      target_mode: relationship?.target_mode ?? null,
      display,
      tooltip,
    })
    for (let row = range.start_row; row <= range.end_row; row += 1) {
      for (let col = range.start_col; col <= range.end_col; col += 1) {
        const record = ensureChunkCellRecord(chunks, sheetId, row, col)
        record.hyperlink = hyperlink
        ensureCellRecordMetadata(record).ooxml_hyperlink = metadata
      }
    }
  }
}

const parseConditionalFormats = (
  xml: string | null,
  sheetId: string,
  styles: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> => {
  const rules: Record<string, Record<string, unknown>> = {}
  if (xml == null) return rules
  let groupIndex = 0
  for (const groupMatch of xml.matchAll(/<conditionalFormatting\b([^>]*)>([\s\S]*?)<\/conditionalFormatting>/g)) {
    const groupAttrs = parseAttributes(groupMatch[1] ?? '')
    const ranges = String(groupAttrs.sqref ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .map((rangeText) => parseA1RangeRef(rangeText, sheetId))
      .filter((range): range is SpreadsheetRangeAddress => range != null)
    if (ranges.length === 0) continue
    let ruleIndex = 0
    for (const ruleMatch of (groupMatch[2] ?? '').matchAll(/<cfRule\b([^>]*?)(?:\/>|>([\s\S]*?)<\/cfRule>)/g)) {
      const attrs = parseAttributes(ruleMatch[1] ?? '')
      const formulas = Array.from((ruleMatch[2] ?? '').matchAll(/<formula\b[^>]*>([\s\S]*?)<\/formula>/g))
        .map((match) => decodeXmlText(match[1] ?? '').trim())
      const dxfId = attrs.dxfId == null ? null : Number(attrs.dxfId)
      const dxfStyle = Number.isFinite(dxfId) ? styles[`ooxml_dxf_${dxfId}`] : undefined
      const id = `ooxml_cf_${sheetId}_${groupIndex}_${ruleIndex}`
      rules[id] = {
        id,
        ranges,
        priority: Number.isFinite(Number(attrs.priority)) ? Number(attrs.priority) : ruleIndex + 1,
        stop_if_true: attrs.stopIfTrue === '1' || attrs.stopIfTrue === 'true',
        kind: attrs.type ?? 'expression',
        operator: attrs.operator ?? null,
        formula1: formulas[0] ?? null,
        formula2: formulas[1] ?? null,
        format: dxfStyle == null
          ? attrs.dxfId == null ? undefined : { ooxml_dxf_id: dxfId }
          : { ooxml_dxf_id: dxfId, style_id: `ooxml_dxf_${dxfId}`, ...dxfStyle },
      }
      ruleIndex += 1
    }
    groupIndex += 1
  }
  return rules
}

const parseDefinedNames = (
  workbookXml: string | null,
  orderedSheetIds: string[],
  sheetIdByName: Map<string, string>,
): NonNullable<SpreadsheetWorkbookSnapshot['defined_names']> | undefined => {
  if (workbookXml == null) return undefined
  const definedNames: NonNullable<SpreadsheetWorkbookSnapshot['defined_names']> = {}
  for (const match of workbookXml.matchAll(/<definedName\b([^>]*)>([\s\S]*?)<\/definedName>/g)) {
    const attrs = parseAttributes(match[1] ?? '')
    const name = String(attrs.name ?? '').trim()
    if (!name) continue
    const refersTo = decodeXmlText(match[2] ?? '').trim()
    const localSheetIndex = attrs.localSheetId == null ? null : Number(attrs.localSheetId)
    const scopedSheetId = Number.isInteger(localSheetIndex) && localSheetIndex! >= 0
      ? orderedSheetIds[localSheetIndex!]
      : undefined
    const scope = scopedSheetId == null ? { kind: 'workbook' as const } : { kind: 'sheet' as const, sheet_id: scopedSheetId }
    const fallbackSheetId = scopedSheetId ?? orderedSheetIds[0] ?? 'sheet_1'
    const range = parseFormulaRangeRef(refersTo, fallbackSheetId, sheetIdByName)
    const key = scopedSheetId == null ? `workbook::${name}` : `${scopedSheetId}::${name}`
    definedNames[key] = {
      name,
      scope,
      refers_to: refersTo,
      range: range ?? undefined,
      comment: attrs.comment ?? null,
      hidden: attrs.hidden === '1' || attrs.hidden === 'true' ? true : undefined,
    }
  }
  return Object.keys(definedNames).length > 0 ? definedNames : undefined
}

const parseOoxmlBoolean = (value: string | undefined): boolean | undefined => {
  if (value === '1' || value === 'true') return true
  if (value === '0' || value === 'false') return false
  return undefined
}

const parseWorkbookProperties = (workbookXml: string | null): SpreadsheetWorkbookProperties | undefined => {
  const attrs = firstElementAttrs(workbookXml ?? '', 'workbookPr')
  if (attrs == null) return undefined
  const properties = compactUnknownRecord({
    date_1904: parseOoxmlBoolean(attrs.date1904),
    code_name: attrs.codeName,
  }) as SpreadsheetWorkbookProperties
  return Object.keys(properties).length > 0 ? properties : undefined
}

const parseWorkbookSecurity = (workbookXml: string | null): SpreadsheetWorkbookSecurity | undefined => {
  const workbookProtectionAttrs = firstElementAttrs(workbookXml ?? '', 'workbookProtection')
  const fileSharingAttrs = firstElementAttrs(workbookXml ?? '', 'fileSharing')
  const spinCount = (value: string | undefined): number | undefined => {
    const parsed = Number(value)
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined
  }
  const workbookProtection = workbookProtectionAttrs == null ? undefined : compactUnknownRecord({
    workbook_password: workbookProtectionAttrs.workbookPassword,
    revisions_password: workbookProtectionAttrs.revisionsPassword,
    lock_structure: parseOoxmlBoolean(workbookProtectionAttrs.lockStructure),
    lock_windows: parseOoxmlBoolean(workbookProtectionAttrs.lockWindows),
    lock_revision: parseOoxmlBoolean(workbookProtectionAttrs.lockRevision),
    algorithm_name: workbookProtectionAttrs.algorithmName,
    hash_value: workbookProtectionAttrs.hashValue,
    salt_value: workbookProtectionAttrs.saltValue,
    spin_count: spinCount(workbookProtectionAttrs.spinCount),
  })
  const fileSharing = fileSharingAttrs == null ? undefined : compactUnknownRecord({
    read_only_recommended: parseOoxmlBoolean(fileSharingAttrs.readOnlyRecommended),
    user_name: fileSharingAttrs.userName,
    reservation_password: fileSharingAttrs.reservationPassword,
    algorithm_name: fileSharingAttrs.algorithmName,
    hash_value: fileSharingAttrs.hashValue,
    salt_value: fileSharingAttrs.saltValue,
    spin_count: spinCount(fileSharingAttrs.spinCount),
  })
  const security = compactUnknownRecord({
    workbook_protection: workbookProtection == null || Object.keys(workbookProtection).length === 0 ? undefined : workbookProtection,
    file_sharing: fileSharing == null || Object.keys(fileSharing).length === 0 ? undefined : fileSharing,
  }) as SpreadsheetWorkbookSecurity
  return Object.keys(security).length > 0 ? security : undefined
}

const parseWorkbookCalculationSettings = (
  workbookXml: string | null,
): SpreadsheetWorkbookCalculationSettings | undefined => {
  const attrs = firstElementAttrs(workbookXml ?? '', 'calcPr')
  if (attrs == null) return undefined
  const iterateCount = Number(attrs.iterateCount)
  const iterateDelta = Number(attrs.iterateDelta)
  const calcId = Number(attrs.calcId)
  const settings = compactUnknownRecord({
    calc_mode: attrs.calcMode,
    full_calc_on_load: parseOoxmlBoolean(attrs.fullCalcOnLoad),
    force_full_calc: parseOoxmlBoolean(attrs.forceFullCalc),
    iterate: parseOoxmlBoolean(attrs.iterate),
    iterate_count: Number.isInteger(iterateCount) && iterateCount >= 0 ? iterateCount : undefined,
    iterate_delta: Number.isFinite(iterateDelta) && iterateDelta >= 0 ? iterateDelta : undefined,
    calc_id: Number.isInteger(calcId) && calcId >= 0 ? calcId : undefined,
  }) as SpreadsheetWorkbookCalculationSettings
  return Object.keys(settings).length > 0 ? settings : undefined
}

const parseWorkbookViews = (workbookXml: string | null): SpreadsheetWorkbookView[] | undefined => {
  if (workbookXml == null) return undefined
  const views: SpreadsheetWorkbookView[] = []
  for (const match of workbookXml.matchAll(/<workbookView\b([^>]*?)\/?>/gi)) {
    const attrs = parseAttributes(match[1] ?? '')
    const intAttr = (name: string): number | undefined => {
      const value = Number(attrs[name])
      return Number.isInteger(value) ? value : undefined
    }
    const nonNegativeIntAttr = (name: string): number | undefined => {
      const value = intAttr(name)
      return value != null && value >= 0 ? value : undefined
    }
    const visibility = attrs.visibility === 'hidden' || attrs.visibility === 'veryHidden' || attrs.visibility === 'visible'
      ? attrs.visibility
      : undefined
    const view = compactUnknownRecord({
      active_tab: nonNegativeIntAttr('activeTab'),
      first_sheet: nonNegativeIntAttr('firstSheet'),
      visibility,
      minimized: parseOoxmlBoolean(attrs.minimized),
      show_horizontal_scroll: parseOoxmlBoolean(attrs.showHorizontalScroll),
      show_vertical_scroll: parseOoxmlBoolean(attrs.showVerticalScroll),
      show_sheet_tabs: parseOoxmlBoolean(attrs.showSheetTabs),
      tab_ratio: nonNegativeIntAttr('tabRatio'),
      x_window: intAttr('xWindow'),
      y_window: intAttr('yWindow'),
      window_width: nonNegativeIntAttr('windowWidth'),
      window_height: nonNegativeIntAttr('windowHeight'),
    }) as SpreadsheetWorkbookView
    if (Object.keys(view).length > 0) views.push(view)
  }
  return views.length > 0 ? views : undefined
}

const extractFirstLocalNameText = (xml: string | null, localName: string): string | undefined => {
  if (xml == null) return undefined
  const escaped = localName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = new RegExp(`<(?:[A-Za-z0-9]+:)?${escaped}\\b[^>]*>([\\s\\S]*?)<\\/(?:[A-Za-z0-9]+:)?${escaped}>`, 'i').exec(xml)
  const value = decodeXmlText(match?.[1] ?? '').trim()
  return value || undefined
}

const parseWorkbookDocumentProperties = (
  corePropertiesXml: string | null,
  appPropertiesXml: string | null,
): SpreadsheetWorkbookDocumentProperties | undefined => {
  const properties = compactUnknownRecord({
    title: extractFirstLocalNameText(corePropertiesXml, 'title'),
    subject: extractFirstLocalNameText(corePropertiesXml, 'subject'),
    creator: extractFirstLocalNameText(corePropertiesXml, 'creator'),
    keywords: extractFirstLocalNameText(corePropertiesXml, 'keywords'),
    description: extractFirstLocalNameText(corePropertiesXml, 'description'),
    last_modified_by: extractFirstLocalNameText(corePropertiesXml, 'lastModifiedBy'),
    revision: extractFirstLocalNameText(corePropertiesXml, 'revision'),
    created: extractFirstLocalNameText(corePropertiesXml, 'created'),
    modified: extractFirstLocalNameText(corePropertiesXml, 'modified'),
    category: extractFirstLocalNameText(corePropertiesXml, 'category'),
    content_status: extractFirstLocalNameText(corePropertiesXml, 'contentStatus'),
    application: extractFirstLocalNameText(appPropertiesXml, 'Application'),
    app_version: extractFirstLocalNameText(appPropertiesXml, 'AppVersion'),
    company: extractFirstLocalNameText(appPropertiesXml, 'Company'),
    manager: extractFirstLocalNameText(appPropertiesXml, 'Manager'),
  }) as SpreadsheetWorkbookDocumentProperties
  return Object.keys(properties).length > 0 ? properties : undefined
}

const parseWorkbookCustomProperties = (
  customPropertiesXml: string | null,
): Record<string, SpreadsheetWorkbookCustomProperty> | undefined => {
  if (customPropertiesXml == null) return undefined
  const properties: Record<string, SpreadsheetWorkbookCustomProperty> = {}
  for (const match of customPropertiesXml.matchAll(/<property\b([^>]*)>([\s\S]*?)<\/property>/gi)) {
    const attrs = parseAttributes(match[1] ?? '')
    const name = String(attrs.name ?? '').trim()
    if (!name) continue
    const body = match[2] ?? ''
    const valueMatch = /<vt:([A-Za-z0-9]+)\b[^>]*>([\s\S]*?)<\/vt:\1>/i.exec(body)
    if (valueMatch == null) continue
    const vtType = valueMatch[1]!
    const rawValue = decodeXmlText(valueMatch[2] ?? '')
    if (vtType === 'bool') {
      const parsed = parseOoxmlBoolean(rawValue)
      if (parsed != null) properties[name] = { name, kind: 'boolean', value: parsed }
      continue
    }
    if (vtType === 'i1' || vtType === 'i2' || vtType === 'i4' || vtType === 'i8' || vtType === 'int' || vtType === 'ui1' || vtType === 'ui2' || vtType === 'ui4' || vtType === 'ui8' || vtType === 'uint' || vtType === 'r4' || vtType === 'r8' || vtType === 'decimal') {
      const parsed = Number(rawValue)
      if (Number.isFinite(parsed)) properties[name] = { name, kind: 'number', value: parsed }
      continue
    }
    if (vtType === 'filetime') {
      properties[name] = { name, kind: 'date', value: rawValue }
      continue
    }
    properties[name] = { name, kind: 'string', value: rawValue }
  }
  return Object.keys(properties).length > 0 ? properties : undefined
}

const toFilesRecord = (input: Uint8Array | ArrayBuffer): Record<string, Uint8Array> => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input)
  const entries = unzipSync(bytes)
  return Object.fromEntries(Object.entries(entries).map(([partName, value]) => [normalizePartName(partName), value]))
}

const collectPackageRelationships = (
  files: Record<string, Uint8Array>,
): Map<string, Relationship[]> => {
  const relationshipsBySource = new Map<string, Relationship[]>()
  for (const partName of Object.keys(files).sort((left, right) => left.localeCompare(right))) {
    if (!partName.endsWith('.rels')) continue
    const sourcePartName = sourcePartNameForRelationshipsPart(partName)
    if (sourcePartName == null) continue
    relationshipsBySource.set(sourcePartName, parseRelationships(readXml(files, partName)))
  }
  return relationshipsBySource
}

const relationshipTypeByTarget = (
  relationshipsBySource: Map<string, Relationship[]>,
): Map<string, string> => {
  const out = new Map<string, string>()
  for (const [sourcePartName, relationships] of relationshipsBySource) {
    for (const relationship of relationships) {
      if (relationship.target_mode === 'External') continue
      out.set(resolveRelationshipTarget(sourcePartName, relationship.target), relationship.type)
    }
  }
  return out
}

type RelationshipTargetDetails = Relationship & {
  source_part_name: string
}

const relationshipDetailsByTarget = (
  relationshipsBySource: Map<string, Relationship[]>,
): Map<string, RelationshipTargetDetails> => {
  const out = new Map<string, RelationshipTargetDetails>()
  for (const [sourcePartName, relationships] of relationshipsBySource) {
    for (const relationship of relationships) {
      if (relationship.target_mode === 'External') continue
      out.set(resolveRelationshipTarget(sourcePartName, relationship.target), {
        ...relationship,
        source_part_name: sourcePartName,
      })
    }
  }
  return out
}

const isAdvancedArtifact = (part: SpreadsheetExcelOoxmlPackagePart): boolean => {
  const relType = part.relationship_type ?? ''
  const contentType = part.content_type ?? ''
  const lowerContentType = contentType.toLowerCase()
  return (
    relType === SHEET_METADATA_REL_TYPE ||
    ADVANCED_RELATIONSHIP_HINTS.some((hint) => relType.includes(hint)) ||
    ADVANCED_CONTENT_TYPE_HINTS.some((hint) => lowerContentType.includes(hint.toLowerCase()))
  )
}

const buildAdvancedArtifactManifest = (
  parts: SpreadsheetExcelOoxmlPackagePart[],
  files: Record<string, Uint8Array>,
  relationshipsBySource: Map<string, Relationship[]>,
  markerAttributesByPart: Map<string, Record<string, string>> = new Map(),
): SpreadsheetExcelOoxmlArtifactManifestEntry[] => parts
  .filter(isAdvancedArtifact)
  .map((part) => {
    const bytes = files[part.part_name]
    const relationships = (relationshipsBySource.get(part.part_name) ?? []).map((relationship) => ({
      id: relationship.id,
      type: relationship.type,
      target: relationship.target,
      target_mode: relationship.target_mode ?? null,
    }))
    return {
      part_name: part.part_name,
      content_type: part.content_type ?? null,
      relationship_type: part.relationship_type ?? null,
      source_part_name: part.source_part_name ?? null,
      relationship_id: part.relationship_id ?? null,
      relationship_target: part.relationship_target ?? null,
      relationship_target_mode: part.relationship_target_mode ?? null,
      marker_attributes: markerAttributesByPart.get(part.part_name),
      byte_length: bytes?.byteLength ?? part.byte_length,
      checksum_crc32: bytes == null ? null : formatCrc32(bytes),
      payload_base64: bytes == null ? null : encodeBytesToBase64(bytes),
      relationships: relationships.length > 0 ? relationships : undefined,
      preservation: 'non_authoritative_attachment',
    }
  })

const buildWorkbookMarkerAttributesByPart = (
  workbookXml: string | null,
  workbookPartName: string,
  workbookRelationships: Relationship[],
): Map<string, Record<string, string>> => {
  const out = new Map<string, Record<string, string>>()
  if (workbookXml == null) return out
  const relationshipById = new Map(workbookRelationships.map((relationship) => [relationship.id, relationship]))
  for (const match of workbookXml.matchAll(/<pivotCache\b([^>]*)\/?>/gi)) {
    const attrs = parseAttributes(match[1] ?? '')
    const relationshipId = attrs['r:id']
    const cacheId = attrs.cacheId
    if (!relationshipId || !cacheId) continue
    const relationship = relationshipById.get(relationshipId)
    if (relationship == null || relationship.target_mode === 'External') continue
    out.set(resolveRelationshipTarget(workbookPartName, relationship.target), { cacheId })
  }
  return out
}

export const inspectSpreadsheetExcelOoxmlPackage = (
  input: Uint8Array | ArrayBuffer,
  format: SpreadsheetExcelWorkbookIoFormat = 'xlsx',
): SpreadsheetExcelOoxmlPackageInspection => {
  const files = toFilesRecord(input)
  const contentTypes = parseContentTypes(readXml(files, '[Content_Types].xml'))
  const rootRelationships = parseRelationships(readXml(files, '_rels/.rels'))
  const workbookRelationship = rootRelationships.find((relationship) => relationship.type === OFFICE_DOCUMENT_REL_TYPE)
  const workbookPartName = workbookRelationship == null
    ? 'xl/workbook.xml'
    : resolveRelationshipTarget('', workbookRelationship.target)
  const workbookRelationshipsPartName = relationshipsPartNameFor(workbookPartName)
  const relationshipsBySource = collectPackageRelationships(files)
  if (!relationshipsBySource.has('')) relationshipsBySource.set('', rootRelationships)
  const workbookRelationships = relationshipsBySource.get(workbookPartName) ??
    parseRelationships(readXml(files, workbookRelationshipsPartName))
  if (!relationshipsBySource.has(workbookPartName)) {
    relationshipsBySource.set(workbookPartName, workbookRelationships)
  }
  const relationshipTypes = relationshipTypeByTarget(relationshipsBySource)
  const relationshipDetails = relationshipDetailsByTarget(relationshipsBySource)
  const worksheetRelationshipById = new Map(workbookRelationships
    .filter((relationship) => relationship.type === WORKSHEET_REL_TYPE)
    .map((relationship) => [relationship.id, relationship]))
  const worksheets = parseWorkbookSheets(readXml(files, workbookPartName)).map((sheet) => {
    const relationship = worksheetRelationshipById.get(sheet.relationship_id)
    const partName = relationship == null ? '' : resolveRelationshipTarget(workbookPartName, relationship.target)
    const dimensions = parseWorksheetDimensions(readXml(files, partName))
    const table_count = (relationshipsBySource.get(partName) ?? [])
      .filter((entry) => entry.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/table')
      .length
    return {
      ...sheet,
      ...dimensions,
      table_count,
      part_name: partName,
    }
  })
  const warnings: SpreadsheetExcelIoWarning[] = []
  if (files['[Content_Types].xml'] == null) {
    warnings.push({
      code: 'missing-content-types',
      severity: 'error',
      message: 'OOXML package is missing [Content_Types].xml.',
    })
  }
  if (workbookRelationship == null && files[workbookPartName] == null) {
    warnings.push({
      code: 'missing-workbook-relationship',
      severity: 'error',
      message: 'OOXML package does not declare a workbook officeDocument relationship.',
      part_name: '_rels/.rels',
    })
  }
  for (const worksheet of worksheets) {
    if (!worksheet.part_name || files[worksheet.part_name] == null) {
      warnings.push({
        code: 'missing-worksheet-part',
        severity: 'error',
        message: `Worksheet "${worksheet.name}" points to a missing OOXML part.`,
        sheet_name: worksheet.name,
      })
    }
  }
  const parts = Object.entries(files)
    .map(([partName, bytes]) => ({
      part_name: partName,
      byte_length: bytes.byteLength,
      content_type: getContentType(partName, contentTypes),
      relationship_type: relationshipTypes.get(partName) ?? null,
      source_part_name: relationshipDetails.get(partName)?.source_part_name ?? null,
      relationship_id: relationshipDetails.get(partName)?.id ?? null,
      relationship_target: relationshipDetails.get(partName)?.target ?? null,
      relationship_target_mode: relationshipDetails.get(partName)?.target_mode ?? null,
    }))
    .sort((left, right) => left.part_name.localeCompare(right.part_name))
  const markerAttributesByPart = buildWorkbookMarkerAttributesByPart(readXml(files, workbookPartName), workbookPartName, workbookRelationships)
  const advanced_ooxml_artifacts = buildAdvancedArtifactManifest(parts, files, relationshipsBySource, markerAttributesByPart)
  if (format === 'xlsx' && advanced_ooxml_artifacts.some((artifact) => artifact.part_name === 'xl/vbaProject.bin')) {
    warnings.push({
      code: 'macro-project-in-xlsx',
      severity: 'error',
      message: 'Macro project payloads are only valid in .xlsm corpus cases and are never executed.',
      part_name: 'xl/vbaProject.bin',
    })
  }
  for (const artifact of advanced_ooxml_artifacts) {
    if (!CORE_MODELED_RELATIONSHIP_TYPES.has(artifact.relationship_type ?? '')) {
      warnings.push({
        code: 'advanced-ooxml-preserved',
        severity: 'info',
        message: 'Advanced OOXML part is preserved as a non-authoritative attachment.',
        part_name: artifact.part_name,
      })
    }
  }
  const inspectedSheetIds = worksheets.map((worksheet, index) => normalizeImportedSheetId(worksheet.workbook_sheet_id, index))
  const themeRelationship = workbookRelationships.find((relationship) => (
    relationship.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme'
  ))
  const themePartName = themeRelationship == null
    ? ''
    : resolveRelationshipTarget(workbookPartName, themeRelationship.target)
  const themeColors = parseThemeColors(readXml(files, themePartName))
  warnings.push(...parseNativeChartImport({
    files,
    worksheets,
    sheetIds: inspectedSheetIds,
    sheetIdByName: new Map(worksheets.map((worksheet, index) => [worksheet.name, inspectedSheetIds[index]!])),
    themeColors,
  }).warnings)

  return {
    format,
    workbook_part_name: workbookPartName,
    workbook_relationships_part_name: workbookRelationshipsPartName,
    worksheets,
    parts,
    warnings,
    advanced_ooxml_artifacts,
  }
}

export const buildSpreadsheetExcelImportPreviewFromOoxmlPackage = ({
  format,
  file_name,
  package_bytes,
  requestId,
  workbookId,
  workbookRevision,
}: SpreadsheetExcelOoxmlImportPreviewInput): SpreadsheetExcelImportPreviewResponsePayload => {
  const inspected = inspectSpreadsheetExcelOoxmlPackage(package_bytes, format)
  return {
    requestId,
    workbookId,
    workbookRevision,
    format,
    file_name,
    workbook_name: file_name.replace(/\.(xlsx|xlsm)$/i, ''),
    sheets: inspected.worksheets.map((sheet) => ({
      name: sheet.name,
      row_count: sheet.row_count,
      col_count: sheet.col_count,
      hidden: sheet.hidden,
      table_count: sheet.table_count,
      formula_count: sheet.formula_count,
      warning_count: inspected.warnings.filter((warning) => warning.sheet_name === sheet.name).length,
    })),
    warnings: inspected.warnings,
    advanced_ooxml_artifacts: inspected.advanced_ooxml_artifacts,
  }
}

const normalizeImportedSheetId = (workbookSheetId: string, fallbackIndex: number): string => {
  const numeric = Number(workbookSheetId)
  if (Number.isInteger(numeric) && numeric > 0) return `sheet_${numeric}`
  return `sheet_${fallbackIndex + 1}`
}

type SnapshotSheetExportEntry = {
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number]
  sheetIndex: number
}

type SnapshotTableExportEntry = {
  sheetId: string
  sheetIndex: number
  tableIndex: number
  relationshipId: string
  partName: string
  table: SpreadsheetTableDefinition
}

type SnapshotCommentCellExportEntry = {
  row: number
  col: number
  author: string
  text: string
  metadata: Record<string, unknown>
}

type SnapshotCommentExportEntry = {
  sheetId: string
  sheetIndex: number
  commentIndex: number
  commentsRelationshipId: string
  vmlRelationshipId: string
  commentsPartName: string
  vmlPartName: string
  hasVmlGeometry: boolean
  comments: SnapshotCommentCellExportEntry[]
}

type SnapshotCellExportEntry = {
  row: number
  col: number
  record: SpreadsheetCellRecord
}

const getOrderedSnapshotSheets = (snapshot: SpreadsheetWorkbookSnapshot): SnapshotSheetExportEntry[] => {
  const byId = new Map(snapshot.sheets.map((sheet) => [sheet.sheet_id, sheet]))
  const ordered = snapshot.sheet_order
    .map((sheetId) => byId.get(sheetId))
    .filter((sheet): sheet is SpreadsheetWorkbookSnapshot['sheets'][number] => sheet != null)
  const included = new Set(ordered.map((sheet) => sheet.sheet_id))
  for (const sheet of snapshot.sheets) {
    if (!included.has(sheet.sheet_id)) ordered.push(sheet)
  }
  return ordered.map((sheet, index) => ({ sheet, sheetIndex: index + 1 }))
}

const getSnapshotTableExportEntries = (sheets: SnapshotSheetExportEntry[]): SnapshotTableExportEntry[] => {
  const entries: SnapshotTableExportEntry[] = []
  let tableIndex = 1
  for (const { sheet, sheetIndex } of sheets) {
    const tables = Object.values(sheet.tables ?? {})
      .filter((table) => table.name && table.range)
      .sort((left, right) => (
        left.range.start_row - right.range.start_row ||
        left.range.start_col - right.range.start_col ||
        left.name.localeCompare(right.name) ||
        left.id.localeCompare(right.id)
      ))
    for (const table of tables) {
      entries.push({
        sheetId: sheet.sheet_id,
        sheetIndex,
        tableIndex,
        relationshipId: `rTable${tableIndex}`,
        partName: `xl/tables/table${tableIndex}.xml`,
        table,
      })
      tableIndex += 1
    }
  }
  return entries
}

const columnIndexToName = (col: number): string => {
  let value = Math.max(0, Math.floor(col)) + 1
  let name = ''
  while (value > 0) {
    value -= 1
    name = String.fromCharCode(65 + (value % 26)) + name
    value = Math.floor(value / 26)
  }
  return name
}

const formatCellRef = (row: number, col: number): string => `${columnIndexToName(col)}${row + 1}`

const formatRangeRef = (range: SpreadsheetRangeAddress): string => {
  const start = formatCellRef(range.start_row, range.start_col)
  const end = formatCellRef(range.end_row, range.end_col)
  return start === end ? start : `${start}:${end}`
}

const quoteSheetNameForFormula = (name: string): string => `'${name.replace(/'/g, "''")}'`

const formatAbsoluteCellRef = (row: number, col: number): string => `$${columnIndexToName(col)}$${row + 1}`

const formatAbsoluteRangeRef = (range: SpreadsheetRangeAddress): string => {
  const start = formatAbsoluteCellRef(range.start_row, range.start_col)
  const end = formatAbsoluteCellRef(range.end_row, range.end_col)
  return start === end ? start : `${start}:${end}`
}

const formatNamedRangeDefinedNameFormula = (
  range: SpreadsheetRangeAddress,
  sheetNameById: ReadonlyMap<string, string>,
): string | null => {
  const sheetName = sheetNameById.get(range.sheet_id)
  if (sheetName == null) return null
  // OOXML <definedName> content is the reference itself, WITHOUT a leading "="
  // (Excel writes them that way and rejects a leading "=" as malformed → repair).
  return `${quoteSheetNameForFormula(sheetName)}!${formatAbsoluteRangeRef(range)}`
}

const iterSnapshotCells = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): SnapshotCellExportEntry[] => {
  const cells: SnapshotCellExportEntry[] = []
  for (const chunk of Object.values(sheet.chunks ?? {})) {
    for (const [key, record] of Object.entries(chunk)) {
      const [rowText, colText] = key.split(':')
      const row = Number(rowText)
      const col = Number(colText)
      if (!Number.isInteger(row) || row < 0 || !Number.isInteger(col) || col < 0) continue
      cells.push({ row, col, record })
    }
  }
  return cells.sort((left, right) => left.row - right.row || left.col - right.col)
}

const spreadsheetCellValueToRawInput = (value: SpreadsheetCellValue): string => {
  if (value.kind === 'number') return String(value.value)
  if (value.kind === 'boolean') return value.value ? 'TRUE' : 'FALSE'
  if (value.kind === 'text') return value.value
  if (value.kind === 'error') return value.code
  return ''
}

const buildVirtualDynamicArraySpillRecord = (
  owner: SpreadsheetCellRecord,
  value: SpreadsheetCellValue,
): SpreadsheetCellRecord => {
  const ownerMetadata = getCellOoxmlMetadata(owner)
  const metadata = ownerMetadata == null ? undefined : { ooxml_cell_metadata: ownerMetadata }
  return {
    raw_input: spreadsheetCellValueToRawInput(value),
    computed_value: value,
    revision: owner.revision,
    ...(metadata == null ? {} : { metadata }),
  }
}

const iterSnapshotCellsWithDynamicArraySpills = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): SnapshotCellExportEntry[] => {
  const cells = iterSnapshotCells(sheet)
  const occupied = new Set(cells.map(({ row, col }) => `${row}:${col}`))
  const projected = [...cells]
  for (const { row: ownerRow, col: ownerCol, record } of cells) {
    const dynamicRows = record.computed_value?.kind === 'dynamic_array' ? record.computed_value.rows : null
    if (dynamicRows == null) continue
    dynamicRows.forEach((values, rowOffset) => {
      values.forEach((value, colOffset) => {
        const row = ownerRow + rowOffset
        const col = ownerCol + colOffset
        const key = `${row}:${col}`
        if (row === ownerRow && col === ownerCol) return
        if (occupied.has(key)) return
        occupied.add(key)
        projected.push({
          row,
          col,
          record: buildVirtualDynamicArraySpillRecord(record, value),
        })
      })
    })
  }
  return projected.sort((left, right) => left.row - right.row || left.col - right.col)
}

const getCellCommentMetadata = (
  record: SpreadsheetCellRecord,
): Record<string, unknown> => {
  const metadata = record.metadata && typeof record.metadata === 'object' ? record.metadata.ooxml_comment : null
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata as Record<string, unknown> : {}
}

const getCommentAuthorAndText = (
  record: SpreadsheetCellRecord,
  metadata: Record<string, unknown>,
): { author: string; text: string } => {
  const metadataText = String(metadata.text ?? '').trim()
  const metadataAuthor = String(metadata.author ?? '').trim()
  if (metadataText) return { author: metadataAuthor, text: metadataText }
  const comment = String(record.comment ?? '').trim()
  const split = /^([^:\n]{1,80}):\s+([\s\S]+)$/.exec(comment)
  if (split != null) return { author: split[1]!.trim(), text: split[2]!.trim() }
  return { author: metadataAuthor, text: comment }
}

const getSnapshotCommentExportEntries = (sheets: SnapshotSheetExportEntry[]): SnapshotCommentExportEntry[] => {
  const entries: SnapshotCommentExportEntry[] = []
  let commentIndex = 1
  for (const { sheet, sheetIndex } of sheets) {
    const comments = iterSnapshotCells(sheet)
      .map(({ row, col, record }) => {
        if (!String(record.comment ?? '').trim()) return null
        const metadata = getCellCommentMetadata(record)
        const { author, text } = getCommentAuthorAndText(record, metadata)
        if (!text) return null
        return { row, col, author, text, metadata }
      })
      .filter((entry): entry is SnapshotCommentCellExportEntry => entry != null)
    if (comments.length === 0) continue
    entries.push({
      sheetId: sheet.sheet_id,
      sheetIndex,
      commentIndex,
      commentsRelationshipId: `rComments${commentIndex}`,
      vmlRelationshipId: `rVml${commentIndex}`,
      commentsPartName: `xl/comments${commentIndex}.xml`,
      vmlPartName: `xl/drawings/vmlDrawing${commentIndex}.vml`,
      // Excel requires every authored cell comment to be backed by a legacy VML
      // drawing (a vmlDrawing part + a <legacyDrawing> ref in the worksheet); a
      // comments part with no VML is treated as unreadable and triggers the
      // "repaired or removed" dialog. When the snapshot carries imported VML
      // geometry we round-trip it; otherwise buildVmlDrawingXml synthesizes a
      // default Excel comment box. Either way, any sheet with comments emits VML.
      hasVmlGeometry: true,
      comments,
    })
    commentIndex += 1
  }
  return entries
}

const isFormulaInput = (rawInput: string): boolean => rawInput.trim().startsWith('=')

const buildFormulaCachedValueXml = (value: SpreadsheetCellValue | null | undefined): { typeAttr: string, valueXml: string } | null => {
  if (value == null) return null
  if (value.kind === 'dynamic_array') return buildFormulaCachedValueXml(value.rows[0]?.[0])
  if (value.kind === 'number') return { typeAttr: '', valueXml: `<v>${encodeXmlText(String(value.value))}</v>` }
  if (value.kind === 'boolean') return { typeAttr: ' t="b"', valueXml: `<v>${value.value ? '1' : '0'}</v>` }
  if (value.kind === 'text') return { typeAttr: ' t="str"', valueXml: `<v>${encodeXmlText(value.value)}</v>` }
  if (value.kind === 'error') return { typeAttr: ' t="e"', valueXml: `<v>${encodeXmlText(value.code)}</v>` }
  return null
}

const getCellFormulaMetadata = (record: SpreadsheetCellRecord): Record<string, unknown> | null => {
  const metadata = record.metadata && typeof record.metadata === 'object' ? record.metadata.ooxml_formula : null
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata as Record<string, unknown> : null
}

const getCellOoxmlMetadata = (record: SpreadsheetCellRecord): Record<string, unknown> | null => {
  const metadata = record.metadata && typeof record.metadata === 'object' ? record.metadata.ooxml_cell_metadata : null
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata as Record<string, unknown> : null
}

const buildCellOoxmlMetadataAttrs = (record: SpreadsheetCellRecord): string => {
  const metadata = getCellOoxmlMetadata(record)
  if (metadata == null) return ''
  const cm = String(metadata.cell_metadata_index ?? '').trim()
  const vm = String(metadata.value_metadata_index ?? '').trim()
  return [
    cm ? `cm="${encodeXmlAttr(cm)}"` : '',
    vm ? `vm="${encodeXmlAttr(vm)}"` : '',
  ].filter(Boolean).map((attr) => ` ${attr}`).join('')
}

const buildCellFormulaXml = (record: SpreadsheetCellRecord): string => {
  const rawInput = String(record.raw_input ?? '')
  const formula = rawInput.trim().slice(1)
  const metadata = getCellFormulaMetadata(record)
  const metadataAttrs = metadata?.attributes
  const attributes = metadataAttrs && typeof metadataAttrs === 'object' && !Array.isArray(metadataAttrs)
    ? metadataAttrs as Record<string, unknown>
    : {}
  const safeAttributes: Record<string, string> = {}
  for (const key of FORMULA_METADATA_ATTR_KEYS) {
    const value = String(attributes[key] ?? '').trim()
    if (value) safeAttributes[key] = value
  }
  const metadataType = String(metadata?.type ?? '').trim()
  if (metadataType && metadataType !== 'shared') safeAttributes.t ??= metadataType
  const metadataRef = String(metadata?.ref ?? '').trim()
  if (metadataRef) safeAttributes.ref ??= metadataRef
  delete safeAttributes.si
  if (safeAttributes.t === 'shared') delete safeAttributes.t
  const attrs = Object.entries(safeAttributes)
    .map(([key, value]) => `${key}="${encodeXmlAttr(value)}"`)
    .join(' ')
  return `<f${attrs ? ` ${attrs}` : ''}>${encodeXmlText(formula)}</f>`
}

const collectSharedStrings = (sheets: SnapshotSheetExportEntry[]): Map<string, number> => {
  const strings = new Map<string, number>()
  for (const { sheet } of sheets) {
    for (const { record } of iterSnapshotCellsWithDynamicArraySpills(sheet)) {
      const rawInput = String(record.raw_input ?? '')
      if (!rawInput || isFormulaInput(rawInput)) continue
      const computed = record.computed_value
      if (computed?.kind === 'number' || computed?.kind === 'boolean' || computed?.kind === 'error') continue
      if (!strings.has(rawInput)) strings.set(rawInput, strings.size)
    }
  }
  return strings
}

const buildSharedStringsXml = (sharedStrings: Map<string, number>): string => {
  const ordered = Array.from(sharedStrings.entries()).sort((left, right) => left[1] - right[1])
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${ordered.length}" uniqueCount="${ordered.length}">
${ordered.map(([text]) => `  <si><t>${encodeXmlText(text)}</t></si>`).join('\n')}
</sst>`
}

const buildWorkbookXml = (
  sheets: SnapshotSheetExportEntry[],
  snapshot: SpreadsheetWorkbookSnapshot,
): string => {
  const workbookProperties = snapshot.workbook_properties
  const workbookPropertiesXml = workbookProperties == null ? '' : (() => {
    const attrs = [
      workbookProperties.date_1904 === true ? 'date1904="1"' : '',
      workbookProperties.code_name ? `codeName="${encodeXmlAttr(workbookProperties.code_name)}"` : '',
    ].filter(Boolean).join(' ')
    return attrs ? `
  <workbookPr ${attrs}/>` : ''
  })()
  const calculationSettings = snapshot.calculation_settings
  const calculationSettingsXml = calculationSettings == null ? '' : (() => {
    const attrs = [
      calculationSettings.calc_mode ? `calcMode="${encodeXmlAttr(calculationSettings.calc_mode)}"` : '',
      calculationSettings.full_calc_on_load === true ? 'fullCalcOnLoad="1"' : '',
      calculationSettings.force_full_calc === true ? 'forceFullCalc="1"' : '',
      calculationSettings.iterate === true ? 'iterate="1"' : '',
      calculationSettings.iterate_count != null ? `iterateCount="${calculationSettings.iterate_count}"` : '',
      calculationSettings.iterate_delta != null ? `iterateDelta="${calculationSettings.iterate_delta}"` : '',
      calculationSettings.calc_id != null ? `calcId="${calculationSettings.calc_id}"` : '',
    ].filter(Boolean).join(' ')
    return attrs ? `
  <calcPr ${attrs}/>` : ''
  })()
  const workbookSecurity = snapshot.workbook_security
  const workbookSecurityXml = workbookSecurity == null ? '' : (() => {
    const workbookProtection = workbookSecurity.workbook_protection
    const workbookProtectionAttrs = workbookProtection == null ? '' : [
      workbookProtection.workbook_password ? `workbookPassword="${encodeXmlAttr(workbookProtection.workbook_password)}"` : '',
      workbookProtection.revisions_password ? `revisionsPassword="${encodeXmlAttr(workbookProtection.revisions_password)}"` : '',
      workbookProtection.lock_structure != null ? `lockStructure="${workbookProtection.lock_structure ? '1' : '0'}"` : '',
      workbookProtection.lock_windows != null ? `lockWindows="${workbookProtection.lock_windows ? '1' : '0'}"` : '',
      workbookProtection.lock_revision != null ? `lockRevision="${workbookProtection.lock_revision ? '1' : '0'}"` : '',
      workbookProtection.algorithm_name ? `algorithmName="${encodeXmlAttr(workbookProtection.algorithm_name)}"` : '',
      workbookProtection.hash_value ? `hashValue="${encodeXmlAttr(workbookProtection.hash_value)}"` : '',
      workbookProtection.salt_value ? `saltValue="${encodeXmlAttr(workbookProtection.salt_value)}"` : '',
      workbookProtection.spin_count != null ? `spinCount="${workbookProtection.spin_count}"` : '',
    ].filter(Boolean).join(' ')
    const fileSharing = workbookSecurity.file_sharing
    const fileSharingAttrs = fileSharing == null ? '' : [
      fileSharing.read_only_recommended != null ? `readOnlyRecommended="${fileSharing.read_only_recommended ? '1' : '0'}"` : '',
      fileSharing.user_name ? `userName="${encodeXmlAttr(fileSharing.user_name)}"` : '',
      fileSharing.reservation_password ? `reservationPassword="${encodeXmlAttr(fileSharing.reservation_password)}"` : '',
      fileSharing.algorithm_name ? `algorithmName="${encodeXmlAttr(fileSharing.algorithm_name)}"` : '',
      fileSharing.hash_value ? `hashValue="${encodeXmlAttr(fileSharing.hash_value)}"` : '',
      fileSharing.salt_value ? `saltValue="${encodeXmlAttr(fileSharing.salt_value)}"` : '',
      fileSharing.spin_count != null ? `spinCount="${fileSharing.spin_count}"` : '',
    ].filter(Boolean).join(' ')
    return [
      workbookProtectionAttrs ? `  <workbookProtection ${workbookProtectionAttrs}/>` : '',
      fileSharingAttrs ? `  <fileSharing ${fileSharingAttrs}/>` : '',
    ].filter(Boolean).join('\n')
  })()
  const workbookViews = (snapshot.workbook_views ?? [])
    .map((view) => {
      const attrs = [
        view.active_tab != null ? `activeTab="${view.active_tab}"` : '',
        view.first_sheet != null ? `firstSheet="${view.first_sheet}"` : '',
        view.visibility ? `visibility="${encodeXmlAttr(view.visibility)}"` : '',
        view.minimized != null ? `minimized="${view.minimized ? '1' : '0'}"` : '',
        view.show_horizontal_scroll != null ? `showHorizontalScroll="${view.show_horizontal_scroll ? '1' : '0'}"` : '',
        view.show_vertical_scroll != null ? `showVerticalScroll="${view.show_vertical_scroll ? '1' : '0'}"` : '',
        view.show_sheet_tabs != null ? `showSheetTabs="${view.show_sheet_tabs ? '1' : '0'}"` : '',
        view.tab_ratio != null ? `tabRatio="${view.tab_ratio}"` : '',
        view.x_window != null ? `xWindow="${view.x_window}"` : '',
        view.y_window != null ? `yWindow="${view.y_window}"` : '',
        view.window_width != null ? `windowWidth="${view.window_width}"` : '',
        view.window_height != null ? `windowHeight="${view.window_height}"` : '',
      ].filter(Boolean).join(' ')
      return attrs ? `    <workbookView ${attrs}/>` : ''
    })
    .filter(Boolean)
  const workbookViewsXml = workbookViews.length === 0 ? '' : `
  <bookViews>
${workbookViews.join('\n')}
  </bookViews>`
  const definedNameEntries: NonNullable<SpreadsheetWorkbookSnapshot['defined_names']>[string][] = Object.values(snapshot.defined_names ?? {})
  const sheetNameById = new Map(sheets.map(({ sheet }) => [sheet.sheet_id, sheet.name]))
  const definedNameKeys = new Set(definedNameEntries.map((entry) => {
    const scopeKey = entry.scope.kind === 'sheet' ? entry.scope.sheet_id : 'workbook'
    return `${scopeKey}::${entry.name.toLowerCase()}`
  }))
  for (const [name, range] of Object.entries(snapshot.named_ranges ?? {}).sort(([left], [right]) => left.localeCompare(right))) {
    const refersTo = formatNamedRangeDefinedNameFormula(range, sheetNameById)
    if (refersTo == null) continue
    const key = `workbook::${name.toLowerCase()}`
    if (definedNameKeys.has(key)) continue
    definedNameKeys.add(key)
    definedNameEntries.push({
      name,
      scope: { kind: 'workbook' },
      refers_to: refersTo,
      range,
    })
  }
  const definedNames = definedNameEntries
    .filter((entry) => entry.name && entry.refers_to)
    .map((entry) => {
      const scope = entry.scope
      const sheetIndex = scope.kind === 'sheet'
        ? sheets.findIndex(({ sheet }) => sheet.sheet_id === scope.sheet_id)
        : -1
      const localSheetId = sheetIndex >= 0 ? ` localSheetId="${sheetIndex}"` : ''
      const hidden = entry.hidden ? ' hidden="1"' : ''
      const comment = entry.comment ? ` comment="${encodeXmlAttr(entry.comment)}"` : ''
      return `    <definedName name="${encodeXmlAttr(entry.name)}"${localSheetId}${hidden}${comment}>${encodeXmlText(entry.refers_to)}</definedName>`
    })
  const externalReferenceIds = getSnapshotOoxmlAttachments(snapshot)
    .filter((artifact) => (
      artifact.relationship_type === EXTERNAL_LINK_REL_TYPE &&
      normalizePartName(artifact.source_part_name ?? '') === 'xl/workbook.xml' &&
      String(artifact.relationship_id ?? '').trim()
    ))
    .map((artifact) => String(artifact.relationship_id))
    .filter((relationshipId, index, ids) => ids.indexOf(relationshipId) === index)
    .sort((left, right) => left.localeCompare(right))
  const externalReferencesXml = externalReferenceIds.length === 0 ? '' : `
  <externalReferences>
${externalReferenceIds.map((relationshipId) => `    <externalReference r:id="${encodeXmlAttr(relationshipId)}"/>`).join('\n')}
  </externalReferences>`
  const pivotCacheEntries = getSnapshotOoxmlAttachments(snapshot)
    .filter((artifact) => (
      artifact.relationship_type === PIVOT_CACHE_DEFINITION_REL_TYPE &&
      normalizePartName(artifact.source_part_name ?? '') === 'xl/workbook.xml' &&
      String(artifact.relationship_id ?? '').trim()
    ))
    .map((artifact, index) => {
      return {
        cacheId: getPivotCacheIdForAttachment(artifact, index),
        relationshipId: String(artifact.relationship_id),
      }
    })
    .filter((entry, index, entries) => entries.findIndex((candidate) => candidate.relationshipId === entry.relationshipId) === index)
    .sort((left, right) => Number(left.cacheId) - Number(right.cacheId) || left.relationshipId.localeCompare(right.relationshipId))
  const pivotCachesXml = pivotCacheEntries.length === 0 ? '' : `
  <pivotCaches>
${pivotCacheEntries.map((entry) => `    <pivotCache cacheId="${encodeXmlAttr(entry.cacheId)}" r:id="${encodeXmlAttr(entry.relationshipId)}"/>`).join('\n')}
  </pivotCaches>`
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">${workbookPropertiesXml}
${workbookSecurityXml}
${workbookViewsXml}
  <sheets>
${sheets.map(({ sheet, sheetIndex }) => `    <sheet name="${encodeXmlAttr(sheet.name)}" sheetId="${sheetIndex}"${sheet.hidden ? ' state="hidden"' : ''} r:id="rId${sheetIndex}"/>`).join('\n')}
  </sheets>${externalReferencesXml}${definedNames.length === 0 ? '' : `
  <definedNames>
${definedNames.join('\n')}
  </definedNames>`}${calculationSettingsXml}${pivotCachesXml}
</workbook>`
}

const buildWorkbookRelationshipsXml = (
  sheets: SnapshotSheetExportEntry[],
  hasSharedStrings: boolean,
  hasTheme: boolean,
): string => {
  const sheetRelationships = sheets
    .map(({ sheetIndex }) => `  <Relationship Id="rId${sheetIndex}" Type="${WORKSHEET_REL_TYPE}" Target="worksheets/sheet${sheetIndex}.xml"/>`)
  const sharedStringsRelationship = hasSharedStrings
    ? ['  <Relationship Id="rSharedStrings" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>']
    : []
  const stylesRelationship = ['  <Relationship Id="rStyles" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>']
  const themeRelationship = hasTheme
    ? ['  <Relationship Id="rTheme" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme" Target="theme/theme1.xml"/>']
    : []
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${[...sheetRelationships, ...stylesRelationship, ...sharedStringsRelationship, ...themeRelationship].join('\n')}
</Relationships>`
}

const buildRootRelationshipsXml = (
  hasDocumentProperties: boolean,
  hasCustomProperties: boolean,
): string => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rWorkbook" Type="${OFFICE_DOCUMENT_REL_TYPE}" Target="xl/workbook.xml"/>
${hasDocumentProperties ? `  <Relationship Id="rCoreProperties" Type="${CORE_PROPERTIES_REL_TYPE}" Target="docProps/core.xml"/>
  <Relationship Id="rExtendedProperties" Type="${EXTENDED_PROPERTIES_REL_TYPE}" Target="docProps/app.xml"/>` : ''}
${hasCustomProperties ? `  <Relationship Id="rCustomProperties" Type="${CUSTOM_PROPERTIES_REL_TYPE}" Target="docProps/custom.xml"/>` : ''}
</Relationships>`

const buildContentTypesXml = (
  sheets: SnapshotSheetExportEntry[],
  hasSharedStrings: boolean,
  hasTheme: boolean,
  tableEntries: SnapshotTableExportEntry[],
  commentEntries: SnapshotCommentExportEntry[],
  hasDocumentProperties: boolean,
  hasCustomProperties: boolean,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[] = [],
): string => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
${commentEntries.some((entry) => entry.hasVmlGeometry) ? '  <Default Extension="vml" ContentType="application/vnd.openxmlformats-officedocument.vmlDrawing"/>' : ''}
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
${sheets.map(({ sheetIndex }) => `  <Override PartName="/xl/worksheets/sheet${sheetIndex}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
${tableEntries.map(({ partName }) => `  <Override PartName="/${partName}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.table+xml"/>`).join('\n')}
${commentEntries.map(({ commentsPartName }) => `  <Override PartName="/${commentsPartName}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.comments+xml"/>`).join('\n')}
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${hasTheme ? '  <Override PartName="/xl/theme/theme1.xml" ContentType="application/vnd.openxmlformats-officedocument.theme+xml"/>' : ''}
${hasSharedStrings ? '  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>' : ''}
${hasDocumentProperties ? '  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>\n  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>' : ''}
${hasCustomProperties ? '  <Override PartName="/docProps/custom.xml" ContentType="application/vnd.openxmlformats-officedocument.custom-properties+xml"/>' : ''}
${attachments
  .filter((artifact) => artifact.content_type && artifact.part_name)
  .map((artifact) => `  <Override PartName="/${normalizePartName(artifact.part_name)}" ContentType="${encodeXmlAttr(artifact.content_type)}"/>`)
  .join('\n')}
</Types>`

const buildCustomPropertiesXml = (
  customProperties: Record<string, SpreadsheetWorkbookCustomProperty>,
): string => {
  const entries = Object.values(customProperties)
    .filter((property) => property.name.trim())
    .sort((left, right) => left.name.localeCompare(right.name))
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/custom-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
${entries.map((property, index) => {
  const pid = index + 2
  if (property.kind === 'boolean') return `  <property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid}" name="${encodeXmlAttr(property.name)}"><vt:bool>${property.value ? 'true' : 'false'}</vt:bool></property>`
  if (property.kind === 'number') return `  <property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid}" name="${encodeXmlAttr(property.name)}"><vt:r8>${property.value}</vt:r8></property>`
  if (property.kind === 'date') return `  <property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid}" name="${encodeXmlAttr(property.name)}"><vt:filetime>${encodeXmlText(property.value)}</vt:filetime></property>`
  return `  <property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid}" name="${encodeXmlAttr(property.name)}"><vt:lpwstr>${encodeXmlText(property.value)}</vt:lpwstr></property>`
}).join('\n')}
</Properties>`
}

const hasWorkbookDocumentProperties = (
  documentProperties: SpreadsheetWorkbookDocumentProperties | null | undefined,
): documentProperties is SpreadsheetWorkbookDocumentProperties => (
  documentProperties != null &&
  Object.values(documentProperties).some((value) => String(value ?? '').trim().length > 0)
)

const buildCorePropertiesXml = (
  documentProperties: SpreadsheetWorkbookDocumentProperties,
): string => {
  const textElement = (prefix: string, name: string, value: string | null | undefined): string => {
    const trimmed = String(value ?? '').trim()
    return trimmed ? `  <${prefix}:${name}>${encodeXmlText(trimmed)}</${prefix}:${name}>` : ''
  }
  const dateElement = (name: string, value: string | null | undefined): string => {
    const trimmed = String(value ?? '').trim()
    return trimmed ? `  <dcterms:${name} xsi:type="dcterms:W3CDTF">${encodeXmlText(trimmed)}</dcterms:${name}>` : ''
  }
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
${[
  textElement('dc', 'title', documentProperties.title),
  textElement('dc', 'subject', documentProperties.subject),
  textElement('dc', 'creator', documentProperties.creator),
  textElement('cp', 'keywords', documentProperties.keywords),
  textElement('dc', 'description', documentProperties.description),
  textElement('cp', 'lastModifiedBy', documentProperties.last_modified_by),
  textElement('cp', 'revision', documentProperties.revision),
  dateElement('created', documentProperties.created),
  dateElement('modified', documentProperties.modified),
  textElement('cp', 'category', documentProperties.category),
  textElement('cp', 'contentStatus', documentProperties.content_status),
].filter(Boolean).join('\n')}
</cp:coreProperties>`
}

const buildExtendedPropertiesXml = (
  documentProperties: SpreadsheetWorkbookDocumentProperties,
): string => {
  const textElement = (name: string, value: string | null | undefined): string => {
    const trimmed = String(value ?? '').trim()
    return trimmed ? `  <${name}>${encodeXmlText(trimmed)}</${name}>` : ''
  }
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
${[
  textElement('Application', documentProperties.application),
  textElement('AppVersion', documentProperties.app_version),
  textElement('Company', documentProperties.company),
  textElement('Manager', documentProperties.manager),
].filter(Boolean).join('\n')}
</Properties>`
}

const buildSheetRelationshipsXml = (
  relationships: Array<{ id: string; type: string; target: string; targetMode?: string }>,
): string => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${relationships.map((relationship) => `  <Relationship Id="${encodeXmlAttr(relationship.id)}" Type="${encodeXmlAttr(relationship.type)}" Target="${encodeXmlAttr(relationship.target)}"${relationship.targetMode ? ` TargetMode="${encodeXmlAttr(relationship.targetMode)}"` : ''}/>`).join('\n')}
</Relationships>`

const buildWorksheetDrawingReferencesXml = (
  sheetPartName: string,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[],
): string => {
  const drawingRelationshipIds = attachments
    .filter((artifact) => (
      artifact.relationship_type === DRAWING_REL_TYPE &&
      normalizePartName(artifact.source_part_name ?? '') === sheetPartName &&
      String(artifact.relationship_id ?? '').trim()
    ))
    .map((artifact) => String(artifact.relationship_id))
    .filter((relationshipId, index, ids) => ids.indexOf(relationshipId) === index)
    .sort((left, right) => left.localeCompare(right))
  return drawingRelationshipIds.length === 0
    ? ''
    : `
${drawingRelationshipIds.map((relationshipId) => `  <drawing r:id="${encodeXmlAttr(relationshipId)}"/>`).join('\n')}`
}

const buildWorksheetPivotTableDefinitionsXml = (
  sheetPartName: string,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[],
): string => {
  const pivotTableRelationshipIds = attachments
    .filter((artifact) => (
      artifact.relationship_type === PIVOT_TABLE_REL_TYPE &&
      normalizePartName(artifact.source_part_name ?? '') === sheetPartName &&
      String(artifact.relationship_id ?? '').trim()
    ))
    .map((artifact) => String(artifact.relationship_id))
    .filter((relationshipId, index, ids) => ids.indexOf(relationshipId) === index)
    .sort((left, right) => left.localeCompare(right))
  return pivotTableRelationshipIds.length === 0
    ? ''
    : `
  <pivotTableDefinitions>
${pivotTableRelationshipIds.map((relationshipId) => `    <pivotTableDefinition r:id="${encodeXmlAttr(relationshipId)}"/>`).join('\n')}
  </pivotTableDefinitions>`
}

const getWorksheetAttachmentRelationshipIds = (
  sheetPartName: string,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[],
  relationshipType: string,
): string[] => attachments
  .filter((artifact) => (
    artifact.relationship_type === relationshipType &&
    normalizePartName(artifact.source_part_name ?? '') === sheetPartName &&
    String(artifact.relationship_id ?? '').trim()
  ))
  .map((artifact) => String(artifact.relationship_id))
  .filter((relationshipId, index, ids) => ids.indexOf(relationshipId) === index)
  .sort((left, right) => left.localeCompare(right))

const buildWorksheetAdvancedExtensionEntries = (
  sheetPartName: string,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[],
): string[] => {
  const slicerRelationshipIds = getWorksheetAttachmentRelationshipIds(sheetPartName, attachments, SLICER_REL_TYPE)
  const timelineRelationshipIds = getWorksheetAttachmentRelationshipIds(sheetPartName, attachments, TIMELINE_REL_TYPE)
  return [
    slicerRelationshipIds.length === 0 ? '' : `    <ext uri="{A8765BA9-456A-4DAB-B4F3-ACF838C121DE}" xmlns:x14="http://schemas.microsoft.com/office/spreadsheetml/2009/9/main"><x14:slicerList>${slicerRelationshipIds.map((relationshipId) => `<x14:slicer r:id="${encodeXmlAttr(relationshipId)}"/>`).join('')}</x14:slicerList></ext>`,
    timelineRelationshipIds.length === 0 ? '' : `    <ext uri="{7E03D99C-DC04-49D9-9315-930204A7B6E9}" xmlns:x15="http://schemas.microsoft.com/office/spreadsheetml/2010/11/main"><x15:timelineRefs>${timelineRelationshipIds.map((relationshipId) => `<x15:timelineRef r:id="${encodeXmlAttr(relationshipId)}"/>`).join('')}</x15:timelineRefs></ext>`,
  ].filter(Boolean)
}

const getSnapshotOoxmlAttachments = (
  snapshot: SpreadsheetWorkbookSnapshot,
): SpreadsheetExcelOoxmlArtifactManifestEntry[] => (snapshot.ooxml_attachments ?? [])
  .filter((artifact) => (
    artifact.preservation === 'non_authoritative_attachment' &&
    String(artifact.part_name ?? '').trim() &&
    String(artifact.payload_base64 ?? '').trim()
  ))
  .sort((left, right) => left.part_name.localeCompare(right.part_name))

const readAttachmentPayloadXml = (
  artifact: SpreadsheetExcelOoxmlArtifactManifestEntry,
): string | null => {
  const encoded = String(artifact.payload_base64 ?? '').trim()
  if (!encoded) return null
  try {
    return strFromU8(decodeBase64ToBytes(encoded))
  } catch {
    return null
  }
}

const getPivotCacheIdForAttachment = (
  artifact: SpreadsheetExcelOoxmlArtifactManifestEntry,
  fallbackIndex: number,
): string => {
  const markerCacheId = String(artifact.marker_attributes?.cacheId ?? '').trim()
  if (markerCacheId) return markerCacheId
  const payloadXml = readAttachmentPayloadXml(artifact)
  const payloadAttrs = parseAttributes(/<pivotCacheDefinition\b([^>]*)/i.exec(payloadXml ?? '')?.[1] ?? '')
  if (payloadAttrs.cacheId) return payloadAttrs.cacheId
  return /pivotCacheDefinition(\d+)\.xml$/i.exec(normalizePartName(artifact.part_name))?.[1] ?? String(fallbackIndex + 1)
}

const addExportRelationship = (
  relationshipsBySource: Map<string, Relationship[]>,
  sourcePartName: string,
  relationship: Relationship,
) => {
  const source = normalizePartName(sourcePartName)
  const relationships = relationshipsBySource.get(source) ?? []
  if (!relationships.some((entry) => (
    entry.id === relationship.id &&
    entry.type === relationship.type &&
    entry.target === relationship.target &&
    entry.target_mode === relationship.target_mode
  ))) {
    relationships.push(relationship)
    relationships.sort((left, right) => left.id.localeCompare(right.id))
  }
  relationshipsBySource.set(source, relationships)
}

const writeRelationshipsToFiles = (
  files: Record<string, Uint8Array>,
  relationshipsBySource: Map<string, Relationship[]>,
) => {
  for (const [sourcePartName, relationships] of relationshipsBySource) {
    if (relationships.length === 0) continue
    files[relationshipsPartNameFor(sourcePartName)] = strToU8(buildSheetRelationshipsXml(relationships.map((relationship) => ({
      id: relationship.id,
      type: relationship.type,
      target: relationship.target,
      targetMode: relationship.target_mode,
    }))))
  }
}

const addSnapshotOoxmlAttachmentsToPackage = (
  files: Record<string, Uint8Array>,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[],
) => {
  if (attachments.length === 0) return
  const relationshipsBySource = collectPackageRelationships(files)
  for (const artifact of attachments) {
    const partName = normalizePartName(artifact.part_name)
    files[partName] = decodeBase64ToBytes(String(artifact.payload_base64 ?? ''))
    if (
      artifact.source_part_name != null &&
      artifact.relationship_id &&
      artifact.relationship_type &&
      artifact.relationship_target
    ) {
      addExportRelationship(relationshipsBySource, artifact.source_part_name, {
        id: artifact.relationship_id,
        type: artifact.relationship_type,
        target: artifact.relationship_target,
        target_mode: artifact.relationship_target_mode ?? undefined,
      })
    }
    for (const relationship of artifact.relationships ?? []) {
      addExportRelationship(relationshipsBySource, partName, {
        id: relationship.id,
        type: relationship.type,
        target: relationship.target,
        target_mode: relationship.target_mode ?? undefined,
      })
    }
  }
  writeRelationshipsToFiles(files, relationshipsBySource)
}

const buildTableXml = (
  tableEntry: SnapshotTableExportEntry,
): string => {
  const { table, tableIndex } = tableEntry
  const rangeRef = formatRangeRef(table.range)
  const tableColumns: SpreadsheetTableColumnDefinition[] = table.columns && table.columns.length > 0
    ? table.columns
    : Array.from({ length: Math.max(1, table.range.end_col - table.range.start_col + 1) }, (_, index) => ({
      id: String(index + 1),
      name: `Column${index + 1}`,
    }))
  const headerRowCount = table.has_headers === false ? 0 : 1
  const totalsRowCount = table.show_totals === true ? 1 : 0
  const tableStyleInfo = table.style_id
    ? `
  <tableStyleInfo name="${encodeXmlAttr(table.style_id)}" showFirstColumn="${table.show_first_column === true ? '1' : '0'}" showLastColumn="${table.show_last_column === true ? '1' : '0'}" showRowStripes="${table.show_row_stripes === false ? '0' : '1'}" showColumnStripes="${table.show_column_stripes === true ? '1' : '0'}"/>`
    : ''
  const autoFilter = table.auto_filter != null
    ? buildAutoFilterXml(table.auto_filter)
    : table.show_filter_buttons === false || headerRowCount === 0 ? '' : `
  <autoFilter ref="${rangeRef}"/>`
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<table xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" id="${tableIndex}" name="${encodeXmlAttr(table.name)}" displayName="${encodeXmlAttr(table.name)}" ref="${rangeRef}" headerRowCount="${headerRowCount}" totalsRowCount="${totalsRowCount}">
${autoFilter}
  <tableColumns count="${tableColumns.length}">
${tableColumns.map((column, index) => {
  const totalsRowLabel = column.totals_row_label ? ` totalsRowLabel="${encodeXmlAttr(column.totals_row_label)}"` : ''
  const totalsRowFunction = column.totals_row_function ? ` totalsRowFunction="${encodeXmlAttr(column.totals_row_function)}"` : ''
  const formulas = [
    column.calculated_column_formula ? `<calculatedColumnFormula>${encodeXmlText(column.calculated_column_formula)}</calculatedColumnFormula>` : '',
    column.totals_row_formula ? `<totalsRowFormula>${encodeXmlText(column.totals_row_formula)}</totalsRowFormula>` : '',
  ].filter(Boolean).join('')
  const attrs = `id="${index + 1}" name="${encodeXmlAttr(column.name || `Column${index + 1}`)}"${totalsRowLabel}${totalsRowFunction}`
  return formulas ? `    <tableColumn ${attrs}>${formulas}</tableColumn>` : `    <tableColumn ${attrs}/>`
}).join('\n')}
  </tableColumns>${tableStyleInfo}
</table>`
}

const buildCommentsXml = (
  commentEntry: SnapshotCommentExportEntry,
): string => {
  const authors = Array.from(new Set(commentEntry.comments.map((comment) => comment.author)))
  const authorIndexByName = new Map(authors.map((author, index) => [author, index]))
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<comments xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <authors>
${authors.map((author) => `    <author>${encodeXmlText(author)}</author>`).join('\n')}
  </authors>
  <commentList>
${commentEntry.comments.map((comment) => `    <comment ref="${formatCellRef(comment.row, comment.col)}" authorId="${authorIndexByName.get(comment.author) ?? 0}"><text><t>${encodeXmlText(comment.text)}</t></text></comment>`).join('\n')}
  </commentList>
</comments>`
}

const buildVmlDrawingXml = (
  commentEntry: SnapshotCommentExportEntry,
): string => {
  const shapes = commentEntry.comments.map((comment, index) => {
    const anchor = Array.isArray(comment.metadata.shape_anchor)
      ? comment.metadata.shape_anchor.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : []
    const shapeAnchor = anchor.length > 0
      ? anchor.join(', ')
      : `${comment.col + 1}, 15, ${comment.row}, 2, ${comment.col + 3}, 31, ${comment.row + 4}, 6`
    const shapeId = String(comment.metadata.shape_id ?? `_x0000_s${1025 + index}`)
    const shapeStyle = String(comment.metadata.shape_style ?? 'position:absolute;visibility:hidden')
    const fillColor = String(comment.metadata.fill_color ?? '#ffffe1')
    // Synthesized comments carry no MoveWithCells/SizeWithCells flag; default to
    // both on (Excel's own behaviour for an authored note) so the box tracks its
    // cell. Imported geometry preserves whatever the source declared.
    const hasImportedGeometry = anchor.length > 0 || comment.metadata.shape_style != null
    const moveWithCells = comment.metadata.move_with_cells === true || (!hasImportedGeometry && comment.metadata.move_with_cells !== false)
    const sizeWithCells = comment.metadata.size_with_cells === true || (!hasImportedGeometry && comment.metadata.size_with_cells !== false)
    return `  <v:shape id="${encodeXmlAttr(shapeId)}" type="#_x0000_t202" style="${encodeXmlAttr(shapeStyle)}" fillcolor="${encodeXmlAttr(fillColor)}" o:insetmode="auto">
    <v:fill color2="${encodeXmlAttr(fillColor)}"/>
    <v:shadow on="t" color="black" obscured="t"/>
    <v:path o:connecttype="none"/>
    <v:textbox style="mso-direction-alt:auto"><div style="text-align:left"></div></v:textbox>
    <x:ClientData ObjectType="Note">
${moveWithCells ? '      <x:MoveWithCells/>\n' : ''}${sizeWithCells ? '      <x:SizeWithCells/>\n' : ''}      <x:Anchor>${encodeXmlText(shapeAnchor)}</x:Anchor>
      <x:AutoFill>False</x:AutoFill>
      <x:Row>${comment.row}</x:Row>
      <x:Column>${comment.col}</x:Column>
${comment.metadata.visible === true ? '      <x:Visible/>\n' : ''}    </x:ClientData>
  </v:shape>`
  })
  // The <o:shapelayout> + <v:shapetype id="_x0000_t202"> preamble is the standard
  // header Excel writes for every comment VML drawing; each comment box then
  // references the shapetype via type="#_x0000_t202".
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xml xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
  <o:shapelayout v:ext="edit">
    <o:idmap v:ext="edit" data="1"/>
  </o:shapelayout>
  <v:shapetype id="_x0000_t202" coordsize="21600,21600" o:spt="202" path="m,l,21600r21600,l21600,xe">
    <v:stroke joinstyle="miter"/>
    <v:path gradientshapeok="t" o:connecttype="rect"/>
  </v:shapetype>
${shapes.join('\n')}
</xml>`
}

const parseHyperlinkLines = (value: string | null | undefined): {
  target: string
  display?: string
  tooltip?: string
} | null => {
  const lines = String(value ?? '').split('\n').map((line) => line.trim()).filter(Boolean)
  const target = lines[0]
  if (!target) return null
  const out: { target: string; display?: string; tooltip?: string } = { target }
  for (const line of lines.slice(1)) {
    const [key, ...rest] = line.split('=')
    const valueText = rest.join('=').trim()
    if (key === 'display' && valueText) out.display = valueText
    if (key === 'tooltip' && valueText) out.tooltip = valueText
  }
  return out
}

const getCellHyperlinkMetadata = (
  record: SpreadsheetCellRecord,
): Record<string, unknown> | null => {
  const metadata = record.metadata && typeof record.metadata === 'object' ? record.metadata.ooxml_hyperlink : null
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata as Record<string, unknown> : null
}

type ExportStyleRecords = {
  xml: string
  themeXml: string | null
  styleIndexById: Map<string, number>
  dxfIndexByConditionalFormatId: Map<string, number>
  hasUnsupportedThemeColors: boolean
}

const stableUnknownStringify = (value: unknown): string => {
  if (value === undefined) return '__undefined__'
  if (Array.isArray(value)) return `[${value.map(stableUnknownStringify).join(',')}]`
  if (!value || typeof value !== 'object') return JSON.stringify(value)
  return `{${Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableUnknownStringify(entryValue)}`)
    .join(',')}}`
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const getInlineProtectionStyleId = (record: SpreadsheetCellRecord): string | null => {
  const protection = record.protection && typeof record.protection === 'object' && !Array.isArray(record.protection)
    ? record.protection as Record<string, unknown>
    : null
  if (protection == null || Object.keys(protection).length === 0) return null
  return `__fg_cell_protection::${String(record.style_id ?? '')}::${stableUnknownStringify(protection)}`
}

const getCellExportStyleId = (record: SpreadsheetCellRecord): string | null =>
  getInlineProtectionStyleId(record) ?? (record.style_id == null ? null : String(record.style_id))

const buildStylesForSnapshotExport = (
  baseStyles: Record<string, Record<string, unknown>>,
  sheets: SnapshotSheetExportEntry[],
): Record<string, Record<string, unknown>> => {
  const styles: Record<string, Record<string, unknown>> = { ...baseStyles }
  for (const { sheet } of sheets) {
    for (const { record } of iterSnapshotCells(sheet)) {
      const inlineStyleId = getInlineProtectionStyleId(record)
      if (inlineStyleId == null || styles[inlineStyleId] != null) continue
      const baseStyle = typeof record.style_id === 'string' && baseStyles[record.style_id] != null
        ? baseStyles[record.style_id]!
        : {}
      styles[inlineStyleId] = compactUnknownRecord({
        ...baseStyle,
        protection: record.protection,
      })
    }
  }
  return styles
}

const isProtectionOnlyStyle = (style: Record<string, unknown> | undefined): boolean => {
  if (style == null) return false
  const meaningfulKeys = Object.keys(compactUnknownRecord(style))
    .filter((key) => !['ooxml_style_index', 'numberFormat', 'font', 'fill'].includes(key))
  const hasOnlyDefaultNumberFormat = !isPlainObject(style.numberFormat) ||
    (style.numberFormat.ooxml_num_fmt_id === 0 && style.numberFormat.formatCode === 'General')
  const hasOnlyDefaultFont = !isPlainObject(style.font) ||
    (style.font.family === 'Calibri' && style.font.size === 11 && Object.keys(style.font).every((key) => key === 'family' || key === 'size'))
  const hasOnlyDefaultFill = !isPlainObject(style.fill) ||
    (style.fill.patternType === 'none' && Object.keys(style.fill).every((key) => key === 'patternType'))
  return meaningfulKeys.length === 1 &&
    meaningfulKeys[0] === 'protection' &&
    hasOnlyDefaultNumberFormat &&
    hasOnlyDefaultFont &&
    hasOnlyDefaultFill
}

const indexRecords = (records: Record<string, unknown>[]): { records: Record<string, unknown>[]; indexBySignature: Map<string, number> } => {
  const out: Record<string, unknown>[] = []
  const indexBySignature = new Map<string, number>()
  for (const record of records) {
    const signature = stableUnknownStringify(record)
    if (indexBySignature.has(signature)) continue
    indexBySignature.set(signature, out.length)
    out.push(record)
  }
  return { records: out, indexBySignature }
}

const getBuiltInNumberFormatId = (formatCode: string): number | null => {
  const normalized = formatCode.trim()
  for (const [id, code] of BUILT_IN_NUMBER_FORMATS.entries()) {
    if (code === normalized) return id
  }
  return null
}

const normalizeNumberFormatForExport = (
  value: unknown,
  customNumberFormatIds: Map<string, number>,
): Record<string, unknown> | undefined => {
  if (typeof value === 'string') {
    const formatCode = value.trim()
    if (!formatCode) return undefined
    const builtInId = getBuiltInNumberFormatId(formatCode)
    if (builtInId != null) return { ooxml_num_fmt_id: builtInId, formatCode }
    if (!customNumberFormatIds.has(formatCode)) customNumberFormatIds.set(formatCode, 164 + customNumberFormatIds.size)
    return { ooxml_num_fmt_id: customNumberFormatIds.get(formatCode), formatCode }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const record = value as Record<string, unknown>
  const formatCode = String(record.formatCode ?? '').trim()
  const explicitId = Number(record.ooxml_num_fmt_id)
  if (Number.isFinite(explicitId)) return compactUnknownRecord({ ...record, ooxml_num_fmt_id: explicitId })
  if (!formatCode) return compactUnknownRecord(record)
  const builtInId = getBuiltInNumberFormatId(formatCode)
  if (builtInId != null) return compactUnknownRecord({ ...record, ooxml_num_fmt_id: builtInId })
  if (!customNumberFormatIds.has(formatCode)) customNumberFormatIds.set(formatCode, 164 + customNumberFormatIds.size)
  return compactUnknownRecord({ ...record, ooxml_num_fmt_id: customNumberFormatIds.get(formatCode) })
}

const colorToOoxmlAttrs = (color: unknown): string => {
  if (typeof color === 'string') {
    const rgb = normalizeRgbColor(color)
    return rgb == null ? '' : `rgb="FF${rgb.replace(/^#/, '')}"`
  }
  if (!color || typeof color !== 'object' || Array.isArray(color)) return ''
  const record = color as Record<string, unknown>
  const attrs: string[] = []
  const rgb = normalizeRgbColor(String(record.rgb ?? ''))
  if (rgb != null) attrs.push(`rgb="FF${rgb.replace(/^#/, '')}"`)
  if (record.theme != null) attrs.push(`theme="${encodeXmlAttr(record.theme)}"`)
  if (record.tint != null && Number.isFinite(Number(record.tint))) attrs.push(`tint="${Number(record.tint)}"`)
  if (record.indexed != null) attrs.push(`indexed="${encodeXmlAttr(record.indexed)}"`)
  if (record.auto === true) attrs.push('auto="1"')
  return attrs.join(' ')
}

const colorToOoxmlElement = (tagName: string, color: unknown): string => {
  const attrs = colorToOoxmlAttrs(color)
  return attrs ? `<${tagName} ${attrs}/>` : ''
}

const collectThemeColorsFromValue = (
  value: unknown,
  themeColors: OoxmlThemeColors,
): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  let hasUnsupportedThemeColor = false
  if (record.theme != null) {
    const themeIndex = Number(record.theme)
    if (Number.isInteger(themeIndex) && themeIndex >= 0 && themeIndex < THEME_COLOR_ORDER.length) {
      if (!themeColors.has(themeIndex)) {
        const defaultColor = DEFAULT_OOXML_THEME_COLORS.get(themeIndex)
        const tint = Number(record.tint)
        const rgb = normalizeRgbColor(String(record.rgb ?? ''))
        const canUseRgbAsThemeBase = rgb != null && (!Number.isFinite(tint) || tint === 0)
        themeColors.set(themeIndex, {
          name: THEME_COLOR_ORDER[themeIndex] ?? defaultColor?.name ?? `theme${themeIndex}`,
          rgb: canUseRgbAsThemeBase ? rgb : defaultColor?.rgb ?? rgb ?? '#000000',
        })
      }
    } else {
      hasUnsupportedThemeColor = true
    }
  }
  for (const entryValue of Object.values(record)) {
    hasUnsupportedThemeColor = collectThemeColorsFromValue(entryValue, themeColors) || hasUnsupportedThemeColor
  }
  return hasUnsupportedThemeColor
}

const workbookThemeToThemeColors = (workbookTheme: SpreadsheetWorkbookTheme | null | undefined): OoxmlThemeColors => {
  const themeColors: OoxmlThemeColors = new Map()
  const colors = workbookTheme?.color_scheme?.colors
  if (colors == null) return themeColors
  THEME_COLOR_ORDER.forEach((name, index) => {
    const color = colors[name]
    const rgb = normalizeRgbColor(String(color?.rgb ?? color?.last_color ?? ''))
    if (rgb != null) themeColors.set(index, { name, rgb })
  })
  return themeColors
}

const buildThemeXml = (
  themeColors: OoxmlThemeColors,
  workbookTheme?: SpreadsheetWorkbookTheme | null,
): string => {
  const colorEntries = THEME_COLOR_ORDER.map((name, index) => {
    const color = themeColors.get(index) ?? DEFAULT_OOXML_THEME_COLORS.get(index)
    const rgb = (color?.rgb ?? '#000000').replace(/^#/, '').toUpperCase()
    if (name === 'lt1') return `      <a:${name}><a:sysClr val="window" lastClr="${rgb}"/></a:${name}>`
    if (name === 'dk1') return `      <a:${name}><a:sysClr val="windowText" lastClr="${rgb}"/></a:${name}>`
    return `      <a:${name}><a:srgbClr val="${rgb}"/></a:${name}>`
  }).join('\n')
  const themeName = encodeXmlAttr(workbookTheme?.name || 'FuseGrid')
  const colorSchemeName = encodeXmlAttr(workbookTheme?.color_scheme?.name || workbookTheme?.name || 'FuseGrid')
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" name="${themeName}">
  <a:themeElements>
    <a:clrScheme name="${colorSchemeName}">
${colorEntries}
    </a:clrScheme>
    <a:fontScheme name="FuseGrid">
      <a:majorFont><a:latin typeface="Aptos Display"/></a:majorFont>
      <a:minorFont><a:latin typeface="Aptos"/></a:minorFont>
    </a:fontScheme>
    <a:fmtScheme name="FuseGrid">
      <a:fillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:fillStyleLst>
      <a:lnStyleLst><a:ln w="6350" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln></a:lnStyleLst>
      <a:effectStyleLst><a:effectStyle><a:effectLst/></a:effectStyle></a:effectStyleLst>
      <a:bgFillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:bgFillStyleLst>
    </a:fmtScheme>
  </a:themeElements>
</a:theme>`
}

const protocolAutoFilterOperatorToOoxml = (operator: SpreadsheetAutoFilterOperator): string => {
  if (operator === 'not_equals') return 'notEqual'
  if (operator === 'greater_than') return 'greaterThan'
  if (operator === 'greater_than_or_equal') return 'greaterThanOrEqual'
  if (operator === 'less_than') return 'lessThan'
  if (operator === 'less_than_or_equal') return 'lessThanOrEqual'
  return 'equal'
}

const buildAutoFilterXml = (
  autoFilter: SpreadsheetAutoFilter | null | undefined,
): string => {
  if (autoFilter == null) return ''
  const rangeRef = formatRangeRef(autoFilter.range)
  const columns = (autoFilter.columns ?? [])
    .filter((column) => Number.isInteger(column.col) && column.col >= autoFilter.range.start_col && column.col <= autoFilter.range.end_col)
    .sort((left, right) => left.col - right.col)
    .map((column) => {
      const colId = column.col - autoFilter.range.start_col
      const values = (column.values ?? []).map((value) => String(value))
      const filters = values.length > 0 || column.blank === true
        ? `<filters${column.blank === true ? ' blank="1"' : ''}>${values.map((value) => `<filter val="${encodeXmlAttr(value)}"/>`).join('')}</filters>`
        : ''
      const conditions = (column.conditions ?? []).slice(0, 2)
      const customFilters = conditions.length > 0
        ? `<customFilters${conditions.length > 1 && column.condition_join === 'and' ? ' and="1"' : ''}>${conditions.map((condition) => `<customFilter operator="${protocolAutoFilterOperatorToOoxml(condition.operator)}" val="${encodeXmlAttr(condition.value)}"/>`).join('')}</customFilters>`
        : ''
      const body = `${filters}${customFilters}`
      return body ? `    <filterColumn colId="${colId}">${body}</filterColumn>` : `    <filterColumn colId="${colId}"/>`
    })
  const sortState = buildAutoFilterSortStateXml(autoFilter.sort_state)
  const body = [...columns, sortState].filter(Boolean).join('\n')
  return body.length === 0
    ? `
  <autoFilter ref="${rangeRef}"/>`
    : `
  <autoFilter ref="${rangeRef}">
${body}
  </autoFilter>`
}

const buildAutoFilterSortStateXml = (
  sortState: SpreadsheetAutoFilterSortState | null | undefined,
): string => {
  if (sortState == null) return ''
  const ref = sortState.ref == null ? null : formatRangeRef(sortState.ref)
  const attrs = [
    ref ? `ref="${ref}"` : '',
    sortState.case_sensitive === true ? 'caseSensitive="1"' : '',
    sortState.column_sort === true ? 'columnSort="1"' : '',
  ].filter(Boolean).join(' ')
  const conditions = (sortState.conditions ?? [])
    .filter((condition) => condition.ref != null)
    .map((condition) => {
      const conditionAttrs = [
        `ref="${formatRangeRef(condition.ref)}"`,
        condition.descending === true ? 'descending="1"' : '',
        condition.sort_by ? `sortBy="${encodeXmlAttr(condition.sort_by)}"` : '',
        condition.custom_list ? `customList="${encodeXmlAttr(condition.custom_list)}"` : '',
        condition.dxf_id ? `dxfId="${encodeXmlAttr(condition.dxf_id)}"` : '',
        condition.icon_set ? `iconSet="${encodeXmlAttr(condition.icon_set)}"` : '',
        condition.icon_id ? `iconId="${encodeXmlAttr(condition.icon_id)}"` : '',
      ].filter(Boolean).join(' ')
      return `      <sortCondition ${conditionAttrs}/>`
    })
  return conditions.length === 0
    ? `    <sortState${attrs ? ` ${attrs}` : ''}/>`
    : `    <sortState${attrs ? ` ${attrs}` : ''}>
${conditions.join('\n')}
    </sortState>`
}

const fontToOoxml = (font: Record<string, unknown>): string => [
  '<font>',
  font.bold === true ? '<b/>' : '',
  font.italic === true ? '<i/>' : '',
  font.strikethrough === true ? '<strike/>' : '',
  font.underline ? `<u${typeof font.underline === 'string' ? ` val="${encodeXmlAttr(font.underline)}"` : ''}/>` : '',
  colorToOoxmlElement('color', font.color),
  Number.isFinite(Number(font.size)) ? `<sz val="${Number(font.size)}"/>` : '',
  font.family ? `<name val="${encodeXmlAttr(font.family)}"/>` : '',
  '</font>',
].join('')

const fillToOoxml = (fill: Record<string, unknown>): string => {
  const hasColor = fill.foregroundColor != null || fill.backgroundColor != null
  const declaredPatternType = String(fill.patternType ?? fill.kind ?? '').trim()
  const patternType = declaredPatternType === 'none' && hasColor
    ? 'solid'
    : declaredPatternType || (hasColor ? 'solid' : 'none')
  const foreground = colorToOoxmlElement('fgColor', fill.foregroundColor)
  const background = colorToOoxmlElement('bgColor', fill.backgroundColor)
  return `<fill><patternFill patternType="${encodeXmlAttr(patternType)}">${foreground}${background}</patternFill></fill>`
}

const borderEdgeToOoxml = (edgeName: string, edge: unknown): string => {
  if (!edge || typeof edge !== 'object' || Array.isArray(edge)) return `<${edgeName}/>`
  const record = edge as Record<string, unknown>
  const style = record.style ? ` style="${encodeXmlAttr(record.style)}"` : ''
  const color = colorToOoxmlElement('color', record.color)
  return color ? `<${edgeName}${style}>${color}</${edgeName}>` : `<${edgeName}${style}/>`
}

const borderToOoxml = (border: Record<string, unknown>): string => `<border>${[
  'left',
  'right',
  'top',
  'bottom',
  'diagonal',
].map((edge) => borderEdgeToOoxml(edge, border[edge])).join('')}</border>`

const normalizeDxfStyle = (style: Record<string, unknown>): Record<string, unknown> => compactUnknownRecord({
  numberFormat: style.numberFormat,
  font: style.font,
  fill: style.fill,
  border: style.border,
  protection: style.protection,
})

const normalizeExportStyle = (
  style: Record<string, unknown>,
  customNumberFormatIds: Map<string, number>,
): Record<string, unknown> => compactUnknownRecord({
  ...style,
  numberFormat: normalizeNumberFormatForExport(style.numberFormat, customNumberFormatIds),
})

const verticalAlignmentToOoxml = (value: unknown): string | null => {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (!raw) return null
  if (raw === 'middle') return 'center'
  if (raw === 'top' || raw === 'center' || raw === 'bottom' || raw === 'justify' || raw === 'distributed') return raw
  return null
}

const dxfToOoxml = (style: Record<string, unknown>): string => {
  const numberFormat = style.numberFormat && typeof style.numberFormat === 'object' && !Array.isArray(style.numberFormat)
    ? style.numberFormat as Record<string, unknown>
    : null
  const numFmtId = Number(numberFormat?.ooxml_num_fmt_id)
  const formatCode = String(numberFormat?.formatCode ?? '')
  const numFmt = Number.isFinite(numFmtId) && formatCode
    ? `<numFmt numFmtId="${numFmtId}" formatCode="${encodeXmlAttr(formatCode)}"/>`
    : ''
  const font = style.font && typeof style.font === 'object' && !Array.isArray(style.font)
    ? fontToOoxml(style.font as Record<string, unknown>)
    : ''
  const fill = style.fill && typeof style.fill === 'object' && !Array.isArray(style.fill)
    ? fillToOoxml(style.fill as Record<string, unknown>)
    : ''
  const border = style.border && typeof style.border === 'object' && !Array.isArray(style.border)
    ? borderToOoxml(style.border as Record<string, unknown>)
    : ''
  const protection = style.protection && typeof style.protection === 'object' && !Array.isArray(style.protection)
    ? style.protection as Record<string, unknown>
    : null
  const protectionXml = protection == null ? '' : `<protection ${[
    protection.locked != null ? `locked="${protection.locked === false ? 0 : 1}"` : '',
    protection.hidden != null ? `hidden="${protection.hidden === true ? 1 : 0}"` : '',
  ].filter(Boolean).join(' ')}/>`
  return `<dxf>${numFmt}${font}${fill}${border}${protectionXml}</dxf>`
}

const tableStyleElementDxfStyle = (
  element: NonNullable<SpreadsheetTableStyleDefinition['elements']>[number],
  styles: Record<string, Record<string, unknown>>,
  customNumberFormatIds: Map<string, number>,
): Record<string, unknown> => {
  if (element.style && typeof element.style === 'object' && !Array.isArray(element.style)) {
    return normalizeExportStyle(normalizeDxfStyle(element.style), customNumberFormatIds)
  }
  if (element.dxf_id && styles[element.dxf_id] != null) {
    return normalizeExportStyle(normalizeDxfStyle(styles[element.dxf_id]!), customNumberFormatIds)
  }
  return {}
}

const buildTableStylesXml = (
  tableStyles: Record<string, SpreadsheetTableStyleDefinition>,
  styles: Record<string, Record<string, unknown>>,
  dxfRecords: { records: Record<string, unknown>[]; indexBySignature: Map<string, number> },
  customNumberFormatIds: Map<string, number>,
): string => {
  const entries = Object.values(tableStyles)
    .filter((style) => style.name.trim())
    .sort((left, right) => left.name.localeCompare(right.name))
  if (entries.length === 0) return ''
  return `  <tableStyles count="${entries.length}">
${entries.map((style) => {
  const attrs = [
    `name="${encodeXmlAttr(style.name)}"`,
    style.is_pivot != null ? `pivot="${style.is_pivot ? '1' : '0'}"` : '',
    style.is_table != null ? `table="${style.is_table ? '1' : '0'}"` : '',
    `count="${style.elements?.length ?? 0}"`,
  ].filter(Boolean).join(' ')
  const elements = (style.elements ?? []).map((element) => {
    const dxfStyle = tableStyleElementDxfStyle(element, styles, customNumberFormatIds)
    const dxfIndex = Object.keys(dxfStyle).length === 0 ? null : dxfRecords.indexBySignature.get(stableUnknownStringify(dxfStyle))
    const elementAttrs = [
      `type="${encodeXmlAttr(element.type)}"`,
      element.size != null ? `size="${element.size}"` : '',
      dxfIndex != null ? `dxfId="${dxfIndex}"` : '',
    ].filter(Boolean).join(' ')
    return `    <tableStyleElement ${elementAttrs}/>`
  })
  return elements.length === 0
    ? `  <tableStyle ${attrs}/>`
    : `  <tableStyle ${attrs}>
${elements.join('\n')}
  </tableStyle>`
}).join('\n')}
  </tableStyles>`
}

const buildStylesXmlFromSnapshot = (
  styles: Record<string, Record<string, unknown>>,
  conditionalFormats: Record<string, Record<string, unknown>> = {},
  tableStyles: Record<string, SpreadsheetTableStyleDefinition> = {},
  workbookTheme?: SpreadsheetWorkbookTheme | null,
): ExportStyleRecords => {
  const defaultFont = { size: 11, family: 'Calibri' }
  const defaultFill = { patternType: 'none' }
  const grayFill = { patternType: 'gray125' }
  const defaultBorder: Record<string, unknown> = {}
  const customNumberFormatIds = new Map<string, number>()
  const orderedStyles = Object.entries(styles)
    .sort(([leftId, left], [rightId, right]) => {
      const leftIndex = Number(left.ooxml_style_index)
      const rightIndex = Number(right.ooxml_style_index)
      if (Number.isFinite(leftIndex) && Number.isFinite(rightIndex) && leftIndex !== rightIndex) return leftIndex - rightIndex
      if (Number.isFinite(leftIndex)) return -1
      if (Number.isFinite(rightIndex)) return 1
      return leftId.localeCompare(rightId)
    })
  const normalizedStyles = orderedStyles.map(([styleId, style]) => [
    styleId,
    normalizeExportStyle(style, customNumberFormatIds),
  ] as const)
  const fontsSource = [defaultFont, ...normalizedStyles.map(([, style]) => style.font).filter((value): value is Record<string, unknown> => (
    value != null && typeof value === 'object' && !Array.isArray(value)
  ))]
  const fillsSource = [defaultFill, grayFill, ...normalizedStyles.map(([, style]) => style.fill).filter((value): value is Record<string, unknown> => (
    value != null && typeof value === 'object' && !Array.isArray(value)
  ))]
  const bordersSource = [defaultBorder, ...normalizedStyles.map(([, style]) => style.border).filter((value): value is Record<string, unknown> => (
    value != null && typeof value === 'object' && !Array.isArray(value)
  ))]
  const fonts = indexRecords(fontsSource)
  const fills = indexRecords(fillsSource)
  const borders = indexRecords(bordersSource)
  const styleIndexById = new Map<string, number>()
  const numFmts = new Map<number, string>()
  const dxfIndexByConditionalFormatId = new Map<string, number>()
  const conditionalFormatStyleRecords = Object.entries(conditionalFormats)
    .sort(([leftId], [rightId]) => leftId.localeCompare(rightId))
    .map(([conditionalFormatId, rule]) => {
      const format = rule.format && typeof rule.format === 'object' && !Array.isArray(rule.format)
          ? normalizeExportStyle(normalizeDxfStyle(rule.format as Record<string, unknown>), customNumberFormatIds)
        : typeof rule.style_id === 'string' && styles[rule.style_id] != null
          ? normalizeExportStyle(normalizeDxfStyle(styles[rule.style_id]!), customNumberFormatIds)
          : {}
      return format
    })
    .filter((record) => Object.keys(record).length > 0)
  const tableStyleRecords = Object.values(tableStyles)
    .flatMap((tableStyle) => tableStyle.elements ?? [])
    .map((element) => tableStyleElementDxfStyle(element, styles, customNumberFormatIds))
    .filter((record) => Object.keys(record).length > 0)
  const dxfRecords = indexRecords([...conditionalFormatStyleRecords, ...tableStyleRecords])
  for (const [conditionalFormatId, rule] of Object.entries(conditionalFormats)) {
    const format = rule.format && typeof rule.format === 'object' && !Array.isArray(rule.format)
      ? normalizeExportStyle(normalizeDxfStyle(rule.format as Record<string, unknown>), customNumberFormatIds)
      : typeof rule.style_id === 'string' && styles[rule.style_id] != null
        ? normalizeExportStyle(normalizeDxfStyle(styles[rule.style_id]!), customNumberFormatIds)
        : {}
    const dxfIndex = dxfRecords.indexBySignature.get(stableUnknownStringify(format))
    if (dxfIndex != null) dxfIndexByConditionalFormatId.set(conditionalFormatId, dxfIndex)
  }
  const isDefaultCellStyle = (style: Record<string, unknown>): boolean => {
    const numberFormat = style.numberFormat && typeof style.numberFormat === 'object' && !Array.isArray(style.numberFormat)
      ? style.numberFormat as Record<string, unknown>
      : null
    const numberFormatId = Number(numberFormat?.ooxml_num_fmt_id ?? 0)
    const numberFormatCode = String(numberFormat?.formatCode ?? 'General')
    const font = style.font && typeof style.font === 'object' && !Array.isArray(style.font)
      ? style.font as Record<string, unknown>
      : defaultFont
    const fill = style.fill && typeof style.fill === 'object' && !Array.isArray(style.fill)
      ? style.fill as Record<string, unknown>
      : defaultFill
    const border = style.border && typeof style.border === 'object' && !Array.isArray(style.border)
      ? style.border as Record<string, unknown>
      : defaultBorder
    const alignment = style.alignment && typeof style.alignment === 'object' && !Array.isArray(style.alignment)
      ? style.alignment as Record<string, unknown>
      : null
    const protection = style.protection && typeof style.protection === 'object' && !Array.isArray(style.protection)
      ? style.protection as Record<string, unknown>
      : null
    return numberFormatId === 0 && (numberFormatCode === '' || numberFormatCode === 'General') &&
      stableUnknownStringify(font) === stableUnknownStringify(defaultFont) &&
      stableUnknownStringify(fill) === stableUnknownStringify(defaultFill) &&
      stableUnknownStringify(border) === stableUnknownStringify(defaultBorder) &&
      (alignment == null || Object.keys(alignment).length === 0) &&
      (protection == null || Object.keys(protection).length === 0)
  }
  const customStyles = normalizedStyles.filter(([styleId, style]) => {
    if (!isDefaultCellStyle(style)) return true
    styleIndexById.set(styleId, 0)
    return false
  })
  const cellXfs = customStyles.map(([styleId, style], index) => {
    styleIndexById.set(styleId, index + 1)
    const numberFormat = style.numberFormat && typeof style.numberFormat === 'object' && !Array.isArray(style.numberFormat)
      ? style.numberFormat as Record<string, unknown>
      : {}
    const numFmtId = Number(numberFormat.ooxml_num_fmt_id ?? 0)
    const formatCode = String(numberFormat.formatCode ?? '')
    if (Number.isFinite(numFmtId) && numFmtId >= 164 && formatCode) numFmts.set(numFmtId, formatCode)
    const fontId = fonts.indexBySignature.get(stableUnknownStringify(style.font ?? defaultFont)) ?? 0
    const fillId = fills.indexBySignature.get(stableUnknownStringify(style.fill ?? defaultFill)) ?? 0
    const borderId = borders.indexBySignature.get(stableUnknownStringify(style.border ?? defaultBorder)) ?? 0
    const alignment = style.alignment && typeof style.alignment === 'object' && !Array.isArray(style.alignment)
      ? style.alignment as Record<string, unknown>
      : null
    const protection = style.protection && typeof style.protection === 'object' && !Array.isArray(style.protection)
      ? style.protection as Record<string, unknown>
      : null
    const verticalAlignment = alignment == null ? null : verticalAlignmentToOoxml(alignment.vertical)
    const alignmentAttrs = alignment == null ? '' : [
      alignment.horizontal ? `horizontal="${encodeXmlAttr(alignment.horizontal)}"` : '',
      verticalAlignment ? `vertical="${encodeXmlAttr(verticalAlignment)}"` : '',
      alignment.wrapText === true ? 'wrapText="1"' : '',
      Number.isFinite(Number(alignment.indent)) ? `indent="${Number(alignment.indent)}"` : '',
    ].filter(Boolean).join(' ')
    const protectionAttrs = protection == null ? '' : [
      protection.locked != null ? `locked="${protection.locked === false ? 0 : 1}"` : '',
      protection.hidden != null ? `hidden="${protection.hidden === true ? 1 : 0}"` : '',
    ].filter(Boolean).join(' ')
    const children = [
      alignmentAttrs ? `<alignment ${alignmentAttrs}/>` : '',
      protectionAttrs ? `<protection ${protectionAttrs}/>` : '',
    ].filter(Boolean).join('')
    const attrs = [
      `numFmtId="${Number.isFinite(numFmtId) ? numFmtId : 0}"`,
      `fontId="${fontId}"`,
      `fillId="${fillId}"`,
      `borderId="${borderId}"`,
      'xfId="0"',
      style.font ? 'applyFont="1"' : '',
      style.fill ? 'applyFill="1"' : '',
      style.border ? 'applyBorder="1"' : '',
      alignmentAttrs ? 'applyAlignment="1"' : '',
      protectionAttrs ? 'applyProtection="1"' : '',
    ].filter(Boolean).join(' ')
    return children ? `<xf ${attrs}>${children}</xf>` : `<xf ${attrs}/>`
  })
  const themeColors: OoxmlThemeColors = workbookThemeToThemeColors(workbookTheme)
  let hasThemeColors = themeColors.size > 0
  let hasUnsupportedThemeColors = false
  for (const [, style] of orderedStyles) {
    if (stableUnknownStringify(style).includes('"theme"')) {
      hasThemeColors = true
      hasUnsupportedThemeColors = collectThemeColorsFromValue(style, themeColors) || hasUnsupportedThemeColors
    }
  }
  for (const style of conditionalFormatStyleRecords) {
    if (stableUnknownStringify(style).includes('"theme"')) {
      hasThemeColors = true
      hasUnsupportedThemeColors = collectThemeColorsFromValue(style, themeColors) || hasUnsupportedThemeColors
    }
  }
  for (const style of tableStyleRecords) {
    if (stableUnknownStringify(style).includes('"theme"')) {
      hasThemeColors = true
      hasUnsupportedThemeColors = collectThemeColorsFromValue(style, themeColors) || hasUnsupportedThemeColors
    }
  }
  const tableStylesXml = buildTableStylesXml(tableStyles, styles, dxfRecords, customNumberFormatIds)
  return {
    styleIndexById,
    dxfIndexByConditionalFormatId,
    themeXml: hasThemeColors ? buildThemeXml(themeColors, workbookTheme) : null,
    hasUnsupportedThemeColors,
    xml: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
${numFmts.size === 0 ? '' : `  <numFmts count="${numFmts.size}">${Array.from(numFmts.entries()).sort(([left], [right]) => left - right).map(([id, code]) => `<numFmt numFmtId="${id}" formatCode="${encodeXmlAttr(code)}"/>`).join('')}</numFmts>`}
  <fonts count="${fonts.records.length}">${fonts.records.map(fontToOoxml).join('')}</fonts>
  <fills count="${fills.records.length}">${fills.records.map(fillToOoxml).join('')}</fills>
  <borders count="${borders.records.length}">${borders.records.map(borderToOoxml).join('')}</borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="${cellXfs.length + 1}"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>${cellXfs.join('')}</cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
${dxfRecords.records.length === 0 ? '' : `  <dxfs count="${dxfRecords.records.length}">${dxfRecords.records.map(dxfToOoxml).join('')}</dxfs>`}
${tableStylesXml || '  <tableStyles count="0" defaultTableStyle="TableStyleMedium2" defaultPivotStyle="PivotStyleLight16"/>'}
</styleSheet>`,
  }
}

const validationKindToOoxmlType = (kind: SpreadsheetValidationRule['kind']): string => {
  if (kind === 'wholeNumber') return 'whole'
  return kind
}

const validationRuleToOoxmlFormula = (rule: SpreadsheetValidationRule, key: 'formula1' | 'formula2'): string => {
  if (key === 'formula1' && rule.kind === 'list' && Array.isArray(rule.values) && rule.values.length > 0) {
    return `"${rule.values.map((value) => String(value).replace(/"/g, '""')).join(',')}"`
  }
  return String(rule[key] ?? '')
}

const buildDataValidationsXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
  validationRules: Record<string, SpreadsheetValidationRule>,
): string => {
  const refsByValidationId = new Map<string, string[]>()
  for (const { row, col, record } of iterSnapshotCells(sheet)) {
    const validationId = String(record.validation_id ?? '').trim()
    if (!validationId || validationRules[validationId] == null) continue
    refsByValidationId.set(validationId, [...(refsByValidationId.get(validationId) ?? []), formatCellRef(row, col)])
  }
  const entries = Array.from(refsByValidationId.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([validationId, refs]) => {
      const rule = validationRules[validationId]!
      const formula1 = validationRuleToOoxmlFormula(rule, 'formula1')
      const formula2 = validationRuleToOoxmlFormula(rule, 'formula2')
      const attrs = [
        `type="${validationKindToOoxmlType(rule.kind)}"`,
        rule.operator && rule.kind !== 'list' && rule.kind !== 'custom' ? `operator="${rule.operator}"` : '',
        rule.allow_blank ? 'allowBlank="1"' : '',
        rule.error_style ? `errorStyle="${encodeXmlAttr(rule.error_style)}"` : '',
        rule.input_title ? `promptTitle="${encodeXmlAttr(rule.input_title)}"` : '',
        rule.input_message ? `prompt="${encodeXmlAttr(rule.input_message)}"` : '',
        rule.error_title ? `errorTitle="${encodeXmlAttr(rule.error_title)}"` : '',
        rule.error_message ? `error="${encodeXmlAttr(rule.error_message)}"` : '',
        `sqref="${refs.sort((left, right) => left.localeCompare(right)).join(' ')}"`,
      ].filter(Boolean).join(' ')
      const formulas = [
        formula1 ? `<formula1>${encodeXmlText(formula1)}</formula1>` : '',
        formula2 ? `<formula2>${encodeXmlText(formula2)}</formula2>` : '',
      ].filter(Boolean).join('')
      return formulas ? `    <dataValidation ${attrs}>${formulas}</dataValidation>` : `    <dataValidation ${attrs}/>`
    })
  return entries.length === 0 ? '' : `
  <dataValidations count="${entries.length}">
${entries.join('\n')}
  </dataValidations>`
}

const buildSheetProtectionXml = (
  protection: SpreadsheetSheetProtectionOptions | null | undefined,
): string => {
  if (protection == null || protection.enabled === false) return ''
  const attrs = [
    'sheet="1"',
    protection.allow_select_locked_cells === true ? 'selectLockedCells="1"' : '',
    protection.allow_select_unlocked_cells === true ? 'selectUnlockedCells="1"' : '',
    protection.allow_format_cells === true ? 'formatCells="0"' : '',
    protection.allow_resize_rows === true ? 'formatRows="0"' : '',
    protection.allow_resize_columns === true ? 'formatColumns="0"' : '',
    protection.allow_insert_rows === true ? 'insertRows="0"' : '',
    protection.allow_insert_columns === true ? 'insertColumns="0"' : '',
    protection.allow_delete_rows === true ? 'deleteRows="0"' : '',
    protection.allow_delete_columns === true ? 'deleteColumns="0"' : '',
    protection.ooxml_algorithm != null ? `algorithmName="${encodeXmlAttr(protection.ooxml_algorithm)}"` : '',
    protection.ooxml_hash_value != null ? `hashValue="${encodeXmlAttr(protection.ooxml_hash_value)}"` : '',
    protection.ooxml_salt_value != null ? `saltValue="${encodeXmlAttr(protection.ooxml_salt_value)}"` : '',
    protection.ooxml_spin_count != null ? `spinCount="${encodeXmlAttr(protection.ooxml_spin_count)}"` : '',
  ].filter(Boolean).join(' ')
  return `
  <sheetProtection ${attrs}/>`
}

const buildProtectedRangesXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const entries = (sheet.protected_ranges ?? [])
    .filter((entry) => String(entry.range ?? '').trim().length > 0)
    .map((entry) => {
      const attrs = [
        entry.name ? `name="${encodeXmlAttr(entry.name)}"` : '',
        `sqref="${encodeXmlAttr(entry.range)}"`,
        entry.security_descriptor ? `securityDescriptor="${encodeXmlAttr(entry.security_descriptor)}"` : '',
        entry.password_hash ? `password="${encodeXmlAttr(entry.password_hash)}"` : '',
        entry.algorithm ? `algorithmName="${encodeXmlAttr(entry.algorithm)}"` : '',
        entry.hash_value ? `hashValue="${encodeXmlAttr(entry.hash_value)}"` : '',
        entry.salt_value ? `saltValue="${encodeXmlAttr(entry.salt_value)}"` : '',
        entry.spin_count != null ? `spinCount="${encodeXmlAttr(entry.spin_count)}"` : '',
      ].filter(Boolean).join(' ')
      return `    <protectedRange ${attrs}/>`
    })
  if (entries.length === 0) return ''
  return `
  <protectedRanges>
${entries.join('\n')}
  </protectedRanges>`
}

const buildSheetPropertiesXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const tabColorAttrs = colorToOoxmlAttrs(sheet.tab_color)
  const formatProperties = sheet.sheet_format_properties
  const outlineAttrs = formatProperties == null ? '' : [
    formatProperties.outline_summary_below != null ? `summaryBelow="${formatProperties.outline_summary_below ? '1' : '0'}"` : '',
    formatProperties.outline_summary_right != null ? `summaryRight="${formatProperties.outline_summary_right ? '1' : '0'}"` : '',
    formatProperties.show_outline_symbols != null ? `showOutlineSymbols="${formatProperties.show_outline_symbols ? '1' : '0'}"` : '',
    formatProperties.apply_outline_styles != null ? `applyStyles="${formatProperties.apply_outline_styles ? '1' : '0'}"` : '',
  ].filter(Boolean).join(' ')
  const fitToPage = sheet.print_settings?.page_setup?.fit_to_page
  const pageSetupPropertiesXml = fitToPage == null ? '' : `<pageSetUpPr fitToPage="${fitToPage ? '1' : '0'}"/>`
  if (!tabColorAttrs && !outlineAttrs && !pageSetupPropertiesXml) return ''
  return `
  <sheetPr>${tabColorAttrs ? `<tabColor ${tabColorAttrs}/>` : ''}${outlineAttrs ? `<outlinePr ${outlineAttrs}/>` : ''}${pageSetupPropertiesXml}</sheetPr>`
}

const buildSheetFormatPropertiesXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const formatProperties = sheet.sheet_format_properties
  if (formatProperties == null) return ''
  const defaultRowHeight = Number(formatProperties.default_row_height_px)
  const defaultColWidth = Number(formatProperties.default_col_width_px)
  const attrs = [
    Number.isFinite(defaultRowHeight) && defaultRowHeight >= 0 ? `defaultRowHeight="${Math.round(defaultRowHeight * 0.75 * 100) / 100}"` : '',
    Number.isFinite(defaultColWidth) && defaultColWidth >= 0 ? `defaultColWidth="${Math.round((defaultColWidth / 7) * 100) / 100}"` : '',
    formatProperties.base_col_width != null ? `baseColWidth="${formatProperties.base_col_width}"` : '',
    formatProperties.zero_height != null ? `zeroHeight="${formatProperties.zero_height ? '1' : '0'}"` : '',
    formatProperties.thick_top != null ? `thickTop="${formatProperties.thick_top ? '1' : '0'}"` : '',
    formatProperties.thick_bottom != null ? `thickBottom="${formatProperties.thick_bottom ? '1' : '0'}"` : '',
    formatProperties.outline_level_row != null ? `outlineLevelRow="${formatProperties.outline_level_row}"` : '',
    formatProperties.outline_level_col != null ? `outlineLevelCol="${formatProperties.outline_level_col}"` : '',
  ].filter(Boolean).join(' ')
  return attrs ? `
  <sheetFormatPr ${attrs}/>` : ''
}

const buildSheetCalculationPropertiesXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const properties = sheet.calculation_properties
  if (properties == null) return ''
  const attrs = [
    properties.full_calc_on_load != null ? `fullCalcOnLoad="${properties.full_calc_on_load ? '1' : '0'}"` : '',
  ].filter(Boolean).join(' ')
  return attrs ? `
  <sheetCalcPr ${attrs}/>` : ''
}

const buildSheetViewsXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const hasFrozenPanes = sheet.frozen_panes && (sheet.frozen_panes.rows > 0 || sheet.frozen_panes.cols > 0)
  const sourceViews = sheet.sheet_views && sheet.sheet_views.length > 0
    ? sheet.sheet_views
    : hasFrozenPanes ? [{ workbook_view_id: 0 }] : []
  if (sourceViews.length === 0) return ''
  const views = sourceViews.map((view, index) => {
    const attrs = [
      `workbookViewId="${view.workbook_view_id ?? 0}"`,
      view.show_grid_lines != null ? `showGridLines="${view.show_grid_lines ? '1' : '0'}"` : '',
      view.show_row_col_headers != null ? `showRowColHeaders="${view.show_row_col_headers ? '1' : '0'}"` : '',
      view.show_formulas != null ? `showFormulas="${view.show_formulas ? '1' : '0'}"` : '',
      view.right_to_left != null ? `rightToLeft="${view.right_to_left ? '1' : '0'}"` : '',
      view.tab_selected != null ? `tabSelected="${view.tab_selected ? '1' : '0'}"` : '',
      view.zoom_scale != null ? `zoomScale="${view.zoom_scale}"` : '',
      view.zoom_scale_normal != null ? `zoomScaleNormal="${view.zoom_scale_normal}"` : '',
      view.top_left_cell ? `topLeftCell="${encodeXmlAttr(view.top_left_cell)}"` : '',
    ].filter(Boolean).join(' ')
    const pane = index === 0 && hasFrozenPanes ? `<pane xSplit="${sheet.frozen_panes!.cols}" ySplit="${sheet.frozen_panes!.rows}" topLeftCell="${formatCellRef(sheet.frozen_panes!.rows, sheet.frozen_panes!.cols)}" activePane="bottomRight" state="frozen"/>` : ''
    const selectionAttrs = [
      view.active_cell ? `activeCell="${encodeXmlAttr(view.active_cell)}"` : '',
      view.selection_sqref ? `sqref="${encodeXmlAttr(view.selection_sqref)}"` : '',
    ].filter(Boolean).join(' ')
    const selection = selectionAttrs ? `<selection ${selectionAttrs}/>` : ''
    return `    <sheetView ${attrs}>${pane}${selection}</sheetView>`
  })
  return `
  <sheetViews>
${views.join('\n')}
  </sheetViews>`
}

const buildCustomSheetViewsXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const entries = (sheet.custom_sheet_views ?? [])
    .map((view) => {
      const attrs = [
        view.guid ? `guid="${encodeXmlAttr(view.guid)}"` : '',
        view.name ? `name="${encodeXmlAttr(view.name)}"` : '',
        view.scale != null ? `scale="${view.scale}"` : '',
        view.color_id != null ? `colorId="${view.color_id}"` : '',
        view.show_page_breaks != null ? `showPageBreaks="${view.show_page_breaks ? '1' : '0'}"` : '',
        view.show_formulas != null ? `showFormulas="${view.show_formulas ? '1' : '0'}"` : '',
        view.show_grid_lines != null ? `showGridLines="${view.show_grid_lines ? '1' : '0'}"` : '',
        view.show_row_col_headers != null ? `showRowCol="${view.show_row_col_headers ? '1' : '0'}"` : '',
        view.outline_symbols != null ? `outlineSymbols="${view.outline_symbols ? '1' : '0'}"` : '',
        view.zero_values != null ? `zeroValues="${view.zero_values ? '1' : '0'}"` : '',
        view.fit_to_page != null ? `fitToPage="${view.fit_to_page ? '1' : '0'}"` : '',
        view.print_area != null ? `printArea="${view.print_area ? '1' : '0'}"` : '',
        view.filter != null ? `filter="${view.filter ? '1' : '0'}"` : '',
        view.hidden_rows != null ? `hiddenRows="${view.hidden_rows ? '1' : '0'}"` : '',
        view.hidden_columns != null ? `hiddenColumns="${view.hidden_columns ? '1' : '0'}"` : '',
        view.state ? `state="${encodeXmlAttr(view.state)}"` : '',
        view.top_left_cell ? `topLeftCell="${encodeXmlAttr(view.top_left_cell)}"` : '',
      ].filter(Boolean).join(' ')
      const selectionAttrs = [
        view.active_cell ? `activeCell="${encodeXmlAttr(view.active_cell)}"` : '',
        view.selection_sqref ? `sqref="${encodeXmlAttr(view.selection_sqref)}"` : '',
      ].filter(Boolean).join(' ')
      const selection = selectionAttrs ? `<selection ${selectionAttrs}/>` : ''
      return attrs || selection ? `    <customSheetView ${attrs}>${selection}</customSheetView>` : ''
    })
    .filter(Boolean)
  if (entries.length === 0) return ''
  return `
  <customSheetViews>
${entries.join('\n')}
  </customSheetViews>`
}

const buildSheetPrintSettingsXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const settings = sheet.print_settings
  if (settings == null) return ''
  const printOptions = settings.print_options
  const printOptionsAttrs = printOptions == null ? '' : [
    printOptions.horizontal_centered != null ? `horizontalCentered="${printOptions.horizontal_centered ? '1' : '0'}"` : '',
    printOptions.vertical_centered != null ? `verticalCentered="${printOptions.vertical_centered ? '1' : '0'}"` : '',
    printOptions.headings != null ? `headings="${printOptions.headings ? '1' : '0'}"` : '',
    printOptions.grid_lines != null ? `gridLines="${printOptions.grid_lines ? '1' : '0'}"` : '',
    printOptions.grid_lines_set != null ? `gridLinesSet="${printOptions.grid_lines_set ? '1' : '0'}"` : '',
  ].filter(Boolean).join(' ')
  const pageMargins = settings.page_margins
  const pageMarginsAttrs = pageMargins == null ? '' : [
    pageMargins.left != null ? `left="${pageMargins.left}"` : '',
    pageMargins.right != null ? `right="${pageMargins.right}"` : '',
    pageMargins.top != null ? `top="${pageMargins.top}"` : '',
    pageMargins.bottom != null ? `bottom="${pageMargins.bottom}"` : '',
    pageMargins.header != null ? `header="${pageMargins.header}"` : '',
    pageMargins.footer != null ? `footer="${pageMargins.footer}"` : '',
  ].filter(Boolean).join(' ')
  const pageSetup = settings.page_setup
  const pageSetupAttrs = pageSetup == null ? '' : [
    pageSetup.paper_size != null ? `paperSize="${pageSetup.paper_size}"` : '',
    pageSetup.scale != null ? `scale="${pageSetup.scale}"` : '',
    pageSetup.fit_to_width != null ? `fitToWidth="${pageSetup.fit_to_width}"` : '',
    pageSetup.fit_to_height != null ? `fitToHeight="${pageSetup.fit_to_height}"` : '',
    pageSetup.orientation ? `orientation="${encodeXmlAttr(pageSetup.orientation)}"` : '',
    pageSetup.page_order ? `pageOrder="${encodeXmlAttr(pageSetup.page_order)}"` : '',
    pageSetup.horizontal_dpi != null ? `horizontalDpi="${pageSetup.horizontal_dpi}"` : '',
    pageSetup.vertical_dpi != null ? `verticalDpi="${pageSetup.vertical_dpi}"` : '',
    pageSetup.first_page_number != null ? `firstPageNumber="${pageSetup.first_page_number}"` : '',
    pageSetup.use_first_page_number != null ? `useFirstPageNumber="${pageSetup.use_first_page_number ? '1' : '0'}"` : '',
    pageSetup.black_and_white != null ? `blackAndWhite="${pageSetup.black_and_white ? '1' : '0'}"` : '',
    pageSetup.draft != null ? `draft="${pageSetup.draft ? '1' : '0'}"` : '',
    pageSetup.cell_comments ? `cellComments="${encodeXmlAttr(pageSetup.cell_comments)}"` : '',
    pageSetup.errors ? `errors="${encodeXmlAttr(pageSetup.errors)}"` : '',
  ].filter(Boolean).join(' ')
  const headerFooter = settings.header_footer
  const headerFooterAttrs = headerFooter == null ? '' : [
    headerFooter.different_odd_even != null ? `differentOddEven="${headerFooter.different_odd_even ? '1' : '0'}"` : '',
    headerFooter.different_first != null ? `differentFirst="${headerFooter.different_first ? '1' : '0'}"` : '',
    headerFooter.scale_with_doc != null ? `scaleWithDoc="${headerFooter.scale_with_doc ? '1' : '0'}"` : '',
    headerFooter.align_with_margins != null ? `alignWithMargins="${headerFooter.align_with_margins ? '1' : '0'}"` : '',
  ].filter(Boolean).join(' ')
  const headerFooterChildren = headerFooter == null ? '' : [
    headerFooter.odd_header ? `<oddHeader>${encodeXmlText(headerFooter.odd_header)}</oddHeader>` : '',
    headerFooter.odd_footer ? `<oddFooter>${encodeXmlText(headerFooter.odd_footer)}</oddFooter>` : '',
    headerFooter.even_header ? `<evenHeader>${encodeXmlText(headerFooter.even_header)}</evenHeader>` : '',
    headerFooter.even_footer ? `<evenFooter>${encodeXmlText(headerFooter.even_footer)}</evenFooter>` : '',
    headerFooter.first_header ? `<firstHeader>${encodeXmlText(headerFooter.first_header)}</firstHeader>` : '',
    headerFooter.first_footer ? `<firstFooter>${encodeXmlText(headerFooter.first_footer)}</firstFooter>` : '',
  ].filter(Boolean).join('')
  const buildBreaksXml = (
    sectionName: 'rowBreaks' | 'colBreaks',
    breaks: NonNullable<NonNullable<typeof settings>['page_breaks']>['rows'],
  ): string => {
    const entries = (breaks ?? [])
      .filter((entry) => Number.isInteger(entry.id) && entry.id >= 0)
      .map((entry) => {
        const attrs = [
          `id="${entry.id}"`,
          entry.min != null ? `min="${entry.min}"` : '',
          entry.max != null ? `max="${entry.max}"` : '',
          entry.manual != null ? `man="${entry.manual ? '1' : '0'}"` : '',
        ].filter(Boolean).join(' ')
        return `    <brk ${attrs}/>`
      })
    if (entries.length === 0) return ''
    const manualCount = (breaks ?? []).filter((entry) => entry.manual === true).length
    return `
  <${sectionName} count="${entries.length}" manualBreakCount="${manualCount}">
${entries.join('\n')}
  </${sectionName}>`
  }
  const rowBreaksXml = buildBreaksXml('rowBreaks', settings.page_breaks?.rows)
  const colBreaksXml = buildBreaksXml('colBreaks', settings.page_breaks?.columns)
  return [
    printOptionsAttrs ? `
  <printOptions ${printOptionsAttrs}/>` : '',
    pageMarginsAttrs ? `
  <pageMargins ${pageMarginsAttrs}/>` : '',
    pageSetupAttrs ? `
  <pageSetup ${pageSetupAttrs}/>` : '',
    headerFooterAttrs || headerFooterChildren ? `
  <headerFooter${headerFooterAttrs ? ` ${headerFooterAttrs}` : ''}>${headerFooterChildren}</headerFooter>` : '',
    rowBreaksXml,
    colBreaksXml,
  ].join('')
}

const buildIgnoredErrorsXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
): string => {
  const entries = (sheet.ignored_errors ?? [])
    .filter((entry) => String(entry.range ?? '').trim().length > 0)
    .map((entry) => {
      const attrs = [
        `sqref="${encodeXmlAttr(entry.range)}"`,
        entry.eval_error != null ? `evalError="${entry.eval_error ? '1' : '0'}"` : '',
        entry.two_digit_text_year != null ? `twoDigitTextYear="${entry.two_digit_text_year ? '1' : '0'}"` : '',
        entry.number_stored_as_text != null ? `numberStoredAsText="${entry.number_stored_as_text ? '1' : '0'}"` : '',
        entry.formula != null ? `formula="${entry.formula ? '1' : '0'}"` : '',
        entry.formula_range != null ? `formulaRange="${entry.formula_range ? '1' : '0'}"` : '',
        entry.unlocked_formula != null ? `unlockedFormula="${entry.unlocked_formula ? '1' : '0'}"` : '',
        entry.empty_cell_reference != null ? `emptyCellReference="${entry.empty_cell_reference ? '1' : '0'}"` : '',
        entry.list_data_validation != null ? `listDataValidation="${entry.list_data_validation ? '1' : '0'}"` : '',
        entry.calculated_column != null ? `calculatedColumn="${entry.calculated_column ? '1' : '0'}"` : '',
      ].filter(Boolean).join(' ')
      return `    <ignoredError ${attrs}/>`
    })
  if (entries.length === 0) return ''
  return `
  <ignoredErrors>
${entries.join('\n')}
  </ignoredErrors>`
}

const mergeWorksheetExtensionListXml = (
  rawExtensionListXml: string,
  extensionEntries: string[],
): string => {
  const raw = rawExtensionListXml.trim()
  const missingEntries = extensionEntries.filter((entry) => {
    const relationshipId = /r:id="([^"]+)"/.exec(entry)?.[1]
    return relationshipId == null || !raw.includes(`r:id="${relationshipId}"`)
  })
  if (!raw && missingEntries.length === 0) return ''
  if (!raw) return `<extLst>\n${missingEntries.join('\n')}\n  </extLst>`
  if (missingEntries.length === 0) return raw
  return /<\/extLst>\s*$/i.test(raw)
    ? raw.replace(/<\/extLst>\s*$/i, `${missingEntries.join('\n')}\n  </extLst>`)
    : `${raw}\n${missingEntries.join('\n')}`
}

const buildSheetOoxmlMetadataXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
  extensionEntries: string[] = [],
): string => {
  const metadata = sheet.ooxml_sheet_metadata
  if (metadata == null && extensionEntries.length === 0) return ''
  const extensionListRaw = metadata?.extension_list?.raw_xml
  const extensionListXml = mergeWorksheetExtensionListXml(String(extensionListRaw ?? ''), extensionEntries)
  const rawElements = [
    metadata?.phonetic_properties?.raw_xml,
    metadata?.smart_tags?.raw_xml,
    metadata?.scenarios?.raw_xml,
    extensionListXml,
  ]
    .map((entry) => String(entry ?? '').trim())
    .filter((entry) => entry.startsWith('<'))
  return rawElements.length === 0 ? '' : `
  ${rawElements.join('\n  ')}`
}

const conditionalCfvoToXml = (cfvo: unknown): string => {
  const record = cfvo && typeof cfvo === 'object' && !Array.isArray(cfvo) ? cfvo as Record<string, unknown> : {}
  const type = String(record.type ?? 'percentile')
  const val = record.val
  const gte = record.gte === false ? ' gte="0"' : ''
  const valAttr = val != null && String(val).trim() !== '' ? ` val="${encodeXmlAttr(String(val))}"` : ''
  return `<cfvo type="${encodeXmlAttr(type)}"${valAttr}${gte}/>`
}

/** Native data-bar / colour-scale / icon-set inner XML (children, not a dxf). */
const buildConditionalGraphicXml = (kind: string, rule: Record<string, unknown>): string | null => {
  if (kind === 'dataBar') {
    const db = (rule.dataBar ?? rule.data_bar) as Record<string, unknown> | undefined
    const min = db?.min ?? { type: 'min' }
    const max = db?.max ?? { type: 'max' }
    const color = db?.color ?? '#638EC6'
    const showValue = db?.showValue === false ? ' showValue="0"' : ''
    return `<dataBar${showValue}>${conditionalCfvoToXml(min)}${conditionalCfvoToXml(max)}${colorToOoxmlElement('color', color)}</dataBar>`
  }
  if (kind === 'colorScale') {
    const cs = (rule.colorScale ?? rule.color_scale) as Record<string, unknown> | undefined
    const stops = Array.isArray(cs?.stops) ? cs!.stops as Array<Record<string, unknown>> : []
    if (stops.length < 2) return null
    const cfvos = stops.map((s) => conditionalCfvoToXml(s.cfvo)).join('')
    const colors = stops.map((s) => colorToOoxmlElement('color', s.color)).join('')
    return `<colorScale>${cfvos}${colors}</colorScale>`
  }
  if (kind === 'iconSet') {
    const is = (rule.iconSet ?? rule.icon_set) as Record<string, unknown> | undefined
    const iconSetType = String(is?.iconSetType ?? is?.type ?? '3TrafficLights1')
    const cfvos = Array.isArray(is?.cfvos) && is!.cfvos.length > 0
      ? is!.cfvos as Array<Record<string, unknown>>
      : [{ type: 'percent', val: 0 }, { type: 'percent', val: 33 }, { type: 'percent', val: 67 }]
    const reverse = is?.reverse === true ? ' reverse="1"' : ''
    const showValue = is?.showValue === false ? ' showValue="0"' : ''
    return `<iconSet iconSet="${encodeXmlAttr(iconSetType)}"${reverse}${showValue}>${cfvos.map(conditionalCfvoToXml).join('')}</iconSet>`
  }
  return null
}

const CONDITIONAL_GRAPHIC_KINDS = new Set(['dataBar', 'colorScale', 'iconSet'])

const buildConditionalFormatsXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
  conditionalFormats: Record<string, Record<string, unknown>>,
  dxfIndexByConditionalFormatId: Map<string, number>,
): string => {
  const entries = Object.entries(conditionalFormats)
    .sort(([leftId], [rightId]) => leftId.localeCompare(rightId))
    .map(([conditionalFormatId, rule]) => {
      const ranges = Array.isArray(rule.ranges) ? rule.ranges.filter((range): range is SpreadsheetRangeAddress => (
        range != null &&
        typeof range === 'object' &&
        !Array.isArray(range) &&
        range.sheet_id === sheet.sheet_id
      )) : []
      if (ranges.length === 0) return ''
      const kind = String(rule.kind ?? 'expression')
      const priority = Number.isFinite(Number(rule.priority)) ? Number(rule.priority) : 1
      const sqref = ranges.map(formatRangeRef).join(' ')

      // Native graphic rules (data bar / colour scale / icon set) carry child
      // elements and no dxf; everything else keeps the formula + dxf path.
      if (CONDITIONAL_GRAPHIC_KINDS.has(kind)) {
        const inner = buildConditionalGraphicXml(kind, rule)
        if (inner == null) return ''
        const attrs = [
          `type="${encodeXmlAttr(kind)}"`,
          `priority="${priority}"`,
          rule.stop_if_true === true ? 'stopIfTrue="1"' : '',
        ].filter(Boolean).join(' ')
        return `  <conditionalFormatting sqref="${sqref}">
    <cfRule ${attrs}>${inner}</cfRule>
  </conditionalFormatting>`
      }

      const dxfId = dxfIndexByConditionalFormatId.get(conditionalFormatId)
      const attrs = [
        `type="${encodeXmlAttr(kind)}"`,
        `priority="${priority}"`,
        rule.operator ? `operator="${encodeXmlAttr(String(rule.operator))}"` : '',
        rule.stop_if_true === true ? 'stopIfTrue="1"' : '',
        dxfId != null ? `dxfId="${dxfId}"` : '',
      ].filter(Boolean).join(' ')
      const formulas = [rule.formula1, rule.formula2]
        .map((formula) => String(formula ?? '').trim())
        .filter(Boolean)
        .map((formula) => `<formula>${encodeXmlText(formula)}</formula>`)
        .join('')
      return `  <conditionalFormatting sqref="${sqref}">
    <cfRule ${attrs}>${formulas}</cfRule>
  </conditionalFormatting>`
    })
    .filter(Boolean)
  return entries.length === 0 ? '' : `
${entries.join('\n')}`
}

const buildWorksheetXml = (
  sheet: SpreadsheetWorkbookSnapshot['sheets'][number],
  sheetIndex: number,
  sharedStrings: Map<string, number>,
  sheetRelationships: Array<{ id: string; type: string; target: string; targetMode?: string }>,
  styleIndexById: Map<string, number>,
  validationRules: Record<string, SpreadsheetValidationRule>,
  tableEntries: SnapshotTableExportEntry[],
  commentEntry: SnapshotCommentExportEntry | undefined,
  conditionalFormats: Record<string, Record<string, unknown>>,
  dxfIndexByConditionalFormatId: Map<string, number>,
  attachments: SpreadsheetExcelOoxmlArtifactManifestEntry[],
  chartDrawing?: SnapshotChartDrawing | null,
): string => {
  const cells = iterSnapshotCellsWithDynamicArraySpills(sheet)
  const maxRow = Math.max(sheet.row_count - 1, ...cells.map((cell) => cell.row), 0)
  const maxCol = Math.max(sheet.col_count - 1, ...cells.map((cell) => cell.col), 0)
  const rowsByIndex = new Map<number, SnapshotCellExportEntry[]>()
  for (const cell of cells) {
    rowsByIndex.set(cell.row, [...(rowsByIndex.get(cell.row) ?? []), cell])
  }
  const metadataRows = new Set(Object.keys(sheet.row_metadata ?? {}).map(Number).filter((row) => Number.isInteger(row) && row >= 0))
  const rowIndexes = Array.from(new Set([...rowsByIndex.keys(), ...metadataRows])).sort((left, right) => left - right)
  const columns = Object.entries(sheet.column_metadata ?? {})
    .map(([colText, metadata]) => ({ col: Number(colText), metadata }))
    .filter((entry) => Number.isInteger(entry.col) && entry.col >= 0)
    .sort((left, right) => left.col - right.col)
  const hyperlinks: string[] = []
  let hyperlinkIndex = 1
  const sheetData = rowIndexes.map((row) => {
    const rowMetadata = sheet.row_metadata?.[String(row)] ?? {}
    const ht = Number(rowMetadata.height_px)
    const rowStyleId = typeof rowMetadata.style_id === 'string' ? rowMetadata.style_id : ''
    const rowStyleIndex = styleIndexById.get(rowStyleId)
    const rowOutlineLevel = Number(rowMetadata.outline_level)
    const rowAttrs = [
      `r="${row + 1}"`,
      Number.isFinite(ht) && ht > 0 ? `ht="${Math.round(ht * 0.75 * 100) / 100}" customHeight="1"` : '',
      rowStyleIndex != null ? `s="${rowStyleIndex}" customFormat="1"` : '',
      rowMetadata.hidden === true ? 'hidden="1"' : '',
      Number.isInteger(rowOutlineLevel) && rowOutlineLevel >= 0 ? `outlineLevel="${rowOutlineLevel}"` : '',
      rowMetadata.collapsed === true ? 'collapsed="1"' : '',
    ].filter(Boolean).join(' ')
    const cellsXml = (rowsByIndex.get(row) ?? []).map(({ col, record }) => {
      const cellRef = formatCellRef(row, col)
      const rawInput = String(record.raw_input ?? '')
      const cellStyleId = getCellExportStyleId(record)
      const styleIndex = cellStyleId == null ? null : styleIndexById.get(cellStyleId)
      const styleAttr = styleIndex == null ? '' : ` s="${styleIndex}"`
      const cellMetadataAttrs = buildCellOoxmlMetadataAttrs(record)
      const cellAttrs = `r="${cellRef}"${styleAttr}${cellMetadataAttrs}`
      const hyperlinkMetadata = getCellHyperlinkMetadata(record)
      const parsedHyperlink = parseHyperlinkLines(record.hyperlink)
      if (parsedHyperlink != null || hyperlinkMetadata != null) {
        const display = String(hyperlinkMetadata?.display ?? parsedHyperlink?.display ?? '').trim()
        const tooltip = String(hyperlinkMetadata?.tooltip ?? parsedHyperlink?.tooltip ?? '').trim()
        const location = String(hyperlinkMetadata?.location ?? '').trim()
        const target = String(hyperlinkMetadata?.target ?? parsedHyperlink?.target ?? '').trim()
        if (location || target.startsWith('#')) {
          const internalLocation = location || target.replace(/^#/, '')
          hyperlinks.push(`  <hyperlink ref="${cellRef}" location="${encodeXmlAttr(internalLocation)}"${display ? ` display="${encodeXmlAttr(display)}"` : ''}${tooltip ? ` tooltip="${encodeXmlAttr(tooltip)}"` : ''}/>` )
        } else if (target) {
          const relationshipId = `rLink${hyperlinkIndex}`
          hyperlinkIndex += 1
          sheetRelationships.push({
            id: relationshipId,
            type: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink',
            target,
            targetMode: 'External',
          })
          hyperlinks.push(`  <hyperlink ref="${cellRef}" r:id="${relationshipId}"${display ? ` display="${encodeXmlAttr(display)}"` : ''}${tooltip ? ` tooltip="${encodeXmlAttr(tooltip)}"` : ''}/>` )
        }
      }
      if (isFormulaInput(rawInput)) {
        const cached = buildFormulaCachedValueXml(record.computed_value)
        return `<c ${cellAttrs}${cached?.typeAttr ?? ''}>${buildCellFormulaXml(record)}${cached?.valueXml ?? ''}</c>`
      }
      if (record.computed_value?.kind === 'number') return `<c ${cellAttrs}><v>${encodeXmlText(record.computed_value.value)}</v></c>`
      if (record.computed_value?.kind === 'boolean') return `<c ${cellAttrs} t="b"><v>${record.computed_value.value ? '1' : '0'}</v></c>`
      if (record.computed_value?.kind === 'error') return `<c ${cellAttrs} t="e"><v>${encodeXmlText(record.computed_value.code)}</v></c>`
      if (rawInput) return `<c ${cellAttrs} t="s"><v>${sharedStrings.get(rawInput) ?? 0}</v></c>`
      if (styleAttr || cellMetadataAttrs) return `<c ${cellAttrs}></c>`
      return ''
    }).filter(Boolean).join('')
    return `    <row ${rowAttrs}>${cellsXml}</row>`
  }).join('\n')
  const colsXml = columns.length === 0 ? '' : `
  <cols>
${columns.map(({ col, metadata }) => {
  const width = Number(metadata.width_px)
  const columnStyleId = typeof metadata.style_id === 'string' ? metadata.style_id : ''
  const columnStyleIndex = styleIndexById.get(columnStyleId)
  const outlineLevel = Number(metadata.outline_level)
  const attrs = [
    `min="${col + 1}"`,
    `max="${col + 1}"`,
    Number.isFinite(width) && width > 0 ? `width="${Math.round((width / 7) * 100) / 100}" customWidth="1"` : '',
    columnStyleIndex != null ? `style="${columnStyleIndex}"` : '',
    metadata.hidden === true ? 'hidden="1"' : '',
    Number.isInteger(outlineLevel) && outlineLevel >= 0 ? `outlineLevel="${outlineLevel}"` : '',
    metadata.collapsed === true ? 'collapsed="1"' : '',
  ].filter(Boolean).join(' ')
  return `    <col ${attrs}/>`
}).join('\n')}
  </cols>`
  const sheetPropertiesXml = buildSheetPropertiesXml(sheet)
  const sheetViewsXml = buildSheetViewsXml(sheet)
  const customSheetViewsXml = buildCustomSheetViewsXml(sheet)
  const sheetFormatPropertiesXml = buildSheetFormatPropertiesXml(sheet)
  const sheetCalculationPropertiesXml = buildSheetCalculationPropertiesXml(sheet)
  const merges = Object.values(sheet.merge_ranges ?? {})
  const mergeXml = merges.length === 0 ? '' : `
  <mergeCells count="${merges.length}">
${merges.map((range) => `    <mergeCell ref="${formatRangeRef(range)}"/>`).join('\n')}
  </mergeCells>`
  const hyperlinksXml = hyperlinks.length === 0 ? '' : `
  <hyperlinks>
${hyperlinks.join('\n')}
  </hyperlinks>`
  for (const tableEntry of tableEntries) {
    sheetRelationships.push({
      id: tableEntry.relationshipId,
      type: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/table',
      target: `../tables/table${tableEntry.tableIndex}.xml`,
    })
  }
  const tablePartsXml = tableEntries.length === 0 ? '' : `
  <tableParts count="${tableEntries.length}">
${tableEntries.map(({ relationshipId }) => `    <tablePart r:id="${relationshipId}"/>`).join('\n')}
  </tableParts>`
  if (commentEntry != null) {
    sheetRelationships.push({
      id: commentEntry.commentsRelationshipId,
      type: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments',
      target: `../comments${commentEntry.commentIndex}.xml`,
    })
    if (commentEntry.hasVmlGeometry) {
      sheetRelationships.push({
        id: commentEntry.vmlRelationshipId,
        type: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/vmlDrawing',
        target: `../drawings/vmlDrawing${commentEntry.commentIndex}.vml`,
      })
    }
  }
  const legacyDrawingXml = commentEntry?.hasVmlGeometry === true ? `
  <legacyDrawing r:id="${commentEntry.vmlRelationshipId}"/>` : ''
  const sheetProtectionXml = buildSheetProtectionXml(sheet.sheet_protection)
  const protectedRangesXml = buildProtectedRangesXml(sheet)
  const autoFilterXml = buildAutoFilterXml(sheet.auto_filter)
  const conditionalFormatsXml = buildConditionalFormatsXml(sheet, conditionalFormats, dxfIndexByConditionalFormatId)
  const dataValidationsXml = buildDataValidationsXml(sheet, validationRules)
  const ignoredErrorsXml = buildIgnoredErrorsXml(sheet)
  const printSettingsXml = buildSheetPrintSettingsXml(sheet)
  const sheetPartName = `xl/worksheets/sheet${sheetIndex}.xml`
  const drawingReferencesXml = buildWorksheetDrawingReferencesXml(sheetPartName, attachments)
  let chartDrawingXml = ''
  if (chartDrawing && !drawingReferencesXml) {
    sheetRelationships.push({ id: chartDrawing.relId, type: DRAWING_REL_TYPE, target: chartDrawing.target })
    chartDrawingXml = `
  <drawing r:id="${chartDrawing.relId}"/>`
  }
  const pivotTableDefinitionsXml = buildWorksheetPivotTableDefinitionsXml(sheetPartName, attachments)
  const advancedExtensionEntries = buildWorksheetAdvancedExtensionEntries(sheetPartName, attachments)
  const ooxmlMetadataXml = buildSheetOoxmlMetadataXml(sheet, advancedExtensionEntries)
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
${sheetPropertiesXml}
  <dimension ref="A1:${formatCellRef(maxRow, maxCol)}"/>${sheetViewsXml}${sheetFormatPropertiesXml}${colsXml}
  <sheetData>
${sheetData}
  </sheetData>${sheetCalculationPropertiesXml}${sheetProtectionXml}${protectedRangesXml}${autoFilterXml}${customSheetViewsXml}${mergeXml}${conditionalFormatsXml}${dataValidationsXml}${hyperlinksXml}${printSettingsXml}${ignoredErrorsXml}${drawingReferencesXml}${chartDrawingXml}${legacyDrawingXml}${tablePartsXml}${pivotTableDefinitionsXml}${ooxmlMetadataXml}
</worksheet>`
}

const buildExportWarnings = (
  snapshot: SpreadsheetWorkbookSnapshot,
  stylesExport: ExportStyleRecords,
): SpreadsheetExcelIoWarning[] => {
  const warnings: SpreadsheetExcelIoWarning[] = []
  if (stylesExport.hasUnsupportedThemeColors) {
    warnings.push({
      code: 'xlsx-export-theme-styles-partial',
      severity: 'warning',
      message: 'Canonical .xlsx export writer emits workbook theme parts for known theme indexes; at least one style uses an unsupported theme color index.',
    })
  }
  const usedValidationIds = new Set(snapshot.sheets.flatMap((sheet) => (
    iterSnapshotCells(sheet).map(({ record }) => String(record.validation_id ?? '').trim()).filter(Boolean)
  )))
  const orphanValidationIds = Object.keys(snapshot.validation_rules ?? {}).filter((validationId) => !usedValidationIds.has(validationId))
  if (orphanValidationIds.length > 0) {
    warnings.push({
      code: 'xlsx-export-unassigned-validation-rules',
      severity: 'warning',
      message: 'Canonical .xlsx export writer serializes assigned validation rules; unassigned validation rules have no OOXML worksheet range.',
    })
  }
  return warnings
}

// ---------------------------------------------------------------------------
// Native chart synthesis — emit real Excel chart parts from snapshot
// derived_artifacts of kind 'chart' carrying a `metadata.chart_spec`. Supports
// column / bar / line / area / combo (mixed per-series type + secondary axis)
// and stacked variants (the basis for waterfall and scenario fan/band charts).
// ---------------------------------------------------------------------------

type SnapshotChartSeriesType = 'col' | 'bar' | 'line' | 'area'

type SnapshotChartSeriesSpec = {
  name?: string | null
  nameRef?: string | null
  categoriesRef?: string | null
  valuesRef: string
  type?: SnapshotChartSeriesType
  color?: string | null
  secondaryAxis?: boolean
  smooth?: boolean
  /** Fill opacity 0-100 (for translucent band fills). */
  alpha?: number
  /** No fill at all (the invisible base series of a stacked-area band). */
  noFill?: boolean
}

type SnapshotChartSpec = {
  title?: string | null
  series: SnapshotChartSeriesSpec[]
  legend?: 'r' | 'l' | 't' | 'b' | 'none'
  catTitle?: string | null
  valTitle?: string | null
  valTitle2?: string | null
  grouping?: 'standard' | 'stacked' | 'percentStacked'
}

const CHART_NS = 'xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"'
const CHART_AX = { cat: 111111111, val: 222222222, cat2: 333333333, val2: 444444444 } as const
const CHART_DEFAULT_COLORS = ['4472C4', 'ED7D31', 'A5A5A5', 'FFC000', '5B9BD5', '70AD47', '264478', '9E480E']

const chartHex = (color: string | null | undefined, index: number): string => {
  const norm = normalizeRgbColor(String(color ?? ''))
  return (norm ? norm.replace(/^#/, '') : CHART_DEFAULT_COLORS[index % CHART_DEFAULT_COLORS.length]).toUpperCase()
}

const chartSrgb = (hex: string, alpha?: number): string => (
  alpha != null
    ? `<a:srgbClr val="${hex}"><a:alpha val="${Math.round(Math.max(0, Math.min(100, alpha)) * 1000)}"/></a:srgbClr>`
    : `<a:srgbClr val="${hex}"/>`
)

const chartSerSpPr = (type: SnapshotChartSeriesType, hex: string, alpha?: number, noFill?: boolean): string => {
  if (type === 'line') {
    const stroke = noFill ? '<a:noFill/>' : `<a:solidFill>${chartSrgb(hex, alpha)}</a:solidFill>`
    return `<c:spPr><a:ln w="28575" cap="rnd">${stroke}<a:round/></a:ln></c:spPr>`
  }
  const fill = noFill ? '<a:noFill/>' : `<a:solidFill>${chartSrgb(hex, alpha)}</a:solidFill>`
  return `<c:spPr>${fill}<a:ln><a:noFill/></a:ln></c:spPr>`
}

// Resolve chart series range refs to their cached cell values so the chart
// renders immediately in Excel (Excel-for-Mac shows a blank plot when a
// synthesized chart's numRef/strRef has no num/strCache).
type ChartCacheFn = (ref: string | null | undefined, kind: 'num' | 'str') => string

const chartColLetterToIndex = (letters: string): number => {
  let n = 0
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

const parseChartRangeRef = (ref: string): { sheet: string; r1: number; c1: number; r2: number; c2: number } | null => {
  const cleaned = String(ref ?? '').replace(/^=/, '')
  const m = /^(?:'((?:[^']|'')+)'|([^'!]+))!\$?([A-Za-z]+)\$?(\d+)(?::\$?([A-Za-z]+)\$?(\d+))?$/.exec(cleaned)
  if (!m) return null
  const sheet = m[1] != null ? m[1].replace(/''/g, "'") : m[2]
  const c1 = chartColLetterToIndex(m[3])
  const r1 = Number(m[4]) - 1
  const c2 = m[5] ? chartColLetterToIndex(m[5]) : c1
  const r2 = m[6] ? Number(m[6]) - 1 : r1
  return { sheet, r1: Math.min(r1, r2), c1: Math.min(c1, c2), r2: Math.max(r1, r2), c2: Math.max(c1, c2) }
}

const buildSnapshotChartCellLookup = (snapshot: SpreadsheetWorkbookSnapshot): Map<string, Map<string, SpreadsheetCellValue | null | undefined>> => {
  const byName = new Map<string, Map<string, SpreadsheetCellValue | null | undefined>>()
  for (const sheet of snapshot.sheets) {
    const cells = new Map<string, SpreadsheetCellValue | null | undefined>()
    for (const chunk of Object.values(sheet.chunks)) {
      for (const [key, record] of Object.entries(chunk)) cells.set(key, record.computed_value)
    }
    byName.set(sheet.name.toLowerCase(), cells)
  }
  return byName
}

const buildChartCache = (
  lookup: Map<string, Map<string, SpreadsheetCellValue | null | undefined>>,
  ref: string | null | undefined,
  kind: 'num' | 'str',
): string => {
  const parsed = ref ? parseChartRangeRef(ref) : null
  if (!parsed) return ''
  const cells = lookup.get(parsed.sheet.toLowerCase())
  if (!cells) return ''
  const pts: string[] = []
  let idx = 0
  for (let r = parsed.r1; r <= parsed.r2; r += 1) {
    for (let c = parsed.c1; c <= parsed.c2; c += 1) {
      const v = cells.get(`${r}:${c}`)
      if (kind === 'num') {
        const num = v && v.kind === 'number'
          ? v.value
          : v && v.kind === 'text' && v.value.trim() !== '' && Number.isFinite(Number(v.value)) ? Number(v.value) : null
        if (num != null) pts.push(`<c:pt idx="${idx}"><c:v>${num}</c:v></c:pt>`)
      } else {
        const s = v && v.kind === 'text' ? v.value : v && v.kind === 'number' ? String(v.value) : ''
        if (s) pts.push(`<c:pt idx="${idx}"><c:v>${encodeXmlText(s)}</c:v></c:pt>`)
      }
      idx += 1
    }
  }
  if (idx === 0) return ''
  return kind === 'num'
    ? `<c:numCache><c:formatCode>General</c:formatCode><c:ptCount val="${idx}"/>${pts.join('')}</c:numCache>`
    : `<c:strCache><c:ptCount val="${idx}"/>${pts.join('')}</c:strCache>`
}

const chartSeriesXml = (series: SnapshotChartSeriesSpec, idx: number, cache: ChartCacheFn): string => {
  const type = series.type ?? 'col'
  const hex = chartHex(series.color, idx)
  const tx = series.nameRef
    ? `<c:tx><c:strRef><c:f>${encodeXmlText(series.nameRef)}</c:f></c:strRef></c:tx>`
    : series.name != null ? `<c:tx><c:v>${encodeXmlText(series.name)}</c:v></c:tx>` : ''
  const cat = series.categoriesRef ? `<c:cat><c:strRef><c:f>${encodeXmlText(series.categoriesRef)}</c:f>${cache(series.categoriesRef, 'str')}</c:strRef></c:cat>` : ''
  const val = `<c:val><c:numRef><c:f>${encodeXmlText(series.valuesRef)}</c:f>${cache(series.valuesRef, 'num')}</c:numRef></c:val>`
  const marker = type === 'line' ? '<c:marker><c:symbol val="none"/></c:marker>' : ''
  const smooth = type === 'line' ? `<c:smooth val="${series.smooth ? 1 : 0}"/>` : ''
  return `<c:ser><c:idx val="${idx}"/><c:order val="${idx}"/>${tx}${chartSerSpPr(type, hex, series.alpha, series.noFill)}${marker}${cat}${val}${smooth}</c:ser>`
}

const chartAxisTitleXml = (text: string): string => (
  `<c:title><c:tx><c:rich><a:bodyPr rot="0"/><a:lstStyle/><a:p><a:r><a:rPr lang="en-US" sz="900"/><a:t>${encodeXmlText(text)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title>`
)

const chartBlocksXml = (
  withIdx: Array<{ s: SnapshotChartSeriesSpec; idx: number }>,
  axId1: number,
  axId2: number,
  grouping: SnapshotChartSpec['grouping'],
  cache: ChartCacheFn,
): string => {
  const groups: Record<SnapshotChartSeriesType, Array<{ s: SnapshotChartSeriesSpec; idx: number }>> = { col: [], bar: [], area: [], line: [] }
  for (const item of withIdx) groups[item.s.type ?? 'col'].push(item)
  const sers = (list: Array<{ s: SnapshotChartSeriesSpec; idx: number }>) => list.map(({ s, idx }) => chartSeriesXml(s, idx, cache)).join('')
  const barGrouping = grouping === 'stacked' ? 'stacked' : grouping === 'percentStacked' ? 'percentStacked' : 'clustered'
  const areaGrouping = grouping === 'stacked' ? 'stacked' : grouping === 'percentStacked' ? 'percentStacked' : 'standard'
  const overlap = barGrouping === 'clustered' ? '<c:overlap val="-20"/>' : '<c:overlap val="100"/>'
  const out: string[] = []
  const barBlock = (dir: 'col' | 'bar', list: Array<{ s: SnapshotChartSeriesSpec; idx: number }>) => (
    `<c:barChart><c:barDir val="${dir}"/><c:grouping val="${barGrouping}"/><c:varyColors val="0"/>${sers(list)}<c:gapWidth val="80"/>${overlap}<c:axId val="${axId1}"/><c:axId val="${axId2}"/></c:barChart>`
  )
  if (groups.col.length) out.push(barBlock('col', groups.col))
  if (groups.bar.length) out.push(barBlock('bar', groups.bar))
  if (groups.area.length) out.push(`<c:areaChart><c:grouping val="${areaGrouping}"/><c:varyColors val="0"/>${sers(groups.area)}<c:axId val="${axId1}"/><c:axId val="${axId2}"/></c:areaChart>`)
  if (groups.line.length) out.push(`<c:lineChart><c:grouping val="standard"/><c:varyColors val="0"/>${sers(groups.line)}<c:marker val="1"/><c:axId val="${axId1}"/><c:axId val="${axId2}"/></c:lineChart>`)
  return out.join('')
}

const buildChartPartXml = (spec: SnapshotChartSpec, cache: ChartCacheFn): string => {
  const withIdx = spec.series.map((s, idx) => ({ s, idx }))
  const primary = withIdx.filter((x) => !x.s.secondaryAxis)
  const secondary = withIdx.filter((x) => x.s.secondaryAxis)
  const primaryBlocks = chartBlocksXml(primary, CHART_AX.cat, CHART_AX.val, spec.grouping, cache)
  const secondaryBlocks = secondary.length ? chartBlocksXml(secondary, CHART_AX.cat2, CHART_AX.val2, spec.grouping, cache) : ''
  const catTitle = spec.catTitle ? chartAxisTitleXml(spec.catTitle) : ''
  const valTitle = spec.valTitle ? chartAxisTitleXml(spec.valTitle) : ''
  const valTitle2 = spec.valTitle2 ? chartAxisTitleXml(spec.valTitle2) : ''
  const catAx = `<c:catAx><c:axId val="${CHART_AX.cat}"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="b"/>${catTitle}<c:numFmt formatCode="General" sourceLinked="1"/><c:majorTickMark val="out"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="${CHART_AX.val}"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:noMultiLvlLbl val="0"/></c:catAx>`
  const valAx = `<c:valAx><c:axId val="${CHART_AX.val}"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="l"/><c:majorGridlines/>${valTitle}<c:numFmt formatCode="General" sourceLinked="1"/><c:majorTickMark val="out"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="${CHART_AX.cat}"/><c:crosses val="autoZero"/><c:crossBetween val="between"/></c:valAx>`
  const secondaryAxes = secondary.length
    ? `<c:valAx><c:axId val="${CHART_AX.val2}"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="r"/>${valTitle2}<c:numFmt formatCode="General" sourceLinked="1"/><c:majorTickMark val="out"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="${CHART_AX.cat2}"/><c:crosses val="max"/><c:crossBetween val="between"/></c:valAx>`
      + `<c:catAx><c:axId val="${CHART_AX.cat2}"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="1"/><c:axPos val="b"/><c:majorTickMark val="out"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="${CHART_AX.val2}"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:noMultiLvlLbl val="0"/></c:catAx>`
    : ''
  const titleBlock = spec.title
    ? `<c:title><c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:r><a:rPr lang="en-US" sz="1100" b="1"/><a:t>${encodeXmlText(spec.title)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title><c:autoTitleDeleted val="0"/>`
    : '<c:autoTitleDeleted val="1"/>'
  const legend = spec.legend === 'none' ? '' : `<c:legend><c:legendPos val="${spec.legend ?? 'b'}"/><c:overlay val="0"/></c:legend>`
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace ${CHART_NS}><c:roundedCorners val="0"/><c:chart>${titleBlock}<c:plotArea><c:layout/>${primaryBlocks}${secondaryBlocks}${catAx}${valAx}${secondaryAxes}</c:plotArea>${legend}<c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/></c:chart></c:chartSpace>`
}

const buildChartAnchorXml = (
  placement: SpreadsheetRangeAddress,
  chartRelId: string,
  graphicId: number,
): string => {
  const c1 = Math.max(0, placement.start_col)
  const r1 = Math.max(0, placement.start_row)
  const c2 = Math.max(c1, placement.end_col)
  const r2 = Math.max(r1, placement.end_row)
  return `<xdr:twoCellAnchor editAs="twoCell"><xdr:from><xdr:col>${c1}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${r1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from><xdr:to><xdr:col>${c2 + 1}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${r2 + 1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to><xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="${graphicId}" name="Chart ${graphicId}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr><xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart"><c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="${chartRelId}"/></a:graphicData></a:graphic></xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor>`
}

type SnapshotChartDrawing = { relId: string; target: string }

type SnapshotChartPlan = {
  drawingBySheetId: Map<string, SnapshotChartDrawing>
  files: Record<string, string>
  contentTypeOverrides: string[]
}

const readChartSpec = (artifact: Record<string, unknown>): SnapshotChartSpec | null => {
  const metadata = artifact.metadata
  const spec = metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? (metadata as Record<string, unknown>).chart_spec
    : null
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return null
  const series = (spec as Record<string, unknown>).series
  if (!Array.isArray(series) || series.length === 0) return null
  return spec as SnapshotChartSpec
}

const planSnapshotCharts = (
  snapshot: SpreadsheetWorkbookSnapshot,
  orderedSheets: Array<{ sheet: SpreadsheetWorkbookSnapshot['sheets'][number] }>,
): SnapshotChartPlan => {
  const plan: SnapshotChartPlan = { drawingBySheetId: new Map(), files: {}, contentTypeOverrides: [] }
  const artifacts = Object.values(snapshot.derived_artifacts ?? {})
    .filter((a) => Boolean(a) && typeof a === 'object' && (a as Record<string, unknown>).kind === 'chart')
  if (artifacts.length === 0) return plan
  const cellLookup = buildSnapshotChartCellLookup(snapshot)
  const cache: ChartCacheFn = (ref, kind) => buildChartCache(cellLookup, ref, kind)
  const bySheet = new Map<string, Array<{ placement: SpreadsheetRangeAddress; spec: SnapshotChartSpec }>>()
  for (const artifact of artifacts) {
    const spec = readChartSpec(artifact)
    const placement = artifact.placement as SpreadsheetRangeAddress | undefined
    const sheetId = String(artifact.sheet_id ?? '')
    if (!spec || !placement || !sheetId) continue
    if (!bySheet.has(sheetId)) bySheet.set(sheetId, [])
    bySheet.get(sheetId)!.push({ placement, spec })
  }
  let drawingIndex = 0
  let chartIndex = 0
  let graphicId = 1
  for (const { sheet } of orderedSheets) {
    const charts = bySheet.get(sheet.sheet_id)
    if (!charts || charts.length === 0) continue
    drawingIndex += 1
    const drawingPart = `xl/drawings/drawing${drawingIndex}.xml`
    const drawingRelsPart = `xl/drawings/_rels/drawing${drawingIndex}.xml.rels`
    const anchors: string[] = []
    const rels: string[] = []
    charts.forEach(({ placement, spec }, j) => {
      chartIndex += 1
      graphicId += 1
      const chartPart = `xl/charts/chart${chartIndex}.xml`
      plan.files[chartPart] = buildChartPartXml(spec, cache)
      plan.contentTypeOverrides.push(`<Override PartName="/${chartPart}" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`)
      const relId = `rId${j + 1}`
      rels.push(`<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart${chartIndex}.xml"/>`)
      anchors.push(buildChartAnchorXml(placement, relId, graphicId))
    })
    plan.files[drawingPart] = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">${anchors.join('')}</xdr:wsDr>`
    plan.files[drawingRelsPart] = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rels.join('')}</Relationships>`
    plan.contentTypeOverrides.push(`<Override PartName="/${drawingPart}" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`)
    plan.drawingBySheetId.set(sheet.sheet_id, { relId: 'rIdChartDrawing', target: `../drawings/drawing${drawingIndex}.xml` })
  }
  return plan
}

export const buildSpreadsheetExcelOoxmlPackageFromWorkbookSnapshot = ({
  snapshot,
}: SpreadsheetExcelOoxmlSnapshotExportInput): SpreadsheetExcelOoxmlSnapshotExportResult => {
  const sheets = getOrderedSnapshotSheets(snapshot)
  const chartPlan = planSnapshotCharts(snapshot, sheets)
  const tableEntries = getSnapshotTableExportEntries(sheets)
  const commentEntries = getSnapshotCommentExportEntries(sheets)
  const sharedStrings = collectSharedStrings(sheets)
  const exportStyles = buildStylesForSnapshotExport(snapshot.styles ?? {}, sheets)
  const stylesExport = buildStylesXmlFromSnapshot(
    exportStyles,
    snapshot.conditional_formats ?? {},
    snapshot.table_styles ?? {},
    snapshot.workbook_theme,
  )
  const hasTheme = stylesExport.themeXml != null
  const hasDocumentProperties = hasWorkbookDocumentProperties(snapshot.document_properties)
  const hasCustomProperties = Object.keys(snapshot.custom_properties ?? {}).length > 0
  const ooxmlAttachments = getSnapshotOoxmlAttachments(snapshot)
  const contentTypesXml = buildContentTypesXml(sheets, sharedStrings.size > 0, hasTheme, tableEntries, commentEntries, hasDocumentProperties, hasCustomProperties, ooxmlAttachments)
  const contentTypesWithCharts = chartPlan.contentTypeOverrides.length > 0
    ? contentTypesXml.replace('</Types>', `${chartPlan.contentTypeOverrides.join('')}</Types>`)
    : contentTypesXml
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(contentTypesWithCharts),
    '_rels/.rels': strToU8(buildRootRelationshipsXml(hasDocumentProperties, hasCustomProperties)),
    'xl/workbook.xml': strToU8(buildWorkbookXml(sheets, snapshot)),
    'xl/_rels/workbook.xml.rels': strToU8(buildWorkbookRelationshipsXml(sheets, sharedStrings.size > 0, hasTheme)),
    'xl/styles.xml': strToU8(stylesExport.xml),
  }
  if (hasDocumentProperties) {
    files['docProps/core.xml'] = strToU8(buildCorePropertiesXml(snapshot.document_properties!))
    files['docProps/app.xml'] = strToU8(buildExtendedPropertiesXml(snapshot.document_properties!))
  }
  if (hasCustomProperties) files['docProps/custom.xml'] = strToU8(buildCustomPropertiesXml(snapshot.custom_properties ?? {}))
  if (stylesExport.themeXml != null) files['xl/theme/theme1.xml'] = strToU8(stylesExport.themeXml)
  if (sharedStrings.size > 0) files['xl/sharedStrings.xml'] = strToU8(buildSharedStringsXml(sharedStrings))
  for (const { sheet, sheetIndex } of sheets) {
    const relationships: Array<{ id: string; type: string; target: string; targetMode?: string }> = []
    const sheetTableEntries = tableEntries.filter((entry) => entry.sheetId === sheet.sheet_id)
    const sheetCommentEntry = commentEntries.find((entry) => entry.sheetId === sheet.sheet_id)
    files[`xl/worksheets/sheet${sheetIndex}.xml`] = strToU8(buildWorksheetXml(
      sheet,
      sheetIndex,
      sharedStrings,
      relationships,
      stylesExport.styleIndexById,
      snapshot.validation_rules ?? {},
      sheetTableEntries,
      sheetCommentEntry,
      snapshot.conditional_formats ?? {},
      stylesExport.dxfIndexByConditionalFormatId,
      ooxmlAttachments,
      chartPlan.drawingBySheetId.get(sheet.sheet_id) ?? null,
    ))
    if (relationships.length > 0) {
      files[`xl/worksheets/_rels/sheet${sheetIndex}.xml.rels`] = strToU8(buildSheetRelationshipsXml(relationships))
    }
  }
  for (const [partName, xml] of Object.entries(chartPlan.files)) {
    files[partName] = strToU8(xml)
  }
  for (const tableEntry of tableEntries) {
    files[tableEntry.partName] = strToU8(buildTableXml(tableEntry))
  }
  for (const commentEntry of commentEntries) {
    files[commentEntry.commentsPartName] = strToU8(buildCommentsXml(commentEntry))
    if (commentEntry.hasVmlGeometry) files[commentEntry.vmlPartName] = strToU8(buildVmlDrawingXml(commentEntry))
  }
  addSnapshotOoxmlAttachmentsToPackage(files, ooxmlAttachments)
  return {
    package_bytes: zipStoredEntries(files),
    warnings: buildExportWarnings(snapshot, stylesExport),
    advanced_ooxml_artifacts: ooxmlAttachments,
  }
}

export const buildSpreadsheetExcelExportResponseFromWorkbookSnapshot = ({
  snapshot,
  format = 'xlsx',
  file_name = 'workbook.xlsx',
  include_calculated_values,
  requestId,
}: SpreadsheetExcelOoxmlSnapshotExportInput): SpreadsheetExcelExportResponsePayload => {
  const exported = buildSpreadsheetExcelOoxmlPackageFromWorkbookSnapshot({
    snapshot,
    format,
    file_name,
    include_calculated_values,
    requestId,
  })
  return {
    requestId,
    workbookId: snapshot.workbook_id,
    workbookRevision: snapshot.workbook_revision,
    format,
    file_name: file_name || 'workbook.xlsx',
    content_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    package_base64: encodeBytesToBase64(exported.package_bytes),
    byte_length: exported.package_bytes.byteLength,
    warnings: exported.warnings,
    advanced_ooxml_artifacts: exported.advanced_ooxml_artifacts,
  }
}

export const buildSpreadsheetWorkbookSnapshotFromOoxmlPackage = ({
  package_bytes,
  workbook_id,
  workbook_revision = 0,
}: SpreadsheetExcelOoxmlSnapshotImportInput): SpreadsheetExcelOoxmlSnapshotImportResult => {
  const files = toFilesRecord(package_bytes)
  const inspected = inspectSpreadsheetExcelOoxmlPackage(package_bytes)
  const workbookXml = readXml(files, inspected.workbook_part_name)
  const rootRelationships = parseRelationships(readXml(files, '_rels/.rels'))
  const corePropertiesRelationship = rootRelationships.find((relationship) => relationship.type === CORE_PROPERTIES_REL_TYPE)
  const corePropertiesPartName = corePropertiesRelationship == null
    ? ''
    : resolveRelationshipTarget('', corePropertiesRelationship.target)
  const extendedPropertiesRelationship = rootRelationships.find((relationship) => relationship.type === EXTENDED_PROPERTIES_REL_TYPE)
  const extendedPropertiesPartName = extendedPropertiesRelationship == null
    ? ''
    : resolveRelationshipTarget('', extendedPropertiesRelationship.target)
  const customPropertiesRelationship = rootRelationships.find((relationship) => relationship.type === CUSTOM_PROPERTIES_REL_TYPE)
  const customPropertiesPartName = customPropertiesRelationship == null
    ? ''
    : resolveRelationshipTarget('', customPropertiesRelationship.target)
  const sharedStrings = parseSharedStrings(readXml(files, 'xl/sharedStrings.xml'))
  const workbookRelationships = parseRelationships(readXml(files, inspected.workbook_relationships_part_name))
  const themeRelationship = workbookRelationships.find((relationship) => (
    relationship.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme'
  ))
  const themePartName = themeRelationship == null
    ? ''
    : resolveRelationshipTarget(inspected.workbook_part_name, themeRelationship.target)
  const themeXml = readXml(files, themePartName)
  const themeColors = parseThemeColors(themeXml)
  const stylesRelationship = workbookRelationships.find((relationship) => (
    relationship.type === 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles'
  ))
  const stylesPartName = stylesRelationship == null
    ? 'xl/styles.xml'
    : resolveRelationshipTarget(inspected.workbook_part_name, stylesRelationship.target)
  const styles = parseOoxmlStyleRecords(readXml(files, stylesPartName), themeColors)
  const tableStyles = parseOoxmlTableStyles(readXml(files, stylesPartName), styles)
  const sheetIds = inspected.worksheets.map((worksheet, index) => (
    normalizeImportedSheetId(worksheet.workbook_sheet_id, index)
  ))
  const sheetIdByName = new Map(inspected.worksheets.map((worksheet, index) => [worksheet.name, sheetIds[index]!]))
  const validationRules: Record<string, SpreadsheetValidationRule> = {}
  const conditionalFormats: Record<string, Record<string, unknown>> = {}
  const sheets = inspected.worksheets.map((worksheet, index) => {
    const sheetId = sheetIds[index]!
    const worksheetXml = readXml(files, worksheet.part_name)
    const chunks = parseWorksheetCells(worksheetXml, sheetId, sharedStrings, styles)
    const validations = parseDataValidations(worksheetXml, sheetId)
    Object.assign(validationRules, validations.validation_rules)
    applyValidationRangesToChunks(chunks, sheetId, validations.ranges)
    applyCommentsToChunks(files, worksheet.part_name, sheetId, chunks)
    applyHyperlinksToChunks(files, worksheetXml, worksheet.part_name, sheetId, chunks)
    Object.assign(conditionalFormats, parseConditionalFormats(worksheetXml, sheetId, styles))
    return {
      sheet_id: sheetId,
      name: worksheet.name,
      row_count: Math.max(1, worksheet.row_count),
      col_count: Math.max(1, worksheet.col_count),
      chunks,
      row_metadata: parseRowMetadata(worksheetXml),
      column_metadata: parseColumnMetadata(worksheetXml),
      ooxml_sheet_metadata: parseWorksheetOoxmlMetadata(worksheetXml),
      tab_color: parseSheetTabColor(worksheetXml, themeColors),
      sheet_format_properties: parseSheetFormatProperties(worksheetXml),
      calculation_properties: parseSheetCalculationProperties(worksheetXml),
      sheet_views: parseSheetViews(worksheetXml),
      custom_sheet_views: parseCustomSheetViews(worksheetXml),
      print_settings: parseSheetPrintSettings(worksheetXml),
      ignored_errors: parseIgnoredErrors(worksheetXml),
      frozen_panes: parseFrozenPanes(worksheetXml),
      hidden: worksheet.hidden,
      sheet_protection: parseSheetProtection(worksheetXml),
      protected_ranges: parseProtectedRanges(worksheetXml),
      auto_filter: parseWorksheetAutoFilter(worksheetXml, sheetId) ?? undefined,
      tables: parseWorksheetTables(files, worksheet.part_name, sheetId),
      merge_ranges: parseMergeRanges(worksheetXml, sheetId),
    }
  })
  const definedNames = parseDefinedNames(workbookXml, sheetIds, sheetIdByName)
  const nativeChartImport = parseNativeChartImport({
    files,
    worksheets: inspected.worksheets,
    sheetIds,
    sheetIdByName,
    themeColors,
  })
  const derivedArtifacts = nativeChartImport.artifacts
  const sheetsWithDerivedArtifactBounds = sheets.map((sheet) => {
    const sheetArtifacts = Object.values(derivedArtifacts).filter((artifact) => artifact.sheet_id === sheet.sheet_id)
    if (sheetArtifacts.length === 0) return sheet
    return {
      ...sheet,
      row_count: Math.max(
        sheet.row_count,
        ...sheetArtifacts.map((artifact) => artifact.placement.end_row + 1),
      ),
      col_count: Math.max(
        sheet.col_count,
        ...sheetArtifacts.map((artifact) => artifact.placement.end_col + 1),
      ),
    }
  })
  return {
    snapshot: {
      protocol_version: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
      workbook_id,
      workbook_revision,
      workbook_properties: parseWorkbookProperties(workbookXml),
      workbook_security: parseWorkbookSecurity(workbookXml),
      document_properties: parseWorkbookDocumentProperties(
        readXml(files, corePropertiesPartName),
        readXml(files, extendedPropertiesPartName),
      ),
      calculation_settings: parseWorkbookCalculationSettings(workbookXml),
      workbook_views: parseWorkbookViews(workbookXml),
      workbook_theme: parseWorkbookTheme(themeXml),
      custom_properties: parseWorkbookCustomProperties(readXml(files, customPropertiesPartName)),
      sheets: sheetsWithDerivedArtifactBounds,
      sheet_order: sheetsWithDerivedArtifactBounds.map((sheet) => sheet.sheet_id),
      named_ranges: {},
      defined_names: definedNames,
      validation_rules: validationRules,
      styles,
      table_styles: tableStyles,
      conditional_formats: conditionalFormats,
      sparkline_groups: {},
      derived_artifacts: derivedArtifacts,
      external_sources: {},
      ooxml_attachments: inspected.advanced_ooxml_artifacts.length > 0 ? inspected.advanced_ooxml_artifacts : undefined,
    },
    warnings: inspected.warnings,
    advanced_ooxml_artifacts: inspected.advanced_ooxml_artifacts,
  }
}
